// apps/estelle — single-file React front-end. Auth gate (sign in / create account) over the
// core's /api endpoints, then an authenticated app shell. The dashboard tiles land at L13;
// this lesson is the shell + the login flow. esbuild bundles this to public/app.js.
import { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
// H·2: manual per-course letter grades on the standard 4.0 map; unweighted average (HS).
import { GRADES, gpaOf, fmtGpa } from "../gpa.js";
import { localDateStr, addDays, byDue, latestTerm } from "@school-apps/core/lib/dates";
import { preview, createScheduler } from "@school-apps/core/srs/fsrs"; // S·3: predicted intervals for the grade-button labels (pure; writes stay server-side)
import { renderMarkdownSubset } from "@school-apps/core/lib/markdown"; // C·5: safe markdown micro-subset on the card faces
import { isAllowedImageUrl, NEAR_BLACK_LUMINANCE, LUMINANCE_SAMPLE_PX } from "@school-apps/core/images/limits"; // IMG·S·4: the post-fetch redirect check; IMG·Q·3: the near-black threshold (client-safe module)
import { clozeFront, clozeBack, clozeAllBlanks, cardFront, cardBack, isCloze, parseCloze } from "@school-apps/core/lib/cloze"; // CZ·2/4/5/6 render + save-eligibility
import { hoursCsv } from "@school-apps/core/hours/csv"; // A·3: client-side CSV export of the hour log
import { computePacing } from "@school-apps/core/lib/pacing"; // A·3: the anti-all-nighter arithmetic (pure, no AI)
import { examRamp, RAMP_WINDOW_DAYS } from "@school-apps/core/exams/exams"; // E·4: the countdown highlight + the ramp banner (pure, no writes)
import { activeCourses, archivedCourses, forActiveCourses } from "@school-apps/core/hub/archive"; // ARC·1: the one "archived" rule (pure)
import { expandOccurrences } from "@school-apps/core/calendar/occurrences"; // CAL·6a: flatten meetings/events/homework/exams into month-grid occurrences (pure, tz-free)
import { validateEvent, EVENT_KINDS } from "@school-apps/core/hub/events-validate"; // CAL·6a: the SAME pure validator the server enforces, gating the Add/Edit form's Save
import { studyPace } from "@school-apps/core/srs/pacer"; // M·3: the daily-pace target, computed client-side (pure, no writes)
import { PHASES, GRADUATE_AT } from "@school-apps/core/srs/learning"; // L·4: the learning-state phases + graduation gate (pure)
import { matchAnswer, VERDICT_GRADE } from "@school-apps/core/srs/answer-match"; // TA·1: type-the-answer matching (pure)
import { simulateDraw, rng } from "@school-apps/core/exam/simulate"; // PR·2: the exam draw (pure, seeded, zero FSRS)
import { evenBlueprint, SECONDS_PER_QUESTION } from "../exam-blueprint.js"; // PR·2: even draw across her tags (no official blueprint)

// --- tiny API client (same-origin; the session cookie rides along via credentials:include) ---
const JSON_HEADERS = { "content-type": "application/json" };

async function req(method, path, body) {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty / non-JSON body */ }
  return { ok: res.ok, status: res.status, data };
}

// CZ·6 — the ONE shared card-face pair, from core (was inline + had drifted). Ordinary cards
// pass through byte-identical; siblings blank their own index; a stray source falls to the guard.
const faceFront = cardFront;
const faceBack = cardBack;

const api = {
  async me() {
    try {
      const r = await req("GET", "/api/me");
      return r.ok && r.data ? r.data.user : null;
    } catch {
      return null; // no network / not signed in -> treat as signed out
    }
  },
  signIn: (email, password) => req("POST", "/api/login", { email, password }),
  createAccount: (email, password) => req("POST", "/api/register", { email, password }),
  async signOut() { try { await req("POST", "/api/logout"); } catch { /* ignore */ } },
  // S2·2 — change password (requires the current one; server audits, never logs material)
  changePassword: (currentPassword, newPassword) => req("POST", "/api/change-password", { currentPassword, newPassword }),

  study: {
    async overview() {
      const r = await req("GET", "/api/study/overview");
      // normalize: a generic fetch stub (jsdom test) may return a shape without `decks`
      return r.ok && r.data && Array.isArray(r.data.decks) ? r.data : { decks: [], totalDue: 0, reviewedToday: 0, streakDays: 0 };
    },
    // M·3: the daily-pace counts (unseenCount + reviewedTodayFresh) — the client runs studyPace over
    // these + the exam countdown + the deck total to render the Study & Review tile. Bare read.
    async pace() {
      const r = await req("GET", "/api/study/pace");
      return r.ok && r.data && typeof r.data.unseenCount === "number" ? r.data : null;
    },
    // IMG·3b — images on cards. `imageSuggest` is PREVIEW-ONLY (the server persists nothing) and her
    // searches are filtered SERVER-SIDE because she is a minor (IMG·2, safe:true at her mount — the
    // client has no say and is given none). Only `imageAttach`, driven by her tap, stores anything.
    // IMG·Q·1 — `query` (optional) is HER OWN words from the picker's search box. It is sent only
    // when she typed one, and the server then searches it verbatim with NO query-writer model call.
    // Her words are screened SERVER-SIDE like everything else here: a typed query is not a way past
    // her filter, and this client is given no way to ask for one.
    async imageSuggest(cardId, query) {
      const r = await req("POST", "/api/cards/image-suggest", query ? { cardId, query } : { cardId });
      if (r.ok && r.data) return r.data;
      // a REFUSED search (her screened words, the rate limiter) has an honest reason — say it,
      // rather than blaming the providers for something the server decided
      if (r.data?.error) return { refused: r.data.error };
      return { query: "", suggestions: [], providerErrors: [{ provider: "server", kind: "http" }] };
    },
    imageAttach: (cardId, image) => req("POST", "/api/cards/image-attach", { cardId, image }),
    imageDetach: (cardId) => req("POST", "/api/cards/image-detach", { cardId }),
    // FC·2: the exam-readiness projection for a date — null on any failure (surfaces stay quiet)
    async forecast(onDate) {
      try {
        const r = await req("GET", "/api/study/forecast?onDate=" + encodeURIComponent(onDate));
        return r.ok && r.data && r.data.overall ? r.data : null;
      } catch { return null; }
    },
    createDeck: (name) => req("POST", "/api/study/decks", { name }),
    renameDeck: (id, name) => req("POST", "/api/study/decks/update", { id, name }), // S·4
    deleteDeck: (id, cascade) => req("POST", "/api/study/decks/delete", cascade ? { id, cascade: true } : { id }), // S·4
    createCard: (deckId, front, back) => req("POST", "/api/study/cards", { deckId, front, back }),
    // AI capture: generate a preview (not persisted), then save the confirmed set in one bulk call.
    generate: (deckId, notes, cloze) => req("POST", "/api/study/generate", cloze ? { deckId, notes, cloze: true } : { deckId, notes }), // CZ·3: opted cloze mode
    // C·2/C·4 — capture from a photo or a PDF of her notes (client downscales the image; reads the PDF
    // to base64). Optional notes ride along as focus guidance. Save stays the same bulk cards[] path.
    generateImage: (deckId, image, notes, cloze) =>
      req("POST", "/api/study/generate", { deckId, image, ...(notes ? { notes } : {}), ...(cloze ? { cloze: true } : {}) }),
    generatePdf: (deckId, pdf, notes, cloze) =>
      req("POST", "/api/study/generate", { deckId, pdf, ...(notes ? { notes } : {}), ...(cloze ? { cloze: true } : {}) }),
    createCards: (deckId, cards) => req("POST", "/api/study/cards", { deckId, cards }),
    // PC·1: check a preview batch against the deck's live cards (server-side; the client never holds the whole deck)
    checkDuplicates: (deckId, cards) => req("POST", "/api/study/check-duplicates", { deckId, cards }),
    async due(deckId) {
      const q = deckId ? `?deck=${encodeURIComponent(deckId)}` : "";
      const r = await req("GET", `/api/study/due${q}`);
      return r.ok && r.data && Array.isArray(r.data.due) ? r.data.due : [];
    },
    async weakTopics() {
      const r = await req("GET", "/api/study/weak-topics");
      return r.ok && r.data && Array.isArray(r.data.topics) ? r.data.topics : [];
    },
    async quiz(tag) {
      const r = await req("GET", `/api/study/due?tag=${encodeURIComponent(tag)}`);
      return r.ok && r.data && Array.isArray(r.data.due) ? r.data.due : [];
    },
    review: (cardId, grade) => req("POST", "/api/study/review", { cardId, grade }),
    // ARC·1 — an archived course's "Study now" cards: a read, never a schedule write
    async practice(courseId) {
      const r = await req("GET", `/api/study/practice?course=${encodeURIComponent(courseId)}`);
      return r.ok && r.data && Array.isArray(r.data.cards) ? r.data.cards : [];
    },
    // L·4 — the unified learn-mode session: the quiz pool (learning cards + their MCQ) + the recall set
    // (probation/review due cards). The client composes + bounds to SESSION_SIZE.
    async learn(deckId) {
      const q = deckId ? `?deck=${encodeURIComponent(deckId)}` : "";
      const r = await req("GET", `/api/study/learn${q}`);
      const d = r.ok && r.data ? r.data : {};
      // DUE·1 — `fresh` is the third lane (never-reviewed cards, which stopped being "due"). Absent
      // on an older server → an empty lane, never undefined: the composer must not have to guess.
      return {
        learning: Array.isArray(d.learning) ? d.learning : [],
        recall: Array.isArray(d.recall) ? d.recall : [],
        fresh: Array.isArray(d.fresh) ? d.fresh : [],
      };
    },
    // L·4 — an MCQ answer advances GRADUATION only; it NEVER writes FSRS (the integrity line).
    quizAnswer: (cardId, correct) => req("POST", "/api/study/quiz-answer", { cardId, correct }),
    // L·4 — the study counts split by learning-state phase: { decks: [{id, learning, recallDue}], totals }.
    async counts() {
      const r = await req("GET", "/api/study/counts");
      const d = r.ok && r.data ? r.data : {};
      return {
        decks: Array.isArray(d.decks) ? d.decks : [],
        totalLearning: typeof d.totalLearning === "number" ? d.totalLearning : 0,
        totalRecallDue: typeof d.totalRecallDue === "number" ? d.totalRecallDue : 0,
        // DUE·1 — cards nobody has met. Absent on an older server → 0, so the split just omits it.
        totalUnseen: typeof d.totalUnseen === "number" ? d.totalUnseen : 0,
      };
    },
    // E·2 bookmarks (ported from Alivia's P3·8): an optional payload flag toggled server-side
    // (app-level route). The cards list comes back in the same { card, srs } shape as /due so
    // ReviewSession can run over it; P·2 widened it to also carry flagged practice questions.
    bookmark: (cardId, bookmarked) => req("POST", "/api/study/bookmark", { cardId, bookmarked }),
    async bookmarks() {
      const r = await req("GET", "/api/study/bookmarks");
      const d = r.ok && r.data ? r.data : {};
      return {
        cards: Array.isArray(d.bookmarks) ? d.bookmarks : [], // [{ card, srs }] — the /due shape
        mcqs: Array.isArray(d.mcqs) ? d.mcqs : [],            // flagged practice questions (P·2)
      };
    },
    // P·2: the MCQ practice bank — the whole thing (small records); filtering is client-side.
    async mcq() {
      const r = await req("GET", "/api/study/mcq");
      return r.ok && r.data && Array.isArray(r.data.items) ? r.data.items : [];
    },
    // Per-tag aggregate for the mastery meters (ported from Alivia's P3·6 consumer; the core
    // endpoint was already mounted in both apps — her UI just never consumed it).
    async tags() {
      const r = await req("GET", "/api/study/tags");
      return r.ok && r.data && Array.isArray(r.data.tags) ? r.data.tags : [];
    },
    // FX·1 — the ACTIVE FSRS weights the server schedules with, so the grade-button preview matches
    // what /api/study/review actually writes (no drift under a custom fitted param set).
    async params() {
      const r = await req("GET", "/api/study/params");
      return r.ok && r.data && Array.isArray(r.data.weights) ? r.data.weights : null;
    },
  },

  // H·1: the course & assignment hub — app-level routes over two change-log collections.
  hub: {
    async courses() {
      const r = await req("GET", "/api/courses");
      return r.ok && r.data && Array.isArray(r.data.courses) ? r.data.courses : [];
    },
    createCourse: (name, term, color) => req("POST", "/api/courses", color ? { name, term, color } : { name, term }),
    updateCourse: (id, fields) => req("POST", "/api/courses/update", { id, ...fields }),
    deleteCourse: (id, cascade) => req("POST", "/api/courses/delete", cascade ? { id, cascade: true } : { id }),
    // ARC·1 — archive / unarchive ride the course resource; `assignments({ active })` asks the
    // QUERY to leave an archived course's records out (the daily surfaces), the bare list stays whole.
    archiveCourse: (id) => req("POST", "/api/courses/archive", { id }),
    unarchiveCourse: (id) => req("POST", "/api/courses/unarchive", { id }),
    async assignments({ active = false } = {}) {
      const r = await req("GET", active ? "/api/assignments?active=1" : "/api/assignments");
      return r.ok && r.data && Array.isArray(r.data.assignments) ? r.data.assignments : [];
    },
    createAssignment: (courseId, title, due) => req("POST", "/api/assignments", { courseId, title, due }),
    updateAssignment: (id, fields) => req("POST", "/api/assignments/update", { id, ...fields }),
    deleteAssignment: (id) => req("POST", "/api/assignments/delete", { id }),
  },

  // Y·4: the syllabus intake (ported from Alivia's Y·3). `parsePdf` reads a PDF into a validated
  // preview (server-side model call — PREVIEW ONLY, nothing persists); `save` posts the CONFIRMED
  // preview to the hub. Same shared parse; no new AI surface beyond it.
  syllabus: {
    parsePdf: (pdf, notes) => req("POST", "/api/syllabus/parse", notes ? { pdf, notes } : { pdf }),
    save: (preview) => req("POST", "/api/syllabus/save", preview),
  },

  // NTS·4: study notes (the port of Alivia's NTS·3). `generate` is PREVIEW-ONLY (nothing persists)
  // and takes the SAME source union capture takes; `draftCards` asks the model to also map each card
  // to a section, which becomes each card's noteRef on save. Notes are a companion, never a review
  // surface — no schedule, no learning state.
  notes: {
    generate: (source, draftCards) => req("POST", "/api/notes/generate",
      draftCards && draftCards.length ? { ...source, draftCards } : { ...source }),
    save: (note) => req("POST", "/api/notes", note),
    async list() {
      const r = await req("GET", "/api/notes");
      return r.ok && r.data && Array.isArray(r.data.notes) ? r.data.notes : [];
    },
    remove: (id) => req("POST", "/api/notes/delete", { id }),
  },

  // BIG·5b·1 — the CHAPTER LIBRARY. A whole chapter is uploaded ONCE and kept, then turned into
  // cards by a background job (BIG·5b·2 adds that button; this slice manages sources only).
  // Deliberately NOT the capture path: `/api/study/generate` is one request/one reply over one
  // page's worth of material, while this stores a source the server reads section by section.
  library: {
    async list() {
      const r = await req("GET", "/api/library");
      // normalize hard: a generic fetch stub (jsdom) may answer a shape with neither key
      return {
        sources: r.ok && r.data && Array.isArray(r.data.sources) ? r.data.sources : [],
        usage: r.ok && r.data && r.data.usage ? r.data.usage : null,
      };
    },
    upload: (pdf, filename) => req("POST", "/api/library/upload", { pdf, filename }),
    remove: (id) => req("POST", "/api/library/delete", { id }),
  },

  // BIG·5b·2 — the chapter JOB: start (survey + park at the consent gate), go (release it), watch,
  // stop. `active()` asks with NO id, which is what lets a refresh reattach to work in flight.
  jobs: {
    async active() {
      const r = await req("GET", "/api/jobs/status");
      return r.ok && r.data && r.data.job ? r.data.job : null;
    },
    async status(id) {
      const r = await req("GET", `/api/jobs/status?id=${encodeURIComponent(id)}`);
      return r.ok && r.data && r.data.job ? r.data.job : null;
    },
    startChapter: (libraryId) => req("POST", "/api/jobs/start-chapter", { libraryId }),
    runChapter: (id, deck) => req("POST", "/api/jobs/run-chapter", deck ? { id, deck } : { id }),
    abort: (id) => req("POST", "/api/jobs/abort", { id }),
  },

  // SCH·4: the class-schedule intake (ported from Alivia's SCH·3). `parseText` is the free,
  // deterministic accessible-text path; `parsePdf` reads a grid; `save` posts the CONFIRMED preview.
  schedule: {
    parsePdf: (pdf, notes) => req("POST", "/api/schedule/parse", notes ? { pdf, notes } : { pdf }),
    parseText: (notes) => req("POST", "/api/schedule/parse", { notes }),
    save: (preview) => req("POST", "/api/schedule/save", preview),
    async meetings() {
      const r = await req("GET", "/api/sync/pull?cursor=0");
      const changes = r.ok && r.data && Array.isArray(r.data.changes) ? r.data.changes : [];
      return changes.filter((c) => c.collection === "meetings" && !c.deleted).map((c) => ({ id: c.id, ...c.payload }));
    },
  },

  // E·4: exam awareness (ported from Alivia's E·3). `upcoming` is the countdown (server-computed
  // daysUntil, nearest first); list/create/update/remove are the exm_ CRUD (Y·2's routes).
  exams: {
    async list() {
      const r = await req("GET", "/api/exams");
      return r.ok && r.data && Array.isArray(r.data.exams) ? r.data.exams : [];
    },
    async upcoming() {
      const r = await req("GET", "/api/exams/upcoming");
      return r.ok && r.data && Array.isArray(r.data.exams) ? r.data.exams : [];
    },
    create: (courseId, title, date, type) => req("POST", "/api/exams", { courseId, title, date, ...(type ? { type } : {}) }),
    update: (id, fields) => req("POST", "/api/exams/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/exams/delete", { id }),
  },

  // CAL·6a — personal / after-school events (pev_). `list` reads them for the calendar (degrades to
  // [] like api.hub.assignments()); create/update/remove are the write wrappers, mirroring Alivia.
  events: {
    async list() {
      const r = await req("GET", "/api/events");
      return r.ok && r.data && Array.isArray(r.data.events) ? r.data.events : [];
    },
    create: (evt) => req("POST", "/api/events", evt),
    update: (id, fields) => req("POST", "/api/events/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/events/delete", { id }),
  },

  // A·3: the hour log (ported from Alivia's A·1) — animal/clinical/volunteer hours, documented as
  // they happen. The active timer is SERVER-side (a singleton record), so it follows her devices.
  hours: {
    async list() {
      const r = await req("GET", "/api/hours");
      return r.ok && r.data && Array.isArray(r.data.entries) ? r.data.entries : [];
    },
    async active() {
      const r = await req("GET", "/api/hours/active");
      return r.ok && r.data && r.data.active ? r.data.active : null;
    },
    start: (category, place, activity) =>
      req("POST", "/api/hours/start", { category, place, ...(activity ? { activity } : {}) }),
    stop: () => req("POST", "/api/hours/stop", {}),
    add: (fields) => req("POST", "/api/hours/add", fields),
    update: (id, fields) => req("POST", "/api/hours/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/hours/delete", { id }),
  },

  // W·5 — the AI tutor. Chat + explain-it-back; the server grounds both in her own cards (deck-scoped).
  // History is held client-side and re-posted each turn; nothing is persisted server-side.
  tutor: {
    chat: (message, history, deckId) =>
      req("POST", "/api/tutor/chat", { message, history, ...(deckId ? { deckId } : {}) }),
    explain: (deckId, explanation) => req("POST", "/api/tutor/explain", { deckId, explanation }),
  },
  // J·2 — report a card that looks wrong, from a review session. Fire-and-check; nothing about the
  // grading flow depends on it.
  reports: {
    // VAR·4: shownFront = the phrasing on screen when she filed (a variant may be displaying)
    create: (cardId, reason, shownFront) => req("POST", "/api/reports", { cardId, ...(reason ? { reason } : {}), ...(shownFront ? { shownFront } : {}) }),
  },
};

// FX·1 — the grade-button interval preview must use the SAME FSRS weights the server schedules with,
// or a custom fitted param set would make the displayed intervals lie. `activePreview` starts as the
// stock preview (byte-identical when no per-app fit exists) and is swapped to a weight-matched preview
// once /api/study/params loads. Module-level so the deep session components stay prop-free.
let activePreview = preview;
async function loadFsrsWeights() {
  const weights = await api.study.params();
  if (Array.isArray(weights)) activePreview = createScheduler(weights).preview;
}

// J·2 — the "something wrong with this card?" affordance. Small + unobtrusive, sits under the review
// controls in every session state. Independent state, so filing a report never touches the grade flow;
// keyed by cardId at the call site so it resets when the card advances. Quiet ack in her register.
function ReportAffordance({ cardId, shownFront }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (sent) return <p className="report-ack">Sent — Dad will take a look.</p>;
  if (!open) return <button type="button" className="report-link" onClick={() => setOpen(true)}>Something wrong with this card?</button>;
  async function send() {
    setBusy(true);
    // VAR·4: the caller threads the text exactly as displayed — never re-derived or re-picked here
    try { const r = await api.reports.create(cardId, reason.trim() || undefined, shownFront); if (r.ok) setSent(true); }
    finally { setBusy(false); }
  }
  return (
    <div className="report-box">
      <input value={reason} maxLength={500} placeholder="What's wrong? (optional)" aria-label="What's wrong with this card?"
        onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
      <button type="button" className="btn btn-sm" onClick={send} disabled={busy}>Send</button>
      <button type="button" className="link" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

// A·3 — the injectable clock (the srs tests' discipline, client-side): jsdom stubs window.__now
// so the ticking elapsed display is asserted from a fixed instant, never real time.
const nowMs = () => (typeof window !== "undefined" && window.__now ? window.__now() : Date.now());
/** Elapsed ms -> "h:mm:ss" (or "m:ss" under an hour) for the live timer readout. */
function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  const h = Math.floor(s / 3600);
  return h > 0 ? `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` : `${Math.floor(s / 60)}:${pad(s % 60)}`;
}
/** Stored minutes -> "2h 15m" / "45m" for totals and entry rows. */
function fmtMins(m) {
  if (m < 60) return `${m}m`;
  return m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

// H·1 date helpers → core (X·3): `due` is a date-only ISO string (YYYY-MM-DD) — a calendar date,
// so LOCAL today (not UTC) is the honest comparison base, and string compare is date compare.
// core/lib/dates is pure + browser-safe, so the bundle imports it directly.

// --- the one signature element: the highlighter wordmark (styled in index.html) ---
function Wordmark() {
  return <span className="wordmark">Estelle<span className="star" aria-hidden="true">★</span></span>;
}

// E·2: a ribbon icon for bookmarks — filled when on, outline when off (currentColor both ways).
function BookmarkIcon({ on }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true">
      <path d="M6.5 3.5h11v17l-5.5-4.2-5.5 4.2Z"
        fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// F·2: map an auth failure to honest, kid-appropriate copy by STATUS (not fragile string-matching),
// preferring the server's own text on a 400 (it carries the real validation bound — e.g. the
// password length). A thrown fetch is handled at the call site as "couldn't reach the server".
function authError(registering, r) {
  const server = r.data && typeof r.data.error === "string" ? r.data.error.trim() : "";
  // F2·1: prefer the server text on 429 too — it carries the honest lockout minutes (S2's contract);
  // without this the lockout rendered the generic line and read as a silent dead-end in the field.
  if ((r.status === 400 || r.status === 429) && server) return server;
  if (r.status === 429) return "Too many attempts — wait a few minutes and try again.";
  if (registering) {
    if (r.status === 409) return "That email already has an account — sign in instead.";
    if (r.status === 403) return "Sign-ups are closed here — this app is already yours. Ask Dad if you're locked out.";
  } else if (r.status === 401) {
    return "That email and password don't match.";
  }
  return "That didn't work — check your details and try again.";
}

function AuthScreen({ onSignedIn }) {
  const [mode, setMode] = useState("signin"); // "signin" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const registering = mode === "register";

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) { setError("Enter an email and a password."); return; }
    setBusy(true);
    let r;
    try {
      r = registering
        ? await api.createAccount(email.trim(), password)
        : await api.signIn(email.trim(), password);
    } catch {
      setBusy(false);
      setError("Couldn't reach the server — nothing changed. Check your connection and retry.");
      return;
    }
    setBusy(false);
    if (r.ok && r.data) { onSignedIn(r.data.user); return; }
    setError(authError(registering, r));
  }

  function swap(next) { setMode(next); setError(""); }

  return (
    <main className="auth">
      <form className="card" onSubmit={submit} noValidate>
        <Wordmark />
        <p className="tagline">Your study companion.</p>

        <label className="field">
          <span>Email</span>
          <input type="email" autoComplete="email" inputMode="email"
            value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Password</span>
          <input type="password" autoComplete={registering ? "new-password" : "current-password"}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Just a moment…" : registering ? "Create account" : "Sign in"}
        </button>

        <p className="switch">
          {registering
            ? <>Already have an account? <button type="button" className="link" onClick={() => swap("signin")}>Sign in</button></>
            : <>New here? <button type="button" className="link" onClick={() => swap("register")}>Create an account</button></>}
        </p>
      </form>
    </main>
  );
}

const TILES = [
  { id: "courses", title: "Courses & Assignments", body: "Every class, reading, and due date in one place.", hint: "Add your fall schedule to begin." },
  { id: "calendar", title: "Calendar", body: "Classes, homework, tests, and your own plans on a month grid." },
  { id: "gpa", title: "GPA", stat: "—", statLabel: "cumulative GPA", body: "Grades roll up here as you enter them." },
  { id: "study", title: "Study & Review", body: "A spaced-repetition deck for each course." },
  { id: "quiz", title: "Quiz & Exam Prep", body: "The right cards, pulled before each test." },
  { id: "hours", title: "Hours Log", body: "Animal, clinical, volunteer — hours documented as they happen.", hint: "Start a timer or add an entry." },
  { id: "tutor", title: "AI Tutor", body: "Ask, go deeper, get harder practice — on your terms." },
  { id: "notes", title: "Study Notes", body: "Organized notes from anything you capture.", hint: "Notes orient you; cards make it stick." },
  { id: "library", title: "Chapter Library", body: "Whole chapters, stored and ready to turn into cards.", hint: "Capture is one page; a chapter is the whole thing." },
  { id: "guide", title: "Guide", body: "New here? How each part works, in plain language.", hint: "Start of semester — read me first." },
];

const ARC = [
  { year: "Freshman", note: "Build the habit. Grades start counting now.", active: true },
  { year: "Sophomore", note: "Deepen the GPA foundation." },
  { year: "Junior", note: "ACT/SAT, AP & dual-credit, college research." },
  { year: "Senior", note: "Applications, deadlines, scholarships." },
];

function Tile({ title, body, hint, stat, statLabel, badge, pace, onClick }) {
  const inner = (
    <>
      <div className="tile-head">
        <h2>{title}</h2>
        {badge > 0 && <span className="tile-badge">{badge}</span>}
      </div>
      {pace && (
        // M·3: the daily study pace replaces the red due-count — today's finishable number, not the wall.
        <p className={`tile-pace is-${pace.mode}`}>
          <span className="pace-headline">{pace.headline}</span>
          {pace.sub && <span className="pace-sub">{pace.sub}</span>}
        </p>
      )}
      {stat !== undefined && (
        <p className="stat"><span className="stat-num">{stat}</span><span className="stat-label">{statLabel}</span></p>
      )}
      <p className="tile-body">{body}</p>
      {hint && <p className="tile-hint">{hint}</p>}
    </>
  );
  return onClick
    ? <button type="button" className="tile tile-button" onClick={onClick}>{inner}</button>
    : <section className="tile">{inner}</section>;
}

// M·3 — the Study & Review tile's copy, per pacer mode (ported from Alivia's M·2, + the `empty` mode
// for her reality: near-empty decks until school starts). Never a wall count, never a "behind" state.
// `p` is studyPace(...)'s result; `near` is the nearest upcoming exam. Returns { mode, headline, sub }.
function paceDisplay(p, near) {
  if (!p) return null;
  const when = (d) => (d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);
  const examTie = near ? `${near.title} ${when(near.daysUntil)}` : null;
  // empty (never started, totalCards 0) FIRST — a quiet invitation, NOT maintenance's "all seen" copy
  if (p.mode === "empty") return { mode: p.mode, headline: "No cards yet", sub: "Capture some notes to start" };
  if (p.mode === "maintenance") return { mode: p.mode, headline: "All cards seen", sub: "reviews only from here" };
  if (p.remainingToday === 0) return { mode: p.mode, headline: "Done for today ✓", sub: examTie }; // the zero-out moment
  if (p.mode === "default") return { mode: p.mode, headline: `${p.doneToday} done / ${p.targetToday} today`, sub: "~20 today keeps you moving" };
  if (p.mode === "capped") return { mode: p.mode, headline: `${p.targetToday} today — max pace`, sub: "the app picks what matters most" };
  return { mode: p.mode, headline: `${p.doneToday} done / ${p.targetToday} today`, sub: examTie }; // paced
}

function ArcTile() {
  return (
    <section className="tile tile-arc">
      <h2>College-Prep Arc</h2>
      <ol className="arc">
        {ARC.map((s, i) => (
          <li key={s.year} className={s.active ? "arc-step is-active" : "arc-step"}>
            <span className="arc-dot">{i + 1}</span>
            <span className="arc-year">{s.year}</span>
            <span className="arc-note">{s.note}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

// A·3 — the "Today" panel (ported from Alivia's A·2): what should I touch today, computed
// CLIENT-side from already-synced hub data (computePacing — pure arithmetic, zero new endpoints).
function TodayPanel({ asns, courses, onOpenCourses }) {
  const { items, more } = computePacing(asns, localDateStr());
  const courseName = Object.fromEntries(courses.map((c) => [c.id, c.name]));
  return (
    <section className="today">
      <h2>Today</h2>
      {items.length === 0
        ? <p className="muted">Nothing pending — you&apos;re ahead.</p>
        : <ul className="today-list">
            {items.map((it) => (
              <li key={it.id} className={`today-row is-${it.status}`}>
                <span className="today-title">{it.title}</span>
                {courseName[it.courseId] && <span className="today-course">{courseName[it.courseId]}</span>}
                <span className={`today-left${it.status === "overdue" ? " overdue" : ""}`}>{it.label}</span>
                {it.nudge && <span className="today-nudge">{it.nudge}</span>}
              </li>
            ))}
          </ul>}
      {more > 0 && <button className="link" onClick={onOpenCourses}>+ {more} more in Courses</button>}
    </section>
  );
}

// E·4 — the exam countdown on the dashboard (ported from Alivia's E·3). Nearest exams, days until,
// sorted; QUIET when empty (no scary empty state). Reads server-computed daysUntil from
// /api/exams/upcoming — no srs, so it never depends on a schedule. In-window exams are flagged.
function ExamCountdown({ exams, onOpenCourses }) {
  if (!exams || exams.length === 0) return null; // quiet when empty
  const when = (d) => (d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);
  return (
    <section className="exam-countdown">
      <h2>Exams</h2>
      <ul className="exam-count-list">
        {exams.slice(0, 5).map((e) => (
          <li key={e.id} className={`exam-count-row${e.daysUntil <= RAMP_WINDOW_DAYS ? " is-soon" : ""}`}>
            <span className="exam-count-title">{e.title}</span>
            <span className="exam-count-when">{when(e.daysUntil)}</span>
          </li>
        ))}
      </ul>
      {exams.length > 5 && <button className="link" onClick={onOpenCourses}>+ {exams.length - 5} more in Courses</button>}
    </section>
  );
}

function Dashboard({ onOpenLibrary, onOpenStudy, onOpenQuiz, onOpenCourses, onOpenCalendar, onOpenHours, onOpenTutor, onOpenGuide, onOpenNotes }) {
  const [ov, setOv] = useState(null);
  const [weak, setWeak] = useState(null);
  const [hub, setHub] = useState(null); // H·1: { courses, asns } for the live tile counts
  const [timer, setTimer] = useState(null); // A·3: the running-timer chip — she can't forget it
  const [exams, setExams] = useState(null); // E·4: the upcoming-exam countdown
  const [paceCounts, setPaceCounts] = useState(null); // M·3: { unseenCount, reviewedTodayFresh }
  useEffect(() => {
    let alive = true;
    api.study.overview().then((o) => { if (alive) setOv(o); });
    api.study.weakTopics().then((w) => { if (alive) setWeak(w); });
    api.hours.active().then((t) => { if (alive) setTimer(t); });
    api.exams.upcoming().then((x) => { if (alive) setExams(x); });
    api.study.pace().then((p) => { if (alive) setPaceCounts(p); });
    // ARC·1 — the Today panel + the Courses tile are daily surfaces: active courses' assignments only
    Promise.all([api.hub.courses(), api.hub.assignments({ active: true })])
      .then(([courses, asns]) => { if (alive) setHub({ courses, asns }); });
    return () => { alive = false; };
  }, []);
  // M·3 — the daily study pace, computed CLIENT-side (pure studyPace) from the counts + the exam
  // countdown + the deck total + the kid's local day. Her reality: near-empty decks, no exam yet →
  // default at low numbers (target ≤ unseen), and `empty` when a deck was never started. `totalCards`
  // (from overview) distinguishes never-started from all-seen. Waits for the counts, exams, AND overview.
  const totalCards = ov ? ov.decks.reduce((s, d) => s + d.cardCount, 0) : null;
  const study = (paceCounts && exams !== null && ov !== null)
    ? paceDisplay(studyPace({ ...paceCounts, totalCards, exam: exams, now: localDateStr() }), exams[0])
    : null;
  const streakDays = ov ? (ov.streakDays ?? 0) : 0; // S·3: the REAL consecutive-day streak (was the reviewedToday stand-in)
  const weakCount = weak ? weak.length : null;
  const quizHint = weakCount == null ? "Loading…" : weakCount === 0 ? "No weak spots yet." : `${weakCount} topic${weakCount === 1 ? "" : "s"} to shore up`;
  // H·1 tile counts: due this week (7 days) + overdue, over open assignments in every term
  const today = localDateStr();
  const week = addDays(7);
  const openAsns = hub ? hub.asns.filter((a) => !a.done) : [];
  const dueWeek = openAsns.filter((a) => a.due >= today && a.due <= week).length;
  const overdueCount = openAsns.filter((a) => a.due < today).length;
  const coursesHint = hub == null ? "Loading…"
    : hub.courses.length === 0 ? "Add your fall schedule to begin."
    : overdueCount > 0 ? `${dueWeek} due this week · ${overdueCount} overdue`
    : dueWeek > 0 ? `${dueWeek} due this week` : "All clear this week.";
  // H·2: the GPA tile goes live — cumulative across all terms present, "—" until a grade exists
  const gpaStat = hub ? fmtGpa(gpaOf(hub.courses)) : "—";

  return (
    <div className="dash">
      <header className="dash-head">
        <div className="dash-when">
          <h1>Fall 2026</h1>
          <p className="phase">Freshman year</p>
        </div>
        <div className="dash-chips">
          {timer && (
            <button className="timer-chip" onClick={onOpenHours}
              title="An hours timer is running — tap to open the log.">
              ⏱ {timer.activity || timer.category} · running
            </button>
          )}
          <div className="streak" title="Review a little every day to build a streak.">
            <span className="streak-star" aria-hidden="true">★</span>
            <span>{streakDays > 0 ? `${streakDays}-day streak` : "Start a study streak"}</span>
          </div>
        </div>
      </header>
      {hub && <TodayPanel asns={hub.asns} courses={hub.courses} onOpenCourses={onOpenCourses} />}
      {exams && <ExamCountdown exams={exams} onOpenCourses={onOpenCourses} />}
      <div className="tiles">
        {TILES.map(({ id, ...rest }) =>
          id === "study"
            ? <Tile key={id} {...rest} pace={study} onClick={onOpenStudy} />
            : id === "quiz"
              ? <Tile key={id} {...rest} badge={weakCount} hint={quizHint} onClick={onOpenQuiz} />
              : id === "courses"
                ? <Tile key={id} {...rest} badge={dueWeek + overdueCount} hint={coursesHint} onClick={onOpenCourses} />
                : id === "calendar"
                ? <Tile key={id} {...rest} onClick={onOpenCalendar} />
                : id === "gpa"
                  ? <Tile key={id} {...rest} stat={gpaStat} onClick={onOpenCourses} />
                  : id === "hours"
                    ? <Tile key={id} {...rest} hint={timer ? "Timer running now." : rest.hint} onClick={onOpenHours} />
                    : id === "tutor"
                      ? <Tile key={id} {...rest} onClick={onOpenTutor} />
                      : id === "notes"
                        ? <Tile key={id} {...rest} onClick={onOpenNotes} />
                      : id === "library"
                        ? <Tile key={id} {...rest} onClick={onOpenLibrary} />
                      : id === "guide"
                        ? <Tile key={id} {...rest} onClick={onOpenGuide} />
                        : <Tile key={id} {...rest} />
        )}
        <ArcTile />
      </div>
    </div>
  );
}

// Per-topic mastery meters — ported from Alivia's P3·2/P3·6 (server-derived via GET
// /api/study/tags; holding = ease ≥ core's weak floor; a new card isn't holding). One settled
// difference from Alivia's: her meters render a fixed 13-topic order, but Estelle's tags are
// free-form from capture, so hers render dynamically — whatever the endpoint returns, ordered
// by total desc (biggest topics first) with an alphabetical tiebreak, and an honest empty state
// until her cards carry tags.
function MasteryMeters() {
  const [tags, setTags] = useState(null);
  useEffect(() => {
    let alive = true;
    api.study.tags().then((t) => { if (alive) setTags(t); });
    return () => { alive = false; };
  }, []);
  if (tags === null) return null; // quietly absent until loaded
  const rows = [...tags].sort((a, b) => (b.total - a.total) || a.tag.localeCompare(b.tag));
  return (
    <section className="mastery">
      <div className="mastery-head"><span className="star" aria-hidden="true">★</span><h2>Topic mastery</h2></div>
      {rows.length === 0
        ? <p className="muted empty">No topics yet — meters appear as your cards pick up tags (generated cards tag themselves).</p>
        : <ul className="meter-list">
            {rows.map(({ tag, total, holding }) => {
              const pct = total ? Math.round((holding / total) * 100) : 0;
              return (
                <li key={tag} className="meter-row">
                  <span className="meter-label">{tag}</span>
                  <span className="meter-track"><span className="meter-fill" style={{ width: `${pct}%` }} /></span>
                  <span className="meter-num">{holding}/{total}</span>
                </li>
              );
            })}
          </ul>}
    </section>
  );
}

// --- study: decks -> review session / add cards -------------------------------------------
// S·4 — a deck row with inline rename + a guarded (blocked-with-count, then cascade) delete,
// mirroring the hub course-card pattern. All three actions ride sync like every other write.
// L·4 (L·3b parity) — the honest learning/recall split, instead of one "N due" wall. "to learn" = the
// quiz pool (cards met as multiple choice first); "to review" = recall-phase cards that are due.
// DUE·1 — a THIRD part: "N new". Never-reviewed cards stopped counting as due, so without this a
// deck holding 150 unmet cards would read "caught up" — a fresh lie in place of the wall that was
// removed. It goes LAST because that is the order the session serves them in.
function phaseSplit(learning, recallDue, unseen = 0) {
  const parts = [];
  if (learning > 0) parts.push(`${learning} to learn`);
  if (recallDue > 0) parts.push(`${recallDue} to review`);
  if (unseen > 0) parts.push(`${unseen} new`);
  return parts.join(" · ");
}

function DeckRow({ deck, onRefresh, onReview, onGenerate, onAdd }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(deck.name);
  const [nameErr, setNameErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(0); // live card count from the 409 delete-guard
  const [confirmName, setConfirmName] = useState(""); // L·4 (L·3a): type-to-confirm the deck name
  // L·4 (L·3b): the phase split (merged in from /api/study/counts). "to learn" leads the Review session.
  const learning = deck.learning ?? 0;
  const recallDue = deck.recallDue ?? 0;
  const unseen = deck.unseen ?? 0; // DUE·1 — cards nobody has met yet
  const toDo = learning + recallDue + unseen;

  async function saveName() {
    const n = name.trim();
    if (!n) return;
    if (n === deck.name) { setEditing(false); setNameErr(null); return; }
    const r = await api.study.renameDeck(deck.id, n);
    if (r.ok) { setEditing(false); setNameErr(null); onRefresh(); }
    else setNameErr(r.status === 409 ? "A deck already has that name." : (r.data?.error ?? "Couldn't rename."));
  }
  async function remove() {
    const r = await api.study.deleteDeck(deck.id, false);
    if (r.status === 409) { setConfirmDel(r.data?.cards ?? deck.cardCount); setConfirmName(""); return; } // blocked -> type-to-confirm
    onRefresh();
  }
  // L·4 (L·3a): a card-bearing deck is destructive, so the cascade requires typing the deck's exact name.
  const confirmed = confirmName.trim() === deck.name;
  async function removeCascade() { if (!confirmed) return; await api.study.deleteDeck(deck.id, true); onRefresh(); }

  return (
    <li className="deck-row">
      <div className="deck-meta">
        {editing
          ? <div className="newdeck">
              <input value={name} autoFocus aria-label={`Rename ${deck.name}`}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditing(false); setName(deck.name); setNameErr(null); } }} />
              <button className="btn btn-sm" onClick={saveName} disabled={!name.trim()}>Save</button>
              <button className="link" onClick={() => { setEditing(false); setName(deck.name); setNameErr(null); }}>Cancel</button>
            </div>
          : <span className="deck-name">{deck.name}</span>}
        <span className="deck-counts">
          {deck.cardCount} card{deck.cardCount === 1 ? "" : "s"}
          {/* L·4 (L·3b): the learning/recall split, not a raw "N due" wall (counts arrive from /api/study/counts) */}
          {toDo > 0 && <> · <span className="deck-due">{phaseSplit(learning, recallDue, unseen)}</span></>}
        </span>
        {nameErr && <span className="deck-err" role="alert">{nameErr}</span>}
      </div>
      <div className="deck-actions">
        <button className="btn btn-sm" disabled={toDo === 0} onClick={onReview}>Review</button>
        <button className="link" onClick={onGenerate}>Generate from notes</button>
        <button className="link" onClick={onAdd}>Add cards</button>
        <button className="link" onClick={() => { setEditing(true); setName(deck.name); }}>Rename</button>
        <button className="link" onClick={remove}>Delete</button>
      </div>
      {confirmDel > 0 && (
        <div className="del-warn" role="alert">
          <p>This deck still has {confirmDel} card{confirmDel === 1 ? "" : "s"} — deleting it removes those cards and their study data. This can't be undone.</p>
          <div className="newdeck">
            <input value={confirmName} autoFocus aria-label={`Type the deck name to delete ${deck.name}`}
              placeholder={`Type “${deck.name}” to confirm`}
              onChange={(e) => setConfirmName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") removeCascade(); if (e.key === "Escape") { setConfirmDel(0); setConfirmName(""); } }} />
            <button className="btn btn-sm btn-danger" disabled={!confirmed} onClick={removeCascade}>
              Delete deck and its {confirmDel} card{confirmDel === 1 ? "" : "s"}
            </button>
            <button className="link" onClick={() => { setConfirmDel(0); setConfirmName(""); }}>Keep it</button>
          </div>
        </div>
      )}
    </li>
  );
}

// PR·2 — exam simulation ported to Estelle. She has no standardized single exam (high-school prep),
// so the draw is EVEN across her bank's own tags (evenBlueprint), not an official blueprint. Mounted
// but honest-empty until her MCQ bank exists. Exam conditions + miss-bookmarking + drill mirror
// Alivia's. Answers write ZERO FSRS (a sim is measurement, not scheduling).
function ExamPicker({ onPick, onBack }) {
  const [bankSize, setBankSize] = useState(null);
  useEffect(() => { api.study.mcq().then((items) => setBankSize(items.length)); }, []);
  return (
    <div className="study">
      <header className="study-top"><button className="link" onClick={onBack}>← Study</button></header>
      <div className="study-head"><h1>Simulate exam</h1></div>
      <p className="muted">Exam conditions: one question at a time, no feedback until the end, a running clock.
        {bankSize !== null ? ` Your bank holds ${bankSize} questions.` : ""}</p>
      <div className="exam-lengths">
        {[20, 50].map((n) => (
          <button key={n} className="btn" disabled={bankSize === 0} onClick={() => onPick(n)}>{n} questions</button>
        ))}
      </div>
      {bankSize === 0 && <p className="muted">No question bank yet — generate MCQs first.</p>}
    </div>
  );
}

function ExamSim({ length, onExit, onDrill }) {
  const [paper, setPaper] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.study.mcq().then((items) => {
      // PR·2: no official blueprint — draw EVENLY across the tags her bank actually has
      const raw = items.map((m) => ({ ...m, tags: m.tags?.length ? m.tags : (m.topic ? [m.topic] : []) }));
      const seed = Date.now() & 0x7fffffff;
      const d = simulateDraw({ bank: raw, blueprint: evenBlueprint(raw), count: length, seed });
      const rand = rng(seed ^ 0x9e3779b9);
      const order = d.questions.map((q) => {
        const ix = q.choices.map((_, i) => i);
        for (let i = ix.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); [ix[i], ix[j]] = [ix[j], ix[i]]; }
        return ix;
      });
      setPaper({ ...d, order });
    });
  }, [length]);

  const finished = paper !== null && (idx >= paper.questions.length || paper.questions.length === 0);

  useEffect(() => {
    if (finished || paper === null) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [finished, paper === null]);

  useEffect(() => {
    if (!finished || report !== null || paper === null) return;
    const domains = new Map();
    const misses = [];
    paper.questions.forEach((q, i) => {
      const domain = q.tags[0] ?? "(untagged)";
      const right = answers[i] === q.choices[q.answer];
      if (!domains.has(domain)) domains.set(domain, { domain, right: 0, total: 0 });
      const d = domains.get(domain);
      d.total += 1;
      if (right) d.right += 1;
      else misses.push(q);
    });
    for (const q of misses) api.study.bookmark(q.mcqOf ?? q.id, true);
    const rows = [...domains.values()].sort((a, b) => (a.right / a.total) - (b.right / b.total) || a.domain.localeCompare(b.domain));
    setReport({ rows, misses: misses.length, total: paper.questions.length });
  }, [finished]);

  if (paper === null) return <div className="study"><p className="muted">Dealing your exam…</p></div>;

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const overPace = !finished && elapsed > (idx + 1) * SECONDS_PER_QUESTION;

  if (finished) {
    const right = report ? report.total - report.misses : 0;
    return (
      <div className="study exam-sim">
        <div className="study-head"><h1>Exam report</h1></div>
        {report && (
          <>
            <p className="exam-score">{right} / {report.total} ({report.total ? Math.round((right / report.total) * 100) : 0}%) · {mmss(elapsed)}</p>
            <p className="muted">Drawn evenly across your card tags — there's no official exam blueprint for this yet.</p>
            {paper.manifest.backfilled > 0 && <p className="muted">{paper.manifest.backfilled} question{paper.manifest.backfilled === 1 ? "" : "s"} backfilled from bigger tags (bank too small in spots).</p>}
            {paper.manifest.shortfall > 0 && <p className="muted">Bank ran out: dealt {paper.manifest.dealt} of the {paper.manifest.requested} requested.</p>}
            <table className="forecast-table exam-domains">
              <thead><tr><th>Topic</th><th>Score</th></tr></thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.domain}><td>{r.domain}</td><td>{r.right}/{r.total}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="muted">{report.misses === 0 ? "Clean sheet — nothing to drill." : `${report.misses} miss${report.misses === 1 ? "" : "es"} bookmarked for drilling.`}</p>
          </>
        )}
        <div className="exam-actions">
          {report && report.misses > 0 && <button className="btn" onClick={onDrill}>Drill misses</button>}
          <button className="link" onClick={onExit}>Done</button>
        </div>
      </div>
    );
  }

  const q = paper.questions[idx];
  return (
    <div className="study exam-sim">
      <header className="study-top">
        <button className="link" onClick={onExit}>← Abandon</button>
        <span className="exam-clock">{mmss(elapsed)}{overPace && <em className="exam-overpace"> · over real-exam pace</em>}</span>
      </header>
      <div className="review-progress">{idx + 1} / {paper.questions.length}</div>
      <section className="flashcard exam-q">
        <p className="card-front">{q.stem}</p>
        <div className="exam-choices">
          {paper.order[idx].map((ci) => (
            <button key={ci} className="btn exam-choice" onClick={() => { setAnswers((a) => [...a, q.choices[ci]]); setIdx((i) => i + 1); }}>
              {q.choices[ci]}
            </button>
          ))}
        </div>
      </section>
      <p className="muted">No feedback until the end — exam conditions.</p>
    </div>
  );
}

function StudyView({ onBack }) {
  const [ov, setOv] = useState(null);
  const [counts, setCounts] = useState(null); // L·4: { decks: [{id, learning, recallDue}], totalLearning, totalRecallDue }
  const [bmCount, setBmCount] = useState(0); // E·2: badge on the Bookmarked entry
  const [mode, setMode] = useState({ type: "list" }); // list | review(deck) | add(deck) | generate(deck) | bookmarks
  const [creating, setCreating] = useState(false);
  const [deckName, setDeckName] = useState("");
  const [cast, setCast] = useState(null); // FC·2 — exam-day projection table (only when an exam is in the window)

  function refresh() {
    api.study.bookmarks().then((b) => setBmCount(b.cards.length + b.mcqs.length));
    // L·4: overview gives per-deck cards; counts gives the learning/recall split. Both, then merge.
    // FC·2: only when an exam sits inside the ramp window — no permanent dashboard clutter
    api.exams.upcoming().then((exams) => {
      const soon = (exams ?? []).find((e) => e.daysUntil <= RAMP_WINDOW_DAYS);
      if (!soon) { setCast(null); return; }
      api.study.forecast(soon.date).then((f) => setCast(f && f.overall.mean != null ? { exam: soon, f } : null));
    });
    return Promise.all([api.study.overview(), api.study.counts()]).then(([o, c]) => { setOv(o); setCounts(c); });
  }
  useEffect(() => { refresh(); }, []);

  // L·4: the deck Review drives the unified LEARN session (recognition-then-recall), not the FSRS-only
  // ReviewSession. ReviewSession stays for the Bookmarked + weak-spot practice entrances (below).
  if (mode.type === "review") return <LearnSession deck={mode.deck} onExit={() => { setMode({ type: "list" }); refresh(); }} />;
  if (mode.type === "add") return <AddCards deck={mode.deck} onDone={() => { setMode({ type: "list" }); refresh(); }} />;
  if (mode.type === "generate") return <GenerateCards deck={mode.deck} onDone={() => { setMode({ type: "list" }); refresh(); }} />;
  if (mode.type === "bookmarks") return <BookmarkedView onBack={() => { setMode({ type: "list" }); refresh(); }} />;
  // PR·2 — the exam simulator (even draw across her tags; honest-empty until her bank exists).
  if (mode.type === "exam-pick") return <ExamPicker onPick={(n) => setMode({ type: "exam", length: n })} onBack={() => { setMode({ type: "list" }); refresh(); }} />;
  if (mode.type === "exam") return <ExamSim length={mode.length} onExit={() => { setMode({ type: "list" }); refresh(); }}
    onDrill={() => setMode({ type: "drill" })} />;
  if (mode.type === "drill") return <ReviewSession bookmarksOnly onExit={() => { setMode({ type: "list" }); refresh(); }} />;

  async function createDeck() {
    const name = deckName.trim();
    if (!name) return;
    await api.study.createDeck(name);
    setDeckName(""); setCreating(false); refresh();
  }

  // L·4: merge the per-deck learning/recall split (counts) into the overview decks so each DeckRow
  // shows "N to learn · M to review" instead of a misleading "all due" wall.
  const splitById = new Map((counts?.decks ?? []).map((d) => [d.id, d]));
  const decks = ov ? ov.decks.map((d) => ({ ...d, ...(splitById.get(d.id) ?? {}) })) : [];
  const totalCards = ov ? ov.decks.reduce((s, d) => s + d.cardCount, 0) : 0;
  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
        <div className="study-top-right">
          <button className="link" onClick={() => setMode({ type: "exam-pick" })}>Simulate exam</button>
          <button className="link bm-link" onClick={() => setMode({ type: "bookmarks" })}>
            <BookmarkIcon on={bmCount > 0} /> Bookmarked{bmCount > 0 ? ` (${bmCount})` : ""}
          </button>
        </div>
      </header>
      <div className="study-head">
        <div className="study-head-title">
          <h1>Study &amp; Review</h1>
          {/* M·3 + L·4: the deck total is context here — and the honest learning/recall split, not a "due" wall */}
          {ov !== null && counts !== null && decks.length > 0 && (
            <p className="study-total muted">
              {totalCards} card{totalCards === 1 ? "" : "s"}
              {(counts.totalLearning + counts.totalRecallDue + (counts.totalUnseen ?? 0)) > 0
                ? <> · {phaseSplit(counts.totalLearning, counts.totalRecallDue, counts.totalUnseen)}</>
                : <> · all caught up</>}
            </p>
          )}
        </div>
        {!creating
          ? <button className="btn btn-sm" onClick={() => setCreating(true)}>+ New deck</button>
          : <div className="newdeck">
              <input value={deckName} placeholder="Deck name" autoFocus
                onChange={(e) => setDeckName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createDeck(); }} />
              <button className="btn btn-sm" onClick={createDeck} disabled={!deckName.trim()}>Create</button>
              <button className="link" onClick={() => { setCreating(false); setDeckName(""); }}>Cancel</button>
            </div>}
      </div>

      {/* FC·2 — the exam-day forecast table: ONLY while an exam is inside the ramp window */}
      {cast && (
        <section className="forecast-panel">
          <h2>Exam-day forecast</h2>
          <p className="muted">{cast.exam.title} · {cast.exam.date}: ~{Math.round(cast.f.overall.mean * 100)}% retrievable across {cast.f.overall.count} reviewed cards{cast.f.unprojected.count > 0 ? ` · ${cast.f.unprojected.count} new cards can't be projected yet` : ""}</p>
          {cast.f.byTag.length > 0 && (
            <table className="forecast-table">
              <thead><tr><th>Domain</th><th>Cards</th><th>Projected</th><th>At risk</th></tr></thead>
              <tbody>
                {cast.f.byTag.map((t) => (
                  <tr key={t.tag}><td>{t.tag}</td><td>{t.count}</td><td>{Math.round(t.mean * 100)}%</td><td>{Math.round(t.belowShare * 100)}%</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {ov === null
        ? <p className="muted">Loading…</p>
        : decks.length === 0
          ? <p className="muted empty">No decks yet. Create one, then add some cards.</p>
          : <ul className="deck-list">
              {decks.map((d) => (
                <DeckRow key={d.id} deck={d} onRefresh={refresh}
                  onReview={() => setMode({ type: "review", deck: d })}
                  onGenerate={() => setMode({ type: "generate", deck: d })}
                  onAdd={() => setMode({ type: "add", deck: d })} />
              ))}
            </ul>}

      {ov !== null && decks.length > 0 && <MasteryMeters />}
    </div>
  );
}

// F·1: one review sitting is capped at SESSION_SIZE cards — Alivia's first real session served her
// the whole due queue (n / 354) and it felt endless. Estelle inherits the cap before she ever hits it.
const SESSION_SIZE = 20;

// S·3: a compact schedule label for a grade button's sublabel (whole-day FSRS intervals).
function fmtDays(days) {
  if (!Number.isFinite(days) || days < 1) return "now";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
}

// L·4 — the unified learn-mode session (ported from Alivia's L·3/L·3c, her Patriots tokens + register).
// Recognition-then-recall: a learning-phase card is met as its MCQ FIRST (which teaches), then — once
// graduated — recalled as a flashcard. L·3c semantics: a quiz is a FIXED sitting (a correct MCQ answer
// NEVER re-queues the card, so each learning card appears at most ONCE; the two corrects toward
// graduation come from two SEPARATE quizzes, streak persisted server-side in learn_). A card that
// GRADUATES this session gets its first cold-recall flashcard at the END of the quiz, not a mid-flip.
// The deck Review drives THIS engine; MCQ answers write ZERO FSRS (server-enforced) — the only FSRS
// write is the flashcard grade posting to /api/study/review.
// TA·2 — type-the-answer recall. An OPT-IN toggle (persisted) makes a recall card ask her to type
// the answer BEFORE reveal; the match verdict PREFILLS a grade suggestion (exact→Good, close→Hard,
// miss→Again) which she can always override — the grade stays hers, the FSRS write path is
// unchanged. Auto-skipped for long backs (recall-by-typing is for short factual answers) and for
// cloze siblings (they already blank). The preference lives in localStorage.
const TYPE_MAX_BACK = 40; // chars — above this, typing the whole answer isn't the point; skip the prompt

function typeModeStored() {
  try { return window.localStorage.getItem("estelleTypeMode") === "1"; } catch { return false; }
}
function setTypeModeStored(on) {
  try { window.localStorage.setItem("estelleTypeMode", on ? "1" : "0"); } catch { /* private mode */ }
}
/** A recall card qualifies for typing when its back is short prose (not a cloze sibling). */
function typeEligible(card) {
  return !card.clozeIndex && typeof card.back === "string" && card.back.trim().length > 0 && card.back.trim().length <= TYPE_MAX_BACK;
}

/** The type-answer input + Check. On Check it computes the verdict and calls onVerdict(v), which
 * reveals the answer; the verdict LABEL + prefilled grade then show in the revealed grades block. */
function TypeAnswerField({ card, onVerdict }) {
  const [typed, setTyped] = useState("");
  const ref = useRef(null);
  useEffect(() => { setTyped(""); if (ref.current) ref.current.focus(); }, [card.id]);
  const check = () => onVerdict(matchAnswer(typed, card.back).verdict);
  return (
    <div className="type-answer">
      <label className="field"><span className="sr-only">Type the answer</span>
        <input ref={ref} type="text" className="type-input" value={typed} aria-label="Type the answer"
          placeholder="Type the answer…" autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") check(); }} /></label>
      <button className="btn btn-reveal" onClick={check}>Check</button>
    </div>
  );
}

// TA·2 — the verdict label shown beside the revealed answer (derived from the suggested grade).
const TYPE_VERDICT_LABEL = { good: "Exact — nice.", hard: "Close.", again: "Not quite." };

// IMG·3b — images on cards, her surface: the Alivia port (IMG·3a) in her tokens. The locked design
// is unchanged and is enforced server-side, so nothing about it is re-argued here: the app NEVER
// GENERATES an image, it recommends licensed ones, and HER TAP is the only thing that attaches one.
// Her searches are additionally filtered because she is a MINOR — that filter lives on the server
// (IMG·2, safe:true at her mount), which is exactly why this file cannot weaken it.

/**
 * Fetch an ALLOWLISTED image URL and downscale it to a data URI, ready to attach. Isolated +
 * injectable via window.__fetchAndDownscale so jsdom (no canvas, no network) stubs it — the
 * `downscaleImage` precedent. Throws on a fetch or decode failure so the caller can show honest copy.
 */
async function fetchAndDownscale(url) {
  if (typeof window !== "undefined" && window.__fetchAndDownscale) return window.__fetchAndDownscale(url);
  // IMG·S·4 — redirects refused, and the FINAL url re-checked against the allowlist: an allowed
  // host that 302s elsewhere must never be where the bytes actually came from.
  const res = await fetch(url, { redirect: "error" });
  if (!res.ok) throw new Error("could not fetch the image");
  if (res.url && !isAllowedImageUrl(res.url)) throw new Error("image moved off the allowed hosts");
  const { mediaType, data } = await downscaleImage(await res.blob());
  return `data:${mediaType};base64,${data}`;
}

/**
 * IMG·Q·3 — mean luminance (0..1) of the image at `url`, or null when it cannot be measured.
 * Injectable via window.__measureLuminance so jsdom (no canvas) stubs it — the fetchAndDownscale
 * precedent.
 *
 * Measured on a SEPARATE offscreen image, never the grid's own <img>: reading pixels needs a
 * CORS-clean canvas (`crossOrigin`), and making the VISIBLE thumbnail depend on a CORS handshake
 * would risk the whole grid to police a corner case. So a measurement that cannot happen — no
 * canvas, a tainted read, a refused cross-origin load — returns null and the candidate is KEPT.
 * Fail open: a picture is never dropped because we could not look at it.
 */
async function measureLuminance(url) {
  if (typeof window !== "undefined" && window.__measureLuminance) return window.__measureLuminance(url);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = "anonymous";
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("could not load for measurement"));
      el.src = url;
    });
    const n = LUMINANCE_SAMPLE_PX;
    const canvas = document.createElement("canvas");
    canvas.width = n; canvas.height = n;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, n, n);
    const { data: px } = ctx.getImageData(0, 0, n, n);
    let sum = 0;
    for (let i = 0; i < px.length; i += 4) {
      sum += (0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2]) / 255;
    }
    return sum / (px.length / 4);
  } catch {
    return null; // unmeasurable is not a verdict
  }
}

/** Detach the card's image, then mirror it locally. Shared by both session components. */
async function detachImage(cardId, patch) {
  const r = await api.study.imageDetach(cardId);
  if (r.ok) patch(null);
}

/**
 * The image ON a card face. Credit is NOT optional chrome: an attached provider image always shows
 * its author, its licence, and a link to where it came from, because that is the condition on which
 * we are allowed to display it at all. A manual upload says so plainly instead.
 */
function CardImage({ image }) {
  if (!image || !image.dataUri) return null;
  return (
    <figure className="card-image">
      <img src={image.dataUri} alt={image.title || ""} />
      <figcaption className="card-image-credit">
        {image.provider === "manual"
          ? "Your photo"
          : <>{image.author || "Unknown author"} · {image.license}
              {image.source && <> · <a href={image.source} target="_blank" rel="noreferrer">source</a></>}</>}
      </figcaption>
    </figure>
  );
}

/**
 * The picker: ask for suggestions, show what came back with its licence visible, and attach exactly
 * the one she taps. Manual upload sits beside it as an equal, and is the honest offer when search is
 * down.
 */
function ImagePicker({ card, onAttached, onClose }) {
  const [state, setState] = useState({ phase: "idle" }); // idle | loading | done | deferred | refused
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  // IMG·F·2 — candidate-specific degradation (live-fire evidence: Openverse's proxy answers 424 for
  // a dead upstream). A failed thumb load or a failed download drops THAT candidate only.
  const [dead, setDead] = useState(() => new Set());
  const markDead = (id) => setDead((prev) => new Set(prev).add(id));
  // IMG·Q·3 — the near-black drop, riding the EXISTING per-candidate drop path (F·2). A thumbnail
  // that loads and turns out to be almost entirely black is a plate scan, not a picture of the
  // thing; it costs ITSELF a grid slot and never the grid. Unmeasurable → kept.
  const screenDark = async (hit) => {
    const lum = await measureLuminance(hit.thumbUrl);
    if (typeof lum === "number" && lum < NEAR_BLACK_LUMINANCE) markDead(hit.id);
  };
  // IMG·Q·1 — SHE steers the search. The model's phrase is the PRE-FILLED DEFAULT, so the one-tap
  // flow is unchanged for anyone who doesn't care; edit the words, press Search, and the server
  // searches them verbatim — no query-writer model call at all on a retry. Her safe filter is
  // applied to typed words server-side; nothing here can turn it off.
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState(false);

  async function findImages(typed) {
    setState({ phase: "loading" }); setErr(null); setSearched(true);
    const r = await api.study.imageSuggest(card.id, typed);
    if (r.refused) { setState({ phase: "refused", message: r.refused }); return; }
    if (r.deferred) { setState({ phase: "deferred" }); return; }
    setQ(typed || r.query || "");
    setState({ phase: "done", query: r.query, suggestions: r.suggestions ?? [], providerErrors: r.providerErrors ?? [] });
  }

  /** HER TAP, and only her tap, attaches: fetch → downscale → attach, with the provenance intact. */
  async function approve(hit) {
    setBusy(hit.id); setErr(null);
    try {
      const dataUri = await fetchAndDownscale(hit.imageUrl);
      const r = await api.study.imageAttach(card.id, {
        dataUri, provider: hit.provider, source: hit.sourcePage, license: hit.license,
        author: hit.author, licenseUrl: hit.licenseUrl, title: hit.title,
      });
      if (!r.ok) { setErr(r.data?.error || "Couldn't add that one — try another."); setBusy(null); return; }
      onAttached(r.data.image);
    } catch {
      // the failure belongs to THIS candidate (a dead upstream, an undecodable file) — drop it
      markDead(hit.id);
      setErr("Couldn't download that picture. Try another, or upload your own.");
      setBusy(null);
    }
  }

  async function uploadOwn(file) {
    if (!file) return;
    setBusy("manual"); setErr(null);
    try {
      const { mediaType, data } = await downscaleImage(file);
      const r = await api.study.imageAttach(card.id, { dataUri: `data:${mediaType};base64,${data}`, provider: "manual" });
      if (!r.ok) { setErr(r.data?.error || "Couldn't add that photo."); setBusy(null); return; }
      onAttached(r.data.image);
    } catch {
      setErr("Couldn't read that file — try a JPG or PNG.");
      setBusy(null);
    }
  }

  const providersDown = state.phase === "done" && state.suggestions.length === 0 && state.providerErrors.length > 0;
  // the LIVE grid: suggestions minus candidates whose thumbs failed (IMG·F·2)
  const live = state.phase === "done" ? state.suggestions.filter((h) => !dead.has(h.id)) : [];
  const allDead = state.phase === "done" && state.suggestions.length > 0 && live.length === 0;
  // IMG·QF·3 — the BLANK PANEL, closed. A search that simply found nothing (no provider failed, no
  // candidate died, the query was not empty) rendered NOTHING AT ALL: the field opened the picker,
  // searched, and got a silent empty box. Every other outcome here says what happened; so does this.
  const nothingFound = state.phase === "done" && !!state.query
    && state.suggestions.length === 0 && state.providerErrors.length === 0;
  return (
    <section className="image-picker">
      <div className="image-picker-head">
        <h3>Add a picture</h3>
        <button className="link" onClick={onClose}>Close</button>
      </div>
      <div className="image-picker-actions">
        <button className="btn btn-ghost" onClick={() => findImages()} disabled={state.phase === "loading"}>
          {state.phase === "loading" ? "Looking…" : "Find a picture"}
        </button>
        <label className="btn btn-ghost image-upload">
          Upload my own
          <input type="file" accept="image/*" aria-label="Upload my own picture"
            onChange={(e) => uploadOwn(e.target.files && e.target.files[0])} />
        </label>
      </div>

      {/* IMG·Q·1 — the search box: her words, her retry, no model call behind it */}
      {searched && (
        <form className="image-search" onSubmit={(e) => { e.preventDefault(); if (q.trim()) findImages(q.trim()); }}>
          <input type="text" value={q} aria-label="Picture search words" placeholder="Search words"
            onChange={(e) => setQ(e.target.value)} />
          <button type="submit" className="btn btn-ghost" disabled={state.phase === "loading" || !q.trim()}>Search</button>
        </form>
      )}

      {err && <p className="error" role="alert">{err}</p>}
      {state.phase === "refused" && <p className="error" role="alert">{state.message}</p>}
      {state.phase === "deferred" && (
        <p className="muted" role="status">Couldn&apos;t work out what to search for just now — try again, or upload your own.</p>
      )}
      {state.phase === "done" && state.query === "" && (
        <p className="muted" role="status">A picture wouldn&apos;t really help this card. Upload your own if you disagree.</p>
      )}
      {providersDown && (
        <p className="muted" role="status">
          Picture search is down right now ({state.providerErrors.map((p) => p.provider).join(", ")}) — upload your own instead.
        </p>
      )}
      {nothingFound && (
        <p className="muted" role="status">Nothing came back for those words — try different ones, or upload your own.</p>
      )}
      {allDead && (
        <p className="muted" role="status">None of the pictures could be loaded — try again, or upload your own.</p>
      )}
      {live.length > 0 && (
        <>
          <p className="muted image-query">Tap one to add it — nothing is added until you do.</p>
          <ul className="image-grid">
            {live.map((hit) => (
              <li key={hit.id} className="image-hit">
                <button type="button" className="image-hit-btn" disabled={busy !== null}
                  aria-label={`Use this picture: ${hit.title || hit.provider}, ${hit.license}`}
                  onClick={() => approve(hit)}>
                  {/* a thumb that fails to load drops ITS candidate only (IMG·F·2) */}
                  <img src={hit.thumbUrl} alt="" loading="lazy" onError={() => markDead(hit.id)}
                    onLoad={() => screenDark(hit)} />
                  <span className="image-hit-meta">
                    <span className="image-hit-author">{hit.author || "Unknown author"}</span>
                    <span className="image-hit-license">{hit.license}</span>
                  </span>
                  {busy === hit.id && <span className="image-hit-busy" role="status">Adding…</span>}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function LearnSession({ deck, onExit }) {
  const [picking, setPicking] = useState(false); // IMG·3b — the image picker is open for THIS card
  const [queue, setQueue] = useState(null); // composed encounters, or null while loading
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);   // the ORIGINAL choice index she tapped
  const [inspect, setInspect] = useState(null); // the choice whose explanation is open
  const [mcqResult, setMcqResult] = useState(null); // { correct, graduated, streak } after answering
  const [revealed, setRevealed] = useState(false);
  const [recallAck, setRecallAck] = useState(null); // { demoted } after grading a recall
  const [typeMode, setTypeMode] = useState(typeModeStored); // TA·2: opt-in type-the-answer (persisted)
  const [suggested, setSuggested] = useState(null);
  const [offline, setOffline] = useState(false); // AX·2: an honest, non-destructive offline-save banner
  const [graduatedThisSession, setGraduatedThisSession] = useState([]); // [{ card }] — recalled at the END
  const [recallIdx, setRecallIdx] = useState(0); // walks the end-of-quiz recall block
  const [recalled, setRecalled] = useState(0);
  const [learned, setLearned] = useState(0);            // correct MCQ answers
  const [graduatedCount, setGraduatedCount] = useState(0);
  const [tally, setTally] = useState({ again: 0, hard: 0, good: 0, easy: 0 });
  const [intervals, setIntervals] = useState([]);
  const [touched, setTouched] = useState(() => new Set());
  const [summary, setSummary] = useState(null);
  const [rampExam, setRampExam] = useState(null);
  const [rampCast, setRampCast] = useState(null); // FC·2 — the exam-day projection shown in the banner
  const orderRef = useRef({ idx: -1, order: [] }); // per-MCQ shuffled display order
  const lastShownRef = useRef(new Map()); // phrasing anti-cue, reused for the recall flashcard
  const pickRef = useRef({ enc: -1, cardId: null, text: null });

  const heading = deck ? deck.name : "Study session";

  // LEARNING-FIRST: the quiz pool leads (a learning card must be met as its MCQ before the recall
  // backlog); recall fills the remaining slots. Learning cards without a built MCQ are skipped.
  // DUE·1 — THREE lanes now, in this order: quiz pool, recall, then FRESH. Never-reviewed cards
  // stopped counting as "due" this lesson, so `fresh` is the only thing that serves new material —
  // dropping it here would make the app count new cards forever and never show one. Fresh entries
  // render as ordinary recall encounters (a card with `srs: null` is exactly what a first sitting
  // has always been), so nothing about the flashcard flow moved. Fresh goes LAST: a card already met
  // and now scheduled beats material nobody has opened, and SESSION_SIZE (F·1) still bounds the
  // sitting — so a 150-card chapter contributes at most the slots recall did not need.
  function compose(learning, recall, fresh = []) {
    const learnEntries = learning.filter((e) => e.mcq).map((e) => ({ kind: "learning", card: e.card, mcq: e.mcq, ls: e.learning }));
    const recallEntries = recall.map((e) => ({ kind: "recall", card: e.card, srs: e.srs, ls: e.learning }));
    const freshEntries = fresh.map((e) => ({ kind: "recall", card: e.card, srs: null, ls: e.learning }));
    return [...learnEntries, ...recallEntries, ...freshEntries].slice(0, SESSION_SIZE);
  }

  useEffect(() => {
    let alive = true;
    Promise.all([api.study.learn(deck?.id), deck?.courseId ? api.exams.upcoming() : Promise.resolve([])])
      .then(([data, exams]) => {
        if (!alive) return;
        setQueue(compose(data.learning, data.recall, data.fresh)); setIdx(0);
        setPicked(null); setInspect(null); setMcqResult(null); setRevealed(false); setSuggested(null); setRecallAck(null);
        setGraduatedThisSession([]); setRecallIdx(0);
        if (deck?.courseId && data.recall.length) {
          const cards = data.recall.map((e) => ({ id: e.card.id, courseId: deck.courseId }));
          const srs = Object.fromEntries(data.recall.map((e) => [e.card.id, e.srs || {}]));
          const ramp = examRamp({ exams, cards, srs, now: localDateStr() });
          const lead = ramp.length > 0 && ramp[0].cardIds.length > 0 ? ramp[0].exam : null;
          setRampExam(lead);
          // FC·2: project the WHOLE deck to exam day (server read); the banner line waits for it
          if (lead?.date) api.study.forecast(lead.date).then((f) => { if (alive) setRampCast(f); });
          else setRampCast(null);
        } else { setRampExam(null); setRampCast(null); }
      });
    return () => { alive = false; };
  }, [deck?.id]);

  const composedDone = queue !== null && idx >= queue.length;
  const endDone = recallIdx >= graduatedThisSession.length;
  const finished = composedDone && endDone;
  const worked = recalled > 0 || learned > 0;

  useEffect(() => {
    if (!finished || !worked) return;
    let alive = true;
    Promise.all([api.study.overview(), api.study.learn(deck?.id)]).then(([ov, data]) => {
      if (alive) setSummary({ reviewedToday: ov.reviewedToday, remaining: data.learning.filter((e) => e.mcq).length + data.recall.length + data.fresh.length, fresh: data });
    });
    return () => { alive = false; };
  }, [finished]);

  // AX·1 — keyboard operability across the LearnSession's phases (ref set only in the flashcard render).
  const kbdRef = useRef(null);
  useEffect(() => {
    function onKey(e) {
      const k = kbdRef.current;
      if (!k) return;
      if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
      if (!k.revealed) { if (!k.typingNow && (e.key === " " || e.key === "Enter")) { e.preventDefault(); k.reveal(); } return; }
      const g = { 1: "again", 2: "hard", 3: "good", 4: "easy" }[e.key];
      if (g) { e.preventDefault(); k.grade(g); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  kbdRef.current = null; // reset each render; the flashcard path re-arms it below

  if (queue === null) return <div className="review"><p className="muted">Loading…</p></div>;

  function resetEncounter() { setPicked(null); setInspect(null); setMcqResult(null); setRevealed(false); setSuggested(null); setRecallAck(null); }
  function advanceComposed() { resetEncounter(); setIdx((i) => i + 1); }
  function advanceEnd() { resetEncounter(); setRecallIdx((i) => i + 1); }

  if (finished) {
    if (!worked) return (
      <div className="review review-done">
        <div className="review-deck">{heading}</div>
        <h1>All caught up</h1>
        <p className="muted">Nothing to study here right now — come back when more is due.</p>
        <button className="btn" onClick={onExit}>Done</button>
      </div>
    );
    const held = tally.good + tally.easy;
    const longGaps = intervals.filter((d) => d >= 6).length;
    const more = !!summary && summary.remaining > 0;
    function keepGoing() {
      setQueue(compose(summary.fresh.learning, summary.fresh.recall));
      setIdx(0); resetEncounter(); setSummary(null);
      setGraduatedThisSession([]); setRecallIdx(0);
      setRecalled(0); setLearned(0); setGraduatedCount(0); setTally({ again: 0, hard: 0, good: 0, easy: 0 }); setIntervals([]); setTouched(new Set());
    }
    return (
      <div className="review review-done">
        <div className="review-deck">{heading}</div>
        <div className="done-star" aria-hidden="true">★</div>
        <h1>Nice work</h1>
        {recalled > 0 && <p className="done-stats">You recalled <b>{recalled}</b> card{recalled === 1 ? "" : "s"}.</p>}
        {recalled > 0 && (
          <ul className="done-grades">
            <li className="dg-again"><b>{tally.again}</b>Again</li>
            <li><b>{tally.hard}</b>Hard</li>
            <li><b>{tally.good}</b>Good</li>
            <li><b>{tally.easy}</b>Easy</li>
          </ul>
        )}
        {recalled > 0 && <p className="done-held">You held <b>{held}</b> of {recalled} — Good or Easy.</p>}
        {graduatedCount > 0 && <p className="done-movement">{graduatedCount} card{graduatedCount === 1 ? "" : "s"} graduated from recognition to recall.</p>}
        {recalled > 0 && longGaps > 0 && <p className="done-movement">{longGaps} card{longGaps === 1 ? "" : "s"} won't be back for 6+ days.</p>}
        {summary && <p className="done-overview">{summary.reviewedToday} reviewed today · {summary.remaining} still due</p>}
        <div className="done-actions">
          {more && <button className="btn" onClick={keepGoing}>Keep going</button>}
          <button className={more ? "btn btn-ghost" : "btn"} onClick={onExit}>Done for now</button>
        </div>
      </div>
    );
  }

  // progress: the composed sitting, then (its own count) the end-of-quiz recall block.
  const progress = composedDone
    ? <span className="review-progress">{Math.min(recallIdx + 1, graduatedThisSession.length)} / {graduatedThisSession.length}</span>
    : <span className="review-progress">{Math.min(idx + 1, queue.length)} / {queue.length}</span>;

  async function toggleBookmark(id, current, patch) {
    const r = await api.study.bookmark(id, !current);
    if (r.ok) patch(!current);
  }

  // ── PHASE 1 — a composed LEARNING encounter → its MCQ (recognition). No flip, no re-queue (L·3c). ──
  if (!composedDone && queue[idx].kind === "learning") {
    const card = queue[idx].card;
    const mcq = queue[idx].mcq;
    if (orderRef.current.idx !== idx) {
      const order = mcq.choices.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
      orderRef.current = { idx, order };
    }
    const answered = picked !== null;
    const explanationFor = (i) =>
      i === mcq.answer ? mcq.why
        : (Array.isArray(mcq.explanations) && typeof mcq.explanations[i] === "string" && mcq.explanations[i].trim())
          ? mcq.explanations[i] : "one of the wrong options — the right answer is highlighted above";

    async function pickMcq(i) {
      if (answered) return;
      setPicked(i); setInspect(mcq.answer);
      const correct = i === mcq.answer;
      if (correct) setLearned((n) => n + 1);
      const r = await api.study.quizAnswer(card.id, correct);
      const ls = r.ok && r.data ? r.data : null;
      const graduated = !!ls && ls.phase === PHASES.PROBATION;
      if (graduated) setGraduatedThisSession((prev) => [...prev, { card }]); // L·3c: recall it at the END of this quiz
      setMcqResult({ correct, graduated, streak: ls ? ls.streak : 0 });
    }

    return (
      <div className="review">
        <header className="review-top">
          <button className="link" onClick={onExit}>← {deck ? "Decks" : "Dashboard"}</button>
          {progress}
        </header>
        <div className="review-deck">Learn · {mcq.topic}</div>
        <section className="flashcard mcq-card">
          <p className="card-front">{mcq.stem}</p>
          <ul className="mcq-choices">
            {orderRef.current.order.map((i) => {
              let cls = "mcq-choice";
              if (answered && i === mcq.answer) cls += " mcq-correct";
              if (answered && i === picked && i !== mcq.answer) cls += " mcq-wrong";
              if (answered && i === inspect) cls += " mcq-inspect";
              return <li key={i}><button className={cls} onClick={() => (answered ? setInspect(i) : pickMcq(i))}>{mcq.choices[i]}</button></li>;
            })}
          </ul>
          {answered && inspect !== null && (
            <p className="mcq-why"><b>{inspect === mcq.answer ? "Why it's right: " : "Why it's wrong: "}</b>{explanationFor(inspect)}</p>
          )}
        </section>
        <button className={card.bookmarked ? "bookmark-btn on" : "bookmark-btn"} aria-pressed={!!card.bookmarked}
          onClick={() => toggleBookmark(card.id, card.bookmarked, (v) => setQueue((prev) => prev.map((e, k) => (k === idx ? { ...e, card: { ...e.card, bookmarked: v } } : e))))}>
          <BookmarkIcon on={!!card.bookmarked} /> {card.bookmarked ? "Bookmarked" : "Bookmark"}
        </button>
        <ReportAffordance key={card.id} cardId={card.id} shownFront={mcq.stem} />
        {!answered
          ? <p className="muted mcq-hint">Recognize it first — tap the answer you think is right.</p>
          : mcqResult && mcqResult.graduated
            ? <><p className="mcq-verdict right" role="status" aria-live="polite">Nice — that's it {GRADUATE_AT} in a row. <b>Graduated!</b> You'll recall it at the end of this quiz.</p>
                <button className="btn btn-reveal" onClick={advanceComposed}>Next</button></>
            : mcqResult && mcqResult.correct
              ? <><p className="mcq-verdict right" role="status" aria-live="polite">Right — {mcqResult.streak} of {GRADUATE_AT} toward recall. You'll see it in your next quiz.</p>
                  <button className="btn btn-reveal" onClick={advanceComposed}>Next</button></>
              : <><p className="mcq-verdict" role="status" aria-live="polite">Not quite — no worries, you'll see it again. Tap any choice to see why.</p>
                  <button className="btn btn-reveal" onClick={advanceComposed}>Next</button></>}
      </div>
    );
  }

  // ── A flashcard: a composed recall (probation from a PRIOR session, or a review card), OR an
  //    end-of-quiz recall for a card that graduated THIS session (L·3c — deferred, not a mid-flip). ──
  const endBlock = composedDone; // composed is done → we're in the end-of-quiz recall block
  const card = endBlock ? graduatedThisSession[recallIdx].card : queue[idx].card;
  const srs = endBlock ? null : queue[idx].srs; // a just-graduated card has no FSRS state yet (first recall)
  const armed = endBlock || queue[idx].ls?.phase === PHASES.PROBATION; // the demotion is live on the FIRST recall
  const encKey = endBlock ? `g${recallIdx}` : idx;
  const advanceRecall = endBlock ? advanceEnd : advanceComposed;
  const bookmarkPatch = endBlock
    ? (v) => setGraduatedThisSession((prev) => prev.map((e, k) => (k === recallIdx ? { ...e, card: { ...e.card, bookmarked: v } } : e)))
    : (v) => setQueue((prev) => prev.map((e, k) => (k === idx ? { ...e, card: { ...e.card, bookmarked: v } } : e)));
  // IMG·3b — the same mirror for the card's image (absent = no image, never a stored null)
  const withImage = (c, image) => { const next = { ...c }; if (image) next.image = image; else delete next.image; return next; };
  const imagePatch = endBlock
    ? (image) => setGraduatedThisSession((prev) => prev.map((e, k) => (k === recallIdx ? { ...e, card: withImage(e.card, image) } : e)))
    : (image) => setQueue((prev) => prev.map((e, k) => (k === idx ? { ...e, card: withImage(e.card, image) } : e)));
  if (pickRef.current.enc !== encKey || pickRef.current.cardId !== card.id) {
    const pool = [card.front, ...(Array.isArray(card.variants) ? card.variants : [])];
    const last = lastShownRef.current.get(card.id);
    const candidates = pool.length > 1 && last != null ? pool.filter((p) => p !== last) : pool;
    const text = candidates[Math.floor(Math.random() * candidates.length)] ?? card.front;
    pickRef.current = { enc: encKey, cardId: card.id, text };
    lastShownRef.current.set(card.id, text);
  }
  const displayFront = pickRef.current.text;

  async function grade(g) {
    // AX·2: an offline/failed save keeps the card + shows an honest banner (non-destructive).
    let r;
    try { r = await api.study.review(card.id, g); } catch { setOffline(true); return; }
    if (!r.ok) { setOffline(true); return; }
    setOffline(false);
    const iv = r.ok && r.data && typeof r.data.intervalDays === "number" ? r.data.intervalDays : null;
    const ls = r.ok && r.data ? r.data.learning : null;
    setRecalled((n) => n + 1);
    setTally((t) => ({ ...t, [g]: t[g] + 1 }));
    if (iv != null) setIntervals((xs) => [...xs, iv]);
    if (Array.isArray(card.tags) && card.tags.length) setTouched((prev) => { const n = new Set(prev); card.tags.forEach((t) => n.add(t)); return n; });
    const demoted = !!ls && ls.phase === PHASES.LEARNING;
    if (armed) {
      if (!demoted) setGraduatedCount((n) => n + 1); // passed its first cold recall — a real graduation
      setRevealed(false); setSuggested(null);
      setRecallAck({ demoted });
      return; // an honest one-line acknowledgment; Continue advances
    }
    // an already-review card in the composed queue: Again re-shows later this session (classic)
    if (g === "again") setQueue((prev) => [...prev, { kind: "recall", card, srs: r.ok && r.data ? r.data : null }]);
    advanceComposed();
  }

  if (recallAck) return (
    <div className="review">
      <header className="review-top"><button className="link" onClick={onExit}>← {deck ? "Decks" : "Dashboard"}</button>{progress}</header>
      <div className="review-deck">{heading}</div>
      <section className="flashcard recall-ack" role="status" aria-live="polite">
        {recallAck.demoted
          ? <p className="ack-copy">No problem — back to the quiz pool. You'll see its question again to lock it in before recall.</p>
          : <p className="ack-copy">Learned it — it's on your review schedule now. <span className="ack-star" aria-hidden="true">★</span></p>}
      </section>
      <button className="btn btn-reveal" onClick={advanceRecall}>Continue</button>
    </div>
  );

  // AX·1 — the flashcard phase: arm the keyboard controls for the window handler above.
  kbdRef.current = { revealed, typingNow: typeMode && typeEligible(card), reveal: () => setRevealed(true), grade };

  return (
    <div className="review">
      <header className="review-top">
        <button className="link" onClick={onExit}>← {deck ? "Decks" : "Dashboard"}</button>
        {progress}
      </header>
      <div className="review-deck">{endBlock ? "End of quiz — recall what you graduated" : armed ? "First recall — no options this time" : heading}</div>
      <label className="type-toggle">
        <input type="checkbox" checked={typeMode} onChange={(e) => { setTypeMode(e.target.checked); setTypeModeStored(e.target.checked); }} />
        <span>Type answers</span>
      </label>
      {offline && <p className="offline-note" role="alert">You're offline — your place is saved. Reconnect, then grade again.</p>}
      {rampExam && !armed && !endBlock && (
        <div className="ramp-banner" role="note">
          <span className="ramp-lead">Up first: {rampExam.title} {rampExam.daysUntil === 0 ? "today" : rampExam.daysUntil === 1 ? "tomorrow" : `in ${rampExam.daysUntil} days`}</span>
          <span className="ramp-sub">Your weakest cards for it are leading — same session, just re-ordered.</span>
          {rampCast?.overall?.mean != null && (
            <span className="ramp-cast">On exam day: ~{Math.round(rampCast.overall.mean * 100)}% retrievable{rampCast.byTag.length > 0 ? ` · weakest: ${rampCast.byTag[0].tag} at ${Math.round(rampCast.byTag[0].mean * 100)}%` : ""}</span>
          )}
        </div>
      )}
      <section className="flashcard">
        {card.reversalOf && <span className="rev-flag" title="Reversed card — generated from another card in this deck">↔</span>}
        {/* IMG·3b — the picture sits ABOVE the question and never over it: it is a cue, not the card. */}
        <CardImage image={card.image} />
        <p className="card-front" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceFront(card, displayFront)) }} />
        {revealed && <><hr className="card-rule" /><p className="card-back" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceBack(card)) }} /></>}
      </section>
      <button className={card.bookmarked ? "bookmark-btn on" : "bookmark-btn"} aria-pressed={!!card.bookmarked}
        onClick={() => toggleBookmark(card.id, card.bookmarked, bookmarkPatch)}>
        <BookmarkIcon on={!!card.bookmarked} /> {card.bookmarked ? "Bookmarked" : "Bookmark"}
      </button>
      <ReportAffordance key={card.id} cardId={card.id} shownFront={displayFront} />

      {/* IMG·3b — the picture affordance: opening the picker adds nothing; only a tap inside it does. */}
      <div className="image-affordance">
        <button type="button" className="link" onClick={() => setPicking((v) => !v)}>
          {card.image ? "Change picture" : "Add a picture"}
        </button>
        {card.image && <button type="button" className="link" onClick={() => detachImage(card.id, imagePatch)}>Remove picture</button>}
      </div>
      {/* IMG·F·1 — the key is PREFIXED: ReportAffordance above is also keyed by card.id, and two
          siblings sharing a key hit React's duplicated-and/or-omitted reconciliation hazard — the
          field defect was the report link accreting once per card advance while the picker was open. */}
      {picking && (
        <ImagePicker key={`img-${card.id}`} card={card}
          onAttached={(image) => { imagePatch(image); setPicking(false); }}
          onClose={() => setPicking(false)} />
      )}

      {!revealed
        ? (typeMode && typeEligible(card)
            ? <TypeAnswerField card={card} onVerdict={(v) => { setSuggested(VERDICT_GRADE[v]); setRevealed(true); }} />
            : <button className="btn btn-reveal" onClick={() => setRevealed(true)}>Show answer</button>)
        : (() => {
            const p = activePreview(srs ?? null);
            const sub = (g) => (g === "again" ? "now" : fmtDays(p[g].intervalDays));
            const cls = (g) => `grade grade-${g}${suggested === g ? " is-suggested" : ""}`;
            return (
              <>
              {suggested && <p className={`type-verdict type-${suggested}`} role="status" aria-live="polite">{TYPE_VERDICT_LABEL[suggested]}</p>}
              <div className="grades">
                <button className={cls("again")} aria-current={suggested === "again"} onClick={() => grade("again")}>{armed ? "Blank" : "Again"}<span className="grade-sub">{sub("again")}</span></button>
                <button className={cls("hard")} aria-current={suggested === "hard"} onClick={() => grade("hard")}>Hard<span className="grade-sub">{sub("hard")}</span></button>
                <button className={cls("good")} aria-current={suggested === "good"} onClick={() => grade("good")}>Good<span className="grade-sub">{sub("good")}</span></button>
                <button className={cls("easy")} aria-current={suggested === "easy"} onClick={() => grade("easy")}>Easy<span className="grade-sub">{sub("easy")}</span></button>
              </div>
              </>
            );
          })()}
    </div>
  );
}

// Reused for a deck's due queue (deck prop), a weak-topic drill (topic prop), and the
// bookmarked-only session (bookmarksOnly prop, E·2) — all three feed the same { card, srs } shape.
function ReviewSession({ deck, topic, bookmarksOnly, onExit }) {
  const [queue, setQueue] = useState(null); // [{ card }] or null while loading
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [typeMode, setTypeMode] = useState(typeModeStored); // TA·2: opt-in type-the-answer (persisted)
  const [suggested, setSuggested] = useState(null);
  const [offline, setOffline] = useState(false); // AX·2: an honest, non-destructive offline-save banner
  const [picking, setPicking] = useState(false); // IMG·3b — the image picker is open for THIS card
  const [reviewed, setReviewed] = useState(0);
  // F·1: per-session tallies drive the done-screen summary. `tally` counts each grade; `intervals`
  // collects the server-returned SM-2 intervalDays per review (schedule movement, no core call);
  // `summary` is the post-session refresh (reviewed-today + still-due + the fresh queue for Keep going).
  const [tally, setTally] = useState({ again: 0, hard: 0, good: 0, easy: 0 });
  const [intervals, setIntervals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rampExam, setRampExam] = useState(null); // E·4 — the near exam steering this session's order (or null)
  const [rampCast, setRampCast] = useState(null); // FC·2 — the exam-day projection shown in the banner
  // E·3 anti-cue-memorization (ported from Alivia's P3·5): per encounter, show a uniformly random
  // phrasing from [front, ...variants], never repeating the phrasing this card showed last time
  // this session. SM-2 neither knows nor cares which phrasing displayed; a card with no variants
  // shows its front.
  const lastShownRef = useRef(new Map()); // cardId -> phrasing shown at its previous encounter
  const pickRef = useRef({ enc: -1, cardId: null, text: null }); // the choice for THIS encounter

  const heading = bookmarksOnly ? "Bookmarked" : topic ? `Weak spot · ${topic.tag}` : deck.name;
  const backLabel = bookmarksOnly ? "Back to bookmarks" : topic ? "Back to weak spots" : "Back to decks";

  // The one loader for all three modes — reused by the initial slice, the post-session refresh,
  // and Keep going.
  function loadQueue() {
    return bookmarksOnly ? api.study.bookmarks().then((b) => b.cards) : topic ? api.study.quiz(topic.tag) : api.study.due(deck.id);
  }

  // F·1: bound the sitting to the first SESSION_SIZE the loader returns. Re-queued "again" cards
  // (below) still ride on top, so the working set can nudge past 20 — never the full due count.
  useEffect(() => {
    let alive = true;
    // E·4 — load the sitting; for a deck session, decide the ramp banner CLIENT-side (bundled examRamp)
    // from the SAME loaded queue + the countdown, so the banner honestly names the exam that steered the
    // order. Read-only: the queue is already server-ordered, this only mirrors WHY. Topic/bookmark drills
    // aren't course-scoped, so they never ramp.
    Promise.all([loadQueue(), deck?.courseId ? api.exams.upcoming() : Promise.resolve([])])
      .then(([q, exams]) => {
        if (!alive) return;
        const slice = q.slice(0, SESSION_SIZE);
        setQueue(slice); setIdx(0); setRevealed(false); setSuggested(null);
        if (deck?.courseId && slice.length) {
          const cards = slice.map((e) => ({ id: e.card.id, courseId: deck.courseId }));
          const srs = Object.fromEntries(slice.map((e) => [e.card.id, e.srs || {}]));
          const ramp = examRamp({ exams, cards, srs, now: localDateStr() });
          const lead = ramp.length > 0 && ramp[0].cardIds.length > 0 ? ramp[0].exam : null;
          setRampExam(lead);
          // FC·2: project the WHOLE deck to exam day (server read); the banner line waits for it
          if (lead?.date) api.study.forecast(lead.date).then((f) => { if (alive) setRampCast(f); });
          else setRampCast(null);
        } else { setRampExam(null); setRampCast(null); }
      });
    return () => { alive = false; };
  }, [deck?.id, topic?.tag, bookmarksOnly]);

  // AX·1 — keyboard operability (ref set only in the flashcard render below; hooks stay before any return).
  const kbdRef = useRef(null);
  useEffect(() => {
    function onKey(e) {
      const k = kbdRef.current;
      if (!k) return;
      if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
      if (!k.revealed) { if (!k.typingNow && (e.key === " " || e.key === "Enter")) { e.preventDefault(); k.reveal(); } return; }
      const g = { 1: "again", 2: "hard", 3: "good", 4: "easy" }[e.key];
      if (g) { e.preventDefault(); k.grade(g); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  kbdRef.current = null; // reset each render; the flashcard path re-arms it below

  const finished = queue !== null && idx >= queue.length;

  // F·1: when a worked session ends, refresh once — reviewed-today + how many are still due (the
  // loader re-run; the server-side queue naturally shrank), and keep the fresh queue for Keep going.
  useEffect(() => {
    if (!finished || reviewed === 0) return;
    let alive = true;
    Promise.all([api.study.overview(), loadQueue()]).then(([ov, q]) => {
      if (alive) setSummary({ reviewedToday: ov.reviewedToday, stillDue: q.length, freshQueue: q });
    });
    return () => { alive = false; };
  }, [finished]);

  if (queue === null) return <div className="review"><p className="muted">Loading…</p></div>;

  if (finished) {
    if (reviewed === 0) return (
      <div className="review review-done">
        <div className="review-deck">{heading}</div>
        <h1>Done for now</h1>
        <p className="muted">{bookmarksOnly ? "Nothing bookmarked right now." : topic ? "Nothing to practice here right now." : "Nothing was due in this deck."}</p>
        <button className="btn" onClick={onExit}>{backLabel}</button>
      </div>
    );
    const held = tally.good + tally.easy;
    const longGaps = intervals.filter((d) => d >= 6).length;
    const more = !!summary && summary.stillDue > 0;
    function keepGoing() {
      setQueue(summary.freshQueue.slice(0, SESSION_SIZE));
      setIdx(0); setRevealed(false); setSuggested(null); setReviewed(0);
      setTally({ again: 0, hard: 0, good: 0, easy: 0 }); setIntervals([]); setSummary(null);
    }
    return (
      <div className="review review-done">
        <div className="review-deck">{heading}</div>
        <div className="done-star" aria-hidden="true">★</div>
        <h1>Nice work</h1>
        <p className="done-stats">You reviewed <b>{reviewed}</b> card{reviewed === 1 ? "" : "s"}.</p>
        <ul className="done-grades">
          <li className="dg-again"><b>{tally.again}</b>Again</li>
          <li><b>{tally.hard}</b>Hard</li>
          <li><b>{tally.good}</b>Good</li>
          <li><b>{tally.easy}</b>Easy</li>
        </ul>
        <p className="done-held">You held <b>{held}</b> of {reviewed} — Good or Easy.</p>
        {longGaps > 0 && <p className="done-movement">{longGaps} card{longGaps === 1 ? "" : "s"} won't be back for 6+ days.</p>}
        {summary && <p className="done-overview">{summary.reviewedToday} reviewed today · {summary.stillDue} still due</p>}
        <div className="done-actions">
          {more && <button className="btn" onClick={keepGoing}>Keep going</button>}
          <button className={more ? "btn btn-ghost" : "btn"} onClick={onExit}>Done for now</button>
        </div>
      </div>
    );
  }

  const card = queue[idx].card;
  // choose the phrasing exactly once per encounter (idempotent across re-renders of the same idx)
  if (pickRef.current.enc !== idx || pickRef.current.cardId !== card.id) {
    const pool = [card.front, ...(Array.isArray(card.variants) ? card.variants : [])];
    const last = lastShownRef.current.get(card.id);
    const candidates = pool.length > 1 && last != null ? pool.filter((p) => p !== last) : pool;
    const text = candidates[Math.floor(Math.random() * candidates.length)] ?? card.front;
    pickRef.current = { enc: idx, cardId: card.id, text };
    lastShownRef.current.set(card.id, text);
  }
  const displayFront = pickRef.current.text;

  // E·2: flag/unflag the current card. Local queue state mirrors the server so the button flips
  // immediately and later encounters of the same card (an "again" re-queue) show the new state.
  // IMG·3b — mirror the attached/detached image locally, the way bookmarking does, so a later
  // encounter of this card in the same session shows the new state.
  function setCardImage(image) {
    setQueue((prev) => prev.map((e) => {
      if (e.card.id !== card.id) return e;
      const next = { ...e.card };
      if (image) next.image = image; else delete next.image; // absent = no image, never a null
      return { ...e, card: next };
    }));
  }

  async function toggleBookmark() {
    const next = !card.bookmarked;
    const r = await api.study.bookmark(card.id, next);
    if (r.ok) setQueue((prev) => prev.map((e) => (e.card.id === card.id ? { ...e, card: { ...e.card, bookmarked: next } } : e)));
  }

  async function grade(g) {
    // AX·2: post FIRST; an offline/failed save keeps the card + shows an honest banner (non-destructive).
    let r;
    try { r = await api.study.review(card.id, g); } catch { setOffline(true); return; }
    if (!r.ok) { setOffline(true); return; }
    setOffline(false);
    setRevealed(false); setSuggested(null);
    const iv = r.ok && r.data && typeof r.data.intervalDays === "number" ? r.data.intervalDays : null;
    setReviewed((n) => n + 1);
    setTally((t) => ({ ...t, [g]: t[g] + 1 }));
    if (iv != null) setIntervals((xs) => [...xs, iv]);
    // re-show later this session; carry the new srs so its grade labels reflect the updated schedule
    if (g === "again") setQueue((prev) => [...prev, { card, srs: r.ok && r.data ? r.data : null }]);
    setIdx((i) => i + 1);
  }

  // AX·1 — this IS the flashcard phase: arm the keyboard controls for the window handler (declared
  // up top, before the early returns, so the hook count is stable across renders).
  kbdRef.current = { revealed, typingNow: typeMode && typeEligible(card), reveal: () => setRevealed(true), grade };

  return (
    <div className="review">
      <header className="review-top">
        <button className="link" onClick={onExit}>{bookmarksOnly ? "← Bookmarks" : topic ? "← Weak spots" : "← Decks"}</button>
        <span className="review-progress">{Math.min(idx + 1, queue.length)} / {queue.length}</span>
      </header>
      <label className="type-toggle">
        <input type="checkbox" checked={typeMode} onChange={(e) => { setTypeMode(e.target.checked); setTypeModeStored(e.target.checked); }} />
        <span>Type answers</span>
      </label>
      {offline && <p className="offline-note" role="alert">You're offline — your place is saved. Reconnect, then grade again.</p>}
      <div className="review-deck">{heading}</div>

      {/* E·4 — the ramp made VISIBLE + honest: say WHY the order changed, name the exam, and be clear
          nothing was added and she isn't behind. Only shows when an in-window exam actually re-ordered. */}
      {rampExam && (
        <div className="ramp-banner" role="note">
          <span className="ramp-lead">Up first: {rampExam.title} {rampExam.daysUntil === 0 ? "today" : rampExam.daysUntil === 1 ? "tomorrow" : `in ${rampExam.daysUntil} days`}</span>
          <span className="ramp-sub">Your weakest cards for it are leading — same session, just re-ordered.</span>
          {rampCast?.overall?.mean != null && (
            <span className="ramp-cast">On exam day: ~{Math.round(rampCast.overall.mean * 100)}% retrievable{rampCast.byTag.length > 0 ? ` · weakest: ${rampCast.byTag[0].tag} at ${Math.round(rampCast.byTag[0].mean * 100)}%` : ""}</span>
          )}
        </div>
      )}

      <section className="flashcard">
        {/* P·3: a reversal is just a card — this marker is the only UI it gets */}
        {card.reversalOf && <span className="rev-flag" title="Reversed card — generated from another card in this deck">↔</span>}
        {/* IMG·3b — the picture sits ABOVE the question and never over it: it is a cue, not the card. */}
        <CardImage image={card.image} />
        <p className="card-front" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceFront(card, displayFront)) }} />
        {revealed && <><hr className="card-rule" /><p className="card-back" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceBack(card)) }} /></>}
      </section>

      {/* visible before AND after reveal — the question alone can be worth flagging */}
      <button className={card.bookmarked ? "bookmark-btn on" : "bookmark-btn"} aria-pressed={!!card.bookmarked}
        onClick={toggleBookmark}>
        <BookmarkIcon on={!!card.bookmarked} /> {card.bookmarked ? "Bookmarked" : "Bookmark"}
      </button>
      <ReportAffordance key={card.id} cardId={card.id} shownFront={displayFront} />

      {/* IMG·3b — the picture affordance, identical to the learn path. */}
      <div className="image-affordance">
        <button type="button" className="link" onClick={() => setPicking((v) => !v)}>
          {card.image ? "Change picture" : "Add a picture"}
        </button>
        {card.image && <button type="button" className="link" onClick={() => detachImage(card.id, setCardImage)}>Remove picture</button>}
      </div>
      {/* IMG·F·1 — prefixed key: never share ReportAffordance's card.id key between siblings. */}
      {picking && (
        <ImagePicker key={`img-${card.id}`} card={card}
          onAttached={(image) => { setCardImage(image); setPicking(false); }}
          onClose={() => setPicking(false)} />
      )}

      {!revealed
        ? (typeMode && typeEligible(card)
            ? <TypeAnswerField card={card} onVerdict={(v) => { setSuggested(VERDICT_GRADE[v]); setRevealed(true); }} />
            : <button className="btn btn-reveal" onClick={() => setRevealed(true)}>Show answer</button>)
        : (() => {
            // S·3: predicted intervals per grade, client-side from this card's FSRS state (null =
            // new card → first-rating labels). "again" re-shows now this session, so it reads "now".
            const p = activePreview(queue[idx].srs ?? null);
            const sub = (g) => (g === "again" ? "now" : fmtDays(p[g].intervalDays));
            const cls = (g) => `grade grade-${g}${suggested === g ? " is-suggested" : ""}`;
            return (
              <>
              {suggested && <p className={`type-verdict type-${suggested}`} role="status" aria-live="polite">{TYPE_VERDICT_LABEL[suggested]}</p>}
              <div className="grades">
                <button className={cls("again")} aria-current={suggested === "again"} onClick={() => grade("again")}>Again<span className="grade-sub">{sub("again")}</span></button>
                <button className={cls("hard")} aria-current={suggested === "hard"} onClick={() => grade("hard")}>Hard<span className="grade-sub">{sub("hard")}</span></button>
                <button className={cls("good")} aria-current={suggested === "good"} onClick={() => grade("good")}>Good<span className="grade-sub">{sub("good")}</span></button>
                <button className={cls("easy")} aria-current={suggested === "easy"} onClick={() => grade("easy")}>Easy<span className="grade-sub">{sub("easy")}</span></button>
              </div>
              </>
            );
          })()}
    </div>
  );
}

// E·2 — the collection of flagged cards (ported from Alivia's P3·8). Front + tags, unbookmark, and
// a bookmarked-only review session ("review these now"); P·2 added the flagged-MCQ section, so a
// question flagged mid-practice lands here too.
function BookmarkedView({ onBack }) {
  const [data, setData] = useState(null); // { cards: [{ card, srs }], mcqs } or null while loading
  const [reviewing, setReviewing] = useState(false);

  // BIG·5c·2 — her library, so a chapter-minted card can name the chapter it came from
  const [sources, setSources] = useState([]);

  function refresh() { return api.study.bookmarks().then(setData); }
  useEffect(() => { refresh(); }, []);
  useEffect(() => { api.library.list().then((l) => setSources(l.sources)).catch(() => {}); }, []);

  if (reviewing) return <ReviewSession bookmarksOnly onExit={() => { setReviewing(false); refresh(); }} />;

  async function unbookmark(id) {
    const r = await api.study.bookmark(id, false);
    if (r.ok) setData((prev) => ({
      cards: prev.cards.filter((e) => e.card.id !== id),
      mcqs: prev.mcqs.filter((m) => m.id !== id),
    }));
  }

  const empty = data !== null && data.cards.length === 0 && data.mcqs.length === 0;
  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Study</button>
      </header>
      <div className="study-head">
        <h1>Bookmarked</h1>
        {data !== null && data.cards.length > 0 &&
          <button className="btn btn-sm" onClick={() => setReviewing(true)}>Review these now</button>}
      </div>
      {data === null
        ? <p className="muted">Loading…</p>
        : empty
          ? <p className="muted empty">Nothing bookmarked — flag cards during review to collect them here.</p>
          : <>
              {data.cards.length > 0 &&
                <ul className="deck-list">
                  {data.cards.map(({ card }) => (
                    <li key={card.id} className="deck-row">
                      <div className="deck-meta">
                        <span className="deck-name" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceFront(card, card.front)) }} />
                        {Array.isArray(card.tags) && card.tags.length > 0 &&
                          <span className="deck-counts">{card.tags.join(" · ")}</span>}
                        <SourceLine card={card} sources={sources} />
                      </div>
                      <div className="deck-actions">
                        <button className="link" onClick={() => unbookmark(card.id)}>Remove</button>
                      </div>
                    </li>
                  ))}
                </ul>}
              {data.mcqs.length > 0 && <>
                <h2 className="bm-sub">Practice questions</h2>
                <ul className="deck-list">
                  {data.mcqs.map((m) => (
                    <li key={m.id} className="deck-row">
                      <div className="deck-meta">
                        <span className="deck-name">{m.stem}</span>
                        <span className="deck-counts">{m.topic}{m.level != null ? ` · level ${m.level}` : ""}</span>
                      </div>
                      <div className="deck-actions">
                        <button className="link" onClick={() => unbookmark(m.id)}>Remove</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>}
            </>}
    </div>
  );
}

function AddCards({ deck, onDone }) {
  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [added, setAdded] = useState(0);
  const [busy, setBusy] = useState(false);
  const ready = front.trim() && back.trim();

  async function add() {
    if (!ready || busy) return;
    setBusy(true);
    await api.study.createCard(deck.id, front.trim(), back.trim());
    setBusy(false);
    setAdded((n) => n + 1);
    setFront(""); setBack("");
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onDone}>← Decks</button>
        <span className="review-progress">{added} added</span>
      </header>
      <h1 className="study-head">Add cards · {deck.name}</h1>
      <div className="cardform">
        <label className="field"><span>Front</span>
          <textarea rows={2} value={front} onChange={(e) => setFront(e.target.value)} autoFocus /></label>
        <label className="field"><span>Back</span>
          <textarea rows={2} value={back} onChange={(e) => setBack(e.target.value)} /></label>
        <div className="cardform-actions">
          <button className="btn" onClick={add} disabled={!ready || busy}>Add card</button>
          <button className="link" onClick={onDone}>Done</button>
        </div>
      </div>
    </div>
  );
}

// Paste notes -> AI preview (server-authoritative, not yet saved) -> vet/edit -> confirm into the deck.
const MAX_NOTES = 8000; // mirrors the server cap so the counter warns before the request is rejected
const MAX_EDGE = 1568;  // downscale long edge — the model reads plenty of detail at this size

/**
 * C·2 — client-side downscale + re-encode before upload: long edge <= MAX_EDGE, JPEG q~0.82, ALWAYS
 * output image/jpeg (normalizes iPhone HEIC where the browser can decode it), EXIF orientation honored.
 * ISOLATED + injectable so jsdom (no canvas) stubs its output via window.__downscaleImage; the real
 * canvas path is exercised by construction, not by the offline suite. Throws on an undecodable file
 * (e.g. HEIC on a Chromebook) so the caller can show the honest "try a JPG or PNG" copy.
 * @returns {Promise<{ mediaType: string, data: string }>}  base64 (no data: prefix)
 */
async function downscaleImage(file) {
  if (typeof window !== "undefined" && window.__downscaleImage) return window.__downscaleImage(file);
  let bmp, w, h;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    w = bmp.width; h = bmp.height;
  } catch {
    const img = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const el = new Image();
      el.onload = () => { URL.revokeObjectURL(url); resolve(el); };
      el.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
      el.src = url;
    });
    bmp = img; w = img.naturalWidth; h = img.naturalHeight;
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
  canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return { mediaType: "image/jpeg", data: canvas.toDataURL("image/jpeg", 0.82).split(",")[1] };
}

/**
 * C·4 — read a PDF file to base64 (no downscale; the server caps decoded size). Isolated +
 * injectable via window.__readPdf so jsdom (no real FileReader flow) stubs its output. Throws on a
 * read failure so the caller can show honest copy.
 * @returns {Promise<{ mediaType: string, data: string }>}
 */
async function readPdf(file) {
  if (typeof window !== "undefined" && window.__readPdf) return window.__readPdf(file);
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("read failed"));
    fr.readAsDataURL(file);
  });
  return { mediaType: "application/pdf", data: String(dataUrl).split(",")[1] };
}

/** Approx decoded byte length of a base64 string — for the attached-file size chip. */
function b64Bytes(data) {
  const pad = data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0;
  return Math.floor((data.length * 3) / 4) - pad;
}
function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  // BIG·5b·1 — a chapter library is measured in GB, and "4096.0 MB" is not a number anyone reads.
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// NTS·4 — the honest framing, verbatim wherever notes appear (the same line as her sister's app:
// notes are a COMPANION surface; the cards are still where the learning happens).
const NOTES_TAGLINE = "Notes orient you; cards make it stick.";

function GenerateCards({ deck, onDone }) {
  const [notes, setNotes] = useState("");
  const [attach, setAttach] = useState(null);    // C·2/C·4: { kind: "image"|"pdf", name, mediaType, data }
  const [clozeMode, setClozeMode] = useState(false); // CZ·3 — OPTED fill-in-the-blank generation (never the default)
  // NTS·4 — what this capture produces. "cards" is the DEFAULT and its path is byte-unchanged.
  const [product, setProduct] = useState("cards"); // "cards" | "notes" | "both"
  const [phase, setPhase] = useState("compose"); // "compose" | "preview"
  const [cards, setCards] = useState([]);        // editable preview: [{ _k, front, back, tags }]
  const [noteDraft, setNoteDraft] = useState(null); // NTS·4: { title, sections: [{ heading, content, keyTerms? }] }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tooLong = notes.length > MAX_NOTES;
  // An attachment makes notes optional (they ride as guidance); otherwise notes are required.
  const canGenerate = !busy && (attach ? true : Boolean(notes.trim()) && !tooLong);

  async function onPickPhoto(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ""; // let her re-pick the same file after removing it
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { mediaType, data } = await downscaleImage(file);
      setAttach({ kind: "image", name: file.name, mediaType, data });
    } catch {
      setError("Couldn't read this image — try a JPG or PNG.");
    } finally {
      setBusy(false);
    }
  }
  async function onPickPdf(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { data } = await readPdf(file);
      setAttach({ kind: "pdf", name: file.name, mediaType: "application/pdf", data });
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally {
      setBusy(false);
    }
  }
  function removeAttach() { setAttach(null); setError(""); }

  /** The source union body for the notes route — the SAME shape the card generate route takes. */
  function noteSource() {
    const guide = notes.trim() || undefined;
    if (attach?.kind === "image") return { image: { mediaType: attach.mediaType, data: attach.data }, ...(guide ? { notes: guide } : {}) };
    if (attach?.kind === "pdf") return { pdf: { mediaType: attach.mediaType, data: attach.data }, ...(guide ? { notes: guide } : {}) };
    return { notes };
  }

  /**
   * NTS·4 — fetch the notes preview. For "both" the draft cards ride along so the model maps each to
   * a section (that mapping becomes each card's noteRef at save). A notes FAILURE inside "both" is
   * non-fatal by design: the cards are already good, so she keeps them rather than losing the capture.
   */
  async function fetchNotes(draftCards) {
    const r = await api.notes.generate(noteSource(), draftCards);
    if (r.ok && r.data?.deferred) return { error: "Couldn't build notes from that — give it another try." };
    if (!r.ok || !r.data?.preview) return { error: "Couldn't build notes from that — give it another try." };
    const p = r.data.preview;
    if (!p.sections || p.sections.length === 0) return { error: "No notes came back — try a longer or clearer source." };
    return {
      note: { title: p.title || "", sections: p.sections.map((s) => ({ ...s })) },
      cardSections: Array.isArray(r.data.cardSections) ? r.data.cardSections : null,
    };
  }

  async function generate() {
    if (!canGenerate) return;
    setBusy(true); setError("");
    try {
      // Notes-only never asks for cards at all — one call, no card generation.
      if (product === "notes") {
        const n = await fetchNotes();
        if (n.error) { setError(n.error); return; }
        setCards([]); setNoteDraft(n.note); setPhase("preview");
        return;
      }
      const guide = notes.trim() || undefined;
      const r = attach?.kind === "image"
        ? await api.study.generateImage(deck.id, { mediaType: attach.mediaType, data: attach.data }, guide, clozeMode)
        : attach?.kind === "pdf"
          ? await api.study.generatePdf(deck.id, { mediaType: attach.mediaType, data: attach.data }, guide, clozeMode)
          : await api.study.generate(deck.id, notes, clozeMode);
      const code = r.data?.code;
      if (r.ok && r.data && Array.isArray(r.data.cards)) {
        const previewCards = r.data.cards.map((c, i) => ({ _k: i, front: c.front, back: c.back, tags: c.tags || [], selected: true, dup: null, section: null }));
        setCards(previewCards);
        setNoteDraft(null);
        setPhase("preview");
        // NTS·4 "both": now ask for the notes, handing the drafts over for the section mapping.
        if (product === "both") {
          const n = await fetchNotes(previewCards.map(({ front, back }) => ({ front, back })));
          if (n.error) {
            setError(`${n.error} Your cards are ready below.`); // the cards survive a notes failure
          } else {
            setNoteDraft(n.note);
            if (n.cardSections) setCards((prev) => prev.map((c, i) => ({ ...c, section: n.cardSections[i] ?? null })));
          }
        }
        // PC·1: flag near-duplicates against the deck; flagged cards are DESELECTED (one tap re-adds)
        api.study.checkDuplicates(deck.id, previewCards.map(({ front, back }) => ({ front, back }))).then((dr) => {
          const flags = dr.ok && dr.data && Array.isArray(dr.data.flags) ? dr.data.flags : [];
          setCards((prev) => prev.map((c, i) => flags[i]?.match ? { ...c, dup: flags[i].match, selected: false } : c));
        }).catch(() => {});
      } else if (r.status === 422 || code === "unparseable") {
        setError(attach ? "Couldn't read that — try a clearer photo or a different PDF." : "Couldn't read that — shorten your notes and try again.");
      } else if (r.status === 415 || code === "unsupported") {
        setError(attach?.kind === "pdf" ? "That file isn't a readable PDF — export or save it as a PDF." : "That image type isn't supported — use a JPG, PNG, or WebP.");
      } else if (code === "too_big") {
        setError(attach?.kind === "pdf" ? "That PDF is too large — try fewer pages or split it." : "That photo is too large — try a smaller photo.");
      } else if (code === "too_long") {
        setError(`Those notes are a bit long — trim them under ${MAX_NOTES} characters and retry.`);
      } else if (r.status === 413) {
        // X·1: a 413 with no JSON code is an upload-size rejection UPSTREAM of the app (proxy /
        // body limit) — it must never masquerade as notes-too-long or PDF-size copy.
        setError("That file is too large to upload — try a smaller one.");
      } else {
        setError("Something went wrong generating cards. Give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally {
      setBusy(false); // always clears the spinner, success or failure
    }
  }

  function editCard(k, field, value) {
    setCards((prev) => prev.map((c) => (c._k === k ? { ...c, [field]: value } : c)));
  }
  function dropCard(k) { setCards((prev) => prev.filter((c) => c._k !== k)); }

  // CZ·4: a VALID cloze front (answers live in the deletions) is save-eligible with an EMPTY back —
  // matches CZ·3's normalizeCard + save routes. A malformed cloze front stays ineligible.
  const clozeSaveable = (front) => isCloze(front) && parseCloze(front).ok;
  const keepable = cards.filter((c) => c.selected && c.front.trim() && (c.back.trim() || clozeSaveable(c.front)));
  function toggleCard(k) { setCards((prev) => prev.map((c) => (c._k === k ? { ...c, selected: !c.selected } : c))); }

  // NTS·4 — edit a note section in place (heading + content only; key terms are the model's).
  function editSection(i, field, value) {
    setNoteDraft((prev) => prev && ({ ...prev, sections: prev.sections.map((s, j) => (j === i ? { ...s, [field]: value } : s)) }));
  }
  // A section the server's cleanNotes would DROP (blank heading or content) — she must fix it, or the
  // saved note silently loses that section and takes its cards' provenance with it.
  const badSections = noteDraft ? noteDraft.sections.filter((s) => !s.heading.trim() || !s.content.trim()).length : 0;
  const noteSaveable = !noteDraft || (Boolean(noteDraft.title.trim()) && noteDraft.sections.length > 0 && badSections === 0);
  const canSave = !busy && noteSaveable && (noteDraft ? true : keepable.length > 0);

  async function confirm() {
    if (!canSave || busy) return;
    setBusy(true); setError("");
    try {
      // SAVE ORDER (the client's job, per NTS·2): the note first, so its id exists before any card
      // references it. If the note fails, NOTHING is saved — she keeps the preview and retries.
      let noteRefFor = () => undefined;
      if (noteDraft) {
        const nr = await api.notes.save({
          title: noteDraft.title.trim(),
          sections: noteDraft.sections.map(({ heading, content, keyTerms }) => ({ heading, content, ...(keyTerms ? { keyTerms } : {}) })),
          sourceKind: attach?.kind === "image" ? "image" : attach?.kind === "pdf" ? "pdf" : "text",
        });
        if (!nr.ok || !nr.data?.id) { setError("Couldn't save those notes — give it another try."); return; }
        const saved = nr.data;
        // POSITIONAL identity (RVW·1): the confirmed preview's section ORDER is the saved order —
        // Save is blocked while any section is blank, so the server's cleanNotes drops nothing.
        // This used to match by HEADING, which was wrong: two sections sharing a heading made the
        // lookup last-wins and pinned a card to the WRONG section. If the counts disagree at all,
        // every ref degrades to NOTHING — provenance is a convenience, and a guess is a lie.
        const aligned = Array.isArray(saved.sections) && saved.sections.length === noteDraft.sections.length;
        noteRefFor = (c) => {
          if (!aligned || !Number.isInteger(c.section)) return undefined;
          return c.section >= 0 && c.section < saved.sections.length ? { noteId: saved.id, section: c.section } : undefined;
        };
      }
      if (keepable.length) {
        const r = await api.study.createCards(deck.id, keepable.map((c) => {
          const ref = noteRefFor(c);
          return { front: c.front, back: c.back, tags: c.tags, ...(ref ? { noteRef: ref } : {}) };
        }));
        if (!r.ok) { setError("Couldn't save those cards — give it another try."); return; }
      }
      onDone();
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally {
      setBusy(false);
    }
  }

  if (phase === "preview") {
    return (
      <div className="study">
        <header className="study-top">
          <button className="link" onClick={() => { setPhase("compose"); setError(""); }}>← Back</button>
          <span className="review-progress">{keepable.length} to save</span>
        </header>
        <h1 className="study-head">{noteDraft && !cards.length ? "Review notes" : "Review cards"} · {deck.name}</h1>
        {error && <p className="error" role="alert">{error}</p>}
        {noteDraft && (
          <section className="note-preview">
            <p className="notes-tagline">{NOTES_TAGLINE}</p>
            <label className="field"><span>Notes title</span>
              <input value={noteDraft.title} onChange={(e) => setNoteDraft((p) => ({ ...p, title: e.target.value }))} /></label>
            {noteDraft.sections.map((s, i) => (
              <div key={i} className="note-section-edit">
                <label className="field"><span>Section {i + 1}</span>
                  <input value={s.heading} onChange={(e) => editSection(i, "heading", e.target.value)} /></label>
                <label className="field"><span>Notes</span>
                  <textarea rows={4} value={s.content} onChange={(e) => editSection(i, "content", e.target.value)} /></label>
                {s.keyTerms?.length > 0 && <p className="note-terms">{s.keyTerms.join(" · ")}</p>}
              </div>
            ))}
            {badSections > 0 && <p className="error" role="alert">Every section needs a heading and notes — fill in or clear the blanks.</p>}
            {!noteDraft.title.trim() && <p className="error" role="alert">Give these notes a title.</p>}
          </section>
        )}
        {cards.length === 0
          ? (noteDraft ? null : <p className="muted empty">No cards came back — try more detailed notes.</p>)
          : <ul className="gen-preview">
              {cards.map((c) => (
                <li key={c._k} className={`gen-card${c.dup && !c.selected ? " gen-card-skip" : ""}`}>
                  {c.dup && (
                    <p className="dup-note" role="note">You may already have this — “{c.dup.front.length > 60 ? c.dup.front.slice(0, 59) + "…" : c.dup.front}”.{" "}
                      <button className="link" onClick={() => toggleCard(c._k)}>{c.selected ? "Skip it" : "Add anyway"}</button>
                    </p>
                  )}
                  <label className="field"><span>Front</span>
                    <textarea rows={2} value={c.front} onChange={(e) => editCard(c._k, "front", e.target.value)} /></label>
                  <label className="field"><span>Back</span>
                    <textarea rows={2} value={c.back} onChange={(e) => editCard(c._k, "back", e.target.value)} /></label>
                  <div className="gen-card-foot">
                    {c.tags.length > 0 && <span className="gen-tags">{c.tags.join(" · ")}</span>}
                    {noteDraft && c.section != null && noteDraft.sections[c.section] && (
                      // NTS·4 — the provenance tag: which note section this card came from.
                      <span className="gen-section">From: {noteDraft.sections[c.section].heading}</span>
                    )}
                    <button className="link" onClick={() => dropCard(c._k)}>Drop</button>
                  </div>
                </li>
              ))}
            </ul>}
        <div className="cardform-actions">
          <button className="btn" onClick={confirm} disabled={!canSave}>
            {busy ? "Saving…"
              : noteDraft && keepable.length ? `Save notes + ${keepable.length} card${keepable.length === 1 ? "" : "s"}`
                : noteDraft ? "Save notes"
                  : `Save ${keepable.length} card${keepable.length === 1 ? "" : "s"}`}
          </button>
          <button className="link" onClick={onDone}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onDone}>← Decks</button>
      </header>
      <h1 className="study-head">{product === "notes" ? "Generate notes" : "Generate cards"} · {deck.name}</h1>
      <div className="cardform">
        {/* NTS·4 — what this capture makes. Cards is the DEFAULT and its path is unchanged. */}
        <fieldset className="product-picker">
          <legend>Make</legend>
          {[["cards", "Cards"], ["notes", "Study notes"], ["both", "Both"]].map(([v, label]) => (
            <label key={v} className={`product-opt${product === v ? " is-on" : ""}`}>
              <input type="radio" name="product" value={v} checked={product === v}
                onChange={() => { setProduct(v); setError(""); }} />
              <span>{label}</span>
            </label>
          ))}
          <p className="product-hint">{NOTES_TAGLINE}</p>
        </fieldset>
        <label className="field">
          <span>{attach ? "Guidance (optional)" : "Paste your notes"}</span>
          <textarea rows={attach ? 3 : 10} value={notes} autoFocus={!attach}
            placeholder={attach
              ? "Optional — tell the AI what to focus on (e.g. “Chapter 4 definitions”)"
              : "Paste class notes here — a paragraph, a page, a study guide…"}
            onChange={(e) => setNotes(e.target.value)} />
        </label>
        {!attach && <p className={tooLong ? "gen-count over" : "gen-count"}>{notes.length} / {MAX_NOTES} characters</p>}
        <div className="gen-source">
          {attach
            ? <span className="gen-chip">{attach.kind === "pdf" ? "📄" : "📷"} {attach.name} · {fmtBytes(b64Bytes(attach.data))}
                <button className="link" onClick={removeAttach}>Remove</button></span>
            : <>
                <label className="gen-photo-add"><span className="link">Add a photo</span>
                  <input type="file" accept="image/*" onChange={onPickPhoto} style={{ display: "none" }} /></label>
                <label className="gen-photo-add"><span className="link">Add a PDF</span>
                  <input type="file" accept="application/pdf" onChange={onPickPdf} style={{ display: "none" }} /></label>
                <p className="gen-chapter-hint">Whole chapter? Add it to your <strong>Chapter Library</strong> instead — a capture reads about one page&apos;s worth.</p>
              </>}
        </div>
        {/* CZ·3 — the opted cloze toggle. Hidden for notes-only: there are no cards to make cloze. */}
        {product !== "notes" && (
          <label className="cloze-toggle">
            <input type="checkbox" checked={clozeMode} onChange={(e) => setClozeMode(e.target.checked)} />
            <span>Cloze mode — fill-in-the-blank cards (good for lists and dense facts)</span>
          </label>
        )}
        {error && <p className="error" role="alert">{error}</p>}
        <div className="cardform-actions">
          <button className="btn" onClick={generate} disabled={!canGenerate}>
            {busy ? "Generating…" : "Generate"}
          </button>
          <button className="link" onClick={onDone}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// P·2 — the MCQ practice session (ported from Alivia's P3·9, restyled to Estelle's tokens). After
// she answers, EVERY choice is tappable: the correct one shows its `why`; a distractor shows its
// generated explanation (P·4), or an honest placeholder until generate-explanations.js has run.
// No SM-2 — this is a practice bank, not the spaced-repetition deck.
function McqSession({ items, onExit }) {
  const [pool, setPool] = useState(items); // local copy so bookmark flags stay current
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);   // the ORIGINAL choice index she tapped
  const [inspect, setInspect] = useState(null); // the choice whose explanation is open
  const [correct, setCorrect] = useState(0);
  const orderRef = useRef({ idx: -1, order: [] }); // per-question shuffled display order

  const finished = idx >= pool.length;
  if (finished) {
    return (
      <div className="review review-done">
        <div className="review-deck">Practice questions</div>
        {correct > 0 && <div className="done-star" aria-hidden="true">★</div>}
        <h1>{correct === pool.length ? "Perfect round" : "Round complete"}</h1>
        <p className="done-stats">You got <b>{correct}</b> of <b>{pool.length}</b> right.</p>
        <button className="btn" onClick={onExit}>Back to practice</button>
      </div>
    );
  }

  const item = pool[idx];
  // shuffle the choice order exactly once per question (idempotent across re-renders)
  if (orderRef.current.idx !== idx) {
    const order = item.choices.map((_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    orderRef.current = { idx, order };
  }
  const answered = picked !== null;

  function pick(i) {
    if (answered) return;
    setPicked(i);
    if (i === item.answer) setCorrect((n) => n + 1);
    setInspect(item.answer); // lead with the correct answer's why; the rest are a tap away
  }

  async function toggleBookmark() {
    const next = !item.bookmarked;
    const r = await api.study.bookmark(item.id, next);
    if (r.ok) setPool((prev) => prev.map((m) => (m.id === item.id ? { ...m, bookmarked: next } : m)));
  }

  const explanationFor = (i) =>
    i === item.answer
      ? item.why
      : (Array.isArray(item.explanations) && typeof item.explanations[i] === "string" && item.explanations[i].trim())
        ? item.explanations[i]
        : "Explanation coming soon — check back after this deck's explanations are generated.";

  return (
    <div className="review">
      <header className="review-top">
        <button className="link" onClick={onExit}>← Practice</button>
        <span className="review-progress">{idx + 1} / {pool.length}</span>
      </header>
      <div className="review-deck">Practice · {item.topic}{item.level != null ? ` · level ${item.level}` : ""}</div>

      <section className="flashcard mcq-card">
        <p className="card-front">{item.stem}</p>
        <ul className="mcq-choices">
          {orderRef.current.order.map((i) => {
            let cls = "mcq-choice";
            if (answered && i === item.answer) cls += " mcq-correct";
            if (answered && i === picked && i !== item.answer) cls += " mcq-wrong";
            if (answered && i === inspect) cls += " mcq-inspect";
            return (
              <li key={i}>
                <button className={cls} onClick={() => (answered ? setInspect(i) : pick(i))}>
                  {item.choices[i]}
                </button>
              </li>
            );
          })}
        </ul>
        {answered && inspect !== null && (
          <p className="mcq-why">
            <b>{inspect === item.answer ? "Why it's right: " : "Why it's wrong: "}</b>
            {explanationFor(inspect)}
          </p>
        )}
      </section>

      <button className={item.bookmarked ? "bookmark-btn on" : "bookmark-btn"} aria-pressed={!!item.bookmarked}
        onClick={toggleBookmark}>
        <BookmarkIcon on={!!item.bookmarked} /> {item.bookmarked ? "Bookmarked" : "Bookmark"}
      </button>

      {!answered
        ? <p className="muted mcq-hint">Tap the answer you think is right.</p>
        : <>
            <p className={picked === item.answer ? "mcq-verdict right" : "mcq-verdict"}>
              {picked === item.answer ? "Nice — that's right." : "Not quite."} Tap any choice to see why it's right or wrong.
            </p>
            <button className="btn btn-reveal" onClick={() => { setPicked(null); setInspect(null); setIdx((i) => i + 1); }}>
              {idx + 1 < pool.length ? "Next question" : "Finish"}
            </button>
          </>}
    </div>
  );
}

// NTS·4 — the Study Notes surface (the port of Alivia's NTS·3): list (by course, newest-first) →
// read. A COMPANION surface: reading notes writes zero srs and zero learning state, and nothing here
// grades, schedules, or counts. Content renders through the SAME escape-first markdown subset the
// card faces use (C·5) — the generator stores content verbatim, and this is the safety boundary.
/** The statuses a chapter job is still moving through — the tracker polls only while one holds. */
const JOB_LIVE = new Set(["queued", "planning", "running", "stalled"]);

// ── BIG·5b·2 — THE CHAPTER SCREEN: start, consent, and the tracker ────────────────────────────
// This is the slice the whole BIG chain was built for. A chapter is many model passes over many
// minutes, and the ONE failure that matters is a kid staring at a screen unable to tell working
// from broken. So: nothing long starts without a warning, progress is always visible and always
// named, and every ending says which ending it was.

/** How often the tracker asks. Fast enough to feel alive, slow enough to be nothing. */
const CHAPTER_POLL_MS = 1500;

/**
 * A rough, HONESTLY-HEDGED runtime for the consent gate. One quality pass per section plus the
 * coverage pass; real passes land well under their 120s ceiling, so this is a range and is labelled
 * as one. A wrong-but-stated estimate is what makes a long wait bearable; no estimate at all is
 * what makes it feel broken.
 */
function estimateMinutes(sections) {
  const n = Math.max(1, Number(sections) || 1);
  return { low: Math.max(1, Math.round((n * 20) / 60)), high: Math.max(2, Math.round((n * 55) / 60)) };
}

// BIG·5c·2 — PROVENANCE, said where the kid can act on it. A chapter-minted card carries
// `sourceRef { libraryId, pageStart, pageEnd }` (BIG·4: shape-only, stamped per segment by the
// runner); until now it was stored and synced and never shown. A kid staring at a card they doubt
// can now read "from ch4.pdf, pages 30–34" and go check the page. The filename resolves through
// the library listing the client already fetches; a source deleted to free quota (sourceRef
// deliberately survives that) reads "a deleted chapter" — the raw `lib_` id is never rendered.
// A card with no sourceRef — every hand-written card, every card older than BIG·4, i.e. most of
// them — renders NOTHING: no node, no placeholder. Text nodes only: a filename is stored data and
// must render inert even if it carries markup.
function SourceLine({ card, sources }) {
  const ref = card && card.sourceRef;
  if (!ref || typeof ref !== "object" || typeof ref.libraryId !== "string" || !ref.libraryId) return null;
  if (!Number.isInteger(ref.pageStart) || ref.pageStart < 1 || !Number.isInteger(ref.pageEnd) || ref.pageEnd < ref.pageStart) return null;
  const src = Array.isArray(sources) ? sources.find((s) => s && s.id === ref.libraryId) : null;
  const name = src && typeof src.filename === "string" && src.filename ? src.filename : "a deleted chapter";
  return <span className="card-source">from {name}, {pageRange(ref.pageStart, ref.pageEnd)}</span>;
}

/** "pages 12–14" / "page 12" — the same shape everywhere a range is shown. */
function pageRange(a, b) {
  if (!a) return "";
  return a === b ? `page ${a}` : `pages ${a}–${b}`;
}

/** The one-line answer to "what is it doing right now", from the record alone. */
function liveLine(job) {
  const segs = job.segments || [];
  const done = segs.filter((s) => s.status === "done" || s.status === "failed").length;
  const running = segs.find((s) => s.status === "running");
  const at = running ? segs.indexOf(running) + 1 : Math.min(done + 1, segs.length);
  return { at, of: segs.length, label: (running || segs[at - 1] || {}).label || "", done };
}

function ChapterJob({ source, job, decks, onGo, onAbort, onClose, busy }) {
  const segs = job.segments || [];
  const plan = job.plan || {};
  const report = job.report || null;
  const unreadable = (plan.unreadablePages || []).concat();
  const skipped = (report && report.skippedPages) || [];
  const cards = (job.cards || []).length;
  const [confirmStop, setConfirmStop] = useState(false);
  // BIG·6a-G — the deck choice at the gate. Defaults to her first deck, or to naming a new one when
  // she has none at all (a first chapter on a fresh account must still have somewhere to go).
  const [deckPick, setDeckPick] = useState("");
  const [deckName, setDeckName] = useState("");
  const [deckErr, setDeckErr] = useState("");
  // the deck the finished run actually landed in — read off the JOB, so it is what the server did,
  // not what the picker happened to be showing when the screen re-rendered
  const landedDeck = (decks || []).find((d) => d.id === job.deckId) || null;
  // CRV·1a — the DEFAULT is derived in render, not set by an effect. An effect lands one scheduler
  // task after the commit that paints the picker, so for that window the screen showed her first
  // deck selected while the tap handler still held "" and refused with "still loading". What she
  // sees and what a tap sends now come from the same render. "Still loading" is only ever said
  // while the deck list itself is still null.
  const pick = deckPick || (decks == null ? "" : decks.length > 0 ? decks[0].id : "__new");

  async function goWithDeck() {
    // NEVER release with an empty choice. Her decks load asynchronously, so a fast tap can land
    // before the picker has one — sending `{ id: "" }` would burn the gate on a refusal she did
    // nothing to earn. Waiting is the honest answer, and it is over in a moment.
    if (!pick) { setDeckErr("One moment — still loading your decks."); return; }
    const choice = pick === "__new" ? { name: deckName.trim() } : { id: pick };
    if (pick === "__new" && !choice.name) { setDeckErr("Name the new deck first."); return; }
    const err = await onGo(choice);
    if (err) setDeckErr(err); // the SERVER's refusal, said in its own words — never a guess here
  }

  // ── the consent gate: what the survey found, before anything long is spent ───────────────────
  if (job.awaitingConsent) {
    const est = estimateMinutes(plan.sections || segs.length);
    return (
      <section className="chap-panel">
        <h2 className="chap-head">Ready to make cards from {source ? source.filename : "this chapter"}</h2>
        <p className="chap-found">
          Found <strong>{plan.sections || segs.length} sections</strong>
          {plan.pages ? <> across {pageRange(plan.pages.start, plan.pages.end)}</> : null}.
          {" "}This usually takes about <strong>{est.low}–{est.high} minutes</strong>.
        </p>
        {unreadable.length > 0 && (
          <p className="chap-warn" role="status">
            {unreadable.length === 1 ? "One stretch" : `${unreadable.length} stretches`} couldn&apos;t be read
            {" "}({unreadable.map((u) => pageRange(u.pageStart, u.pageEnd)).join(", ")}) — those pages look scanned,
            with no text behind the picture. Everything else will still be used.
          </p>
        )}
        <ul className="chap-sections">
          {segs.map((s) => (
            <li key={s.id} className="chap-section">
              <span className="chap-section-label">{s.label}</span>
              {s.pageStart ? <span className="chap-section-pages">{pageRange(s.pageStart, s.pageEnd)}</span> : null}
            </li>
          ))}
        </ul>
        {/* BIG·6a-G — WHERE THE CARDS GO, chosen here. She is already reading this screen, so the
            choice costs no new step and sits right next to its consequence. The server validates it
            and refuses to release the job without one, BEFORE any generation spend. */}
        <div className="chap-deck">
          <label className="chap-deck-label" htmlFor="chap-deck-pick">Put these cards in</label>
          <select id="chap-deck-pick" value={pick} onChange={(e) => { setDeckPick(e.target.value); setDeckErr(""); }}>
            {(decks || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            <option value="__new">+ a new deck…</option>
          </select>
          {pick === "__new" && (
            <input className="chap-deck-name" type="text" placeholder="Name the new deck"
              value={deckName} onChange={(e) => { setDeckName(e.target.value); setDeckErr(""); }} />
          )}
          {deckErr && <p className="error" role="alert">{deckErr}</p>}
        </div>
        <div className="cardform-actions">
          <button className="btn" onClick={goWithDeck} disabled={busy}>{busy ? "Starting…" : "Start making cards"}</button>
          <button className="link" onClick={onAbort}>Not now</button>
        </div>
      </section>
    );
  }

  // ── the terminal states, each said in its own words ──────────────────────────────────────────
  if (job.status === "done" || job.status === "failed" || job.status === "aborted") {
    const ceiling = job.error && (job.error.code === "max_passes" || job.error.code === "max_wall_clock");
    return (
      <section className="chap-panel">
        <h2 className="chap-head">
          {job.status === "done"
            ? `${cards} card${cards === 1 ? "" : "s"} ready`
            : ceiling
              ? "Stopped early"
              : job.status === "aborted" ? "Stopped" : "Couldn't finish this chapter"}
        </h2>
        {/* BIG·6a-G — the copy is TRUE again, in the other direction: her cards really are in a deck
            now, and it says WHICH. BIG·6a-D's honest "not in your deck yet" would be the false one
            from here on. The deck NAME comes from her own deck list; if it is not loaded (or the
            deck was renamed elsewhere) the sentence still stands without inventing a name. */}
        {cards > 0 && (
          <p className="chap-note">
            {landedDeck
              ? <>They&apos;re in <strong>{landedDeck.name}</strong>, ready to study.</>
              : <>They&apos;re in your deck, ready to study.</>}
          </p>
        )}
        {ceiling && (
          <p className="chap-warn" role="status">
            This chapter ran longer than one job is allowed to. <strong>The {cards} card{cards === 1 ? "" : "s"} it
            already made are kept</strong> — start it again to pick up the rest.
          </p>
        )}
        {job.status === "aborted" && !ceiling && (
          <p className="chap-note">The {cards} card{cards === 1 ? "" : "s"} it made before you stopped it are kept.</p>
        )}
        {job.status === "failed" && job.error && <p className="error" role="alert">{job.error.message}</p>}
        <ChapterGaps skipped={skipped} unreadable={unreadable} />
        <div className="cardform-actions">
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </section>
    );
  }

  // ── stalled: BIG·1 derives this at read. Never dress it as progress. ─────────────────────────
  if (job.status === "stalled") {
    return (
      <section className="chap-panel">
        <h2 className="chap-head">This looks stuck</h2>
        <p className="chap-warn" role="alert">
          Nothing has finished for a while. It may still be working, but it may not be.
          {cards > 0 ? ` The ${cards} card${cards === 1 ? "" : "s"} it already made are safe either way.` : ""}
        </p>
        <StopButton confirmStop={confirmStop} setConfirmStop={setConfirmStop} onAbort={onAbort} cards={cards} />
      </section>
    );
  }

  // ── working ──────────────────────────────────────────────────────────────────────────────────
  const live = liveLine(job);
  const pct = live.of ? Math.round((live.done / live.of) * 100) : 0;
  return (
    <section className="chap-panel">
      <h2 className="chap-head">Reading {source ? source.filename : "your chapter"}…</h2>
      <p className="chap-progress" role="status">
        {job.status === "queued" || job.status === "planning"
          ? "Looking through the chapter to see what's in it…"
          : <>Section <strong>{live.at}</strong> of <strong>{live.of}</strong>
              {live.label ? <> — {live.label}</> : null}</>}
      </p>
      <div className="meter-track" role="img" aria-label={`${live.done} of ${live.of} sections finished`}>
        <span className="meter-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="chap-count">{cards} card{cards === 1 ? "" : "s"} so far</p>
      <ul className="chap-sections">
        {segs.map((s) => (
          <li key={s.id} className={`chap-section chap-${s.status}`}>
            <span className="chap-dot" aria-hidden="true">
              {s.status === "done" ? "✓" : s.status === "failed" ? "!" : s.status === "running" ? "•" : "·"}
            </span>
            <span className="chap-section-label">{s.label}</span>
            {s.pageStart ? <span className="chap-section-pages">{pageRange(s.pageStart, s.pageEnd)}</span> : null}
            <span className="chap-section-state">
              {s.status === "done" ? `${s.cardCount || 0} cards` : s.status === "failed" ? "skipped" : s.status === "running" ? "reading…" : "waiting"}
            </span>
          </li>
        ))}
      </ul>
      <StopButton confirmStop={confirmStop} setConfirmStop={setConfirmStop} onAbort={onAbort} cards={cards} />
    </section>
  );
}

/** Stopping must never feel like throwing the work away — because BIG·1 guarantees it isn't. */
function StopButton({ confirmStop, setConfirmStop, onAbort, cards }) {
  if (!confirmStop) return <button className="link chap-stop" onClick={() => setConfirmStop(true)}>Stop</button>;
  return (
    <span className="del-warn" role="alert">
      Stop making cards from this chapter?{" "}
      {cards > 0
        ? `The ${cards} card${cards === 1 ? "" : "s"} it already made are kept.`
        : "Nothing is lost — you can start it again."}{" "}
      <button className="link" onClick={onAbort}>Stop it</button>{" "}
      <button className="link" onClick={() => setConfirmStop(false)}>Keep going</button>
    </span>
  );
}

/** What a kid must be told or they will study a hole: pages that produced nothing, and why. */
function ChapterGaps({ skipped, unreadable }) {
  if (!skipped.length && !unreadable.length) return null;
  return (
    <div className="chap-gaps">
      {skipped.length > 0 && (
        <>
          <p className="chap-gaps-head">These pages didn&apos;t make any cards:</p>
          <ul className="chap-gap-list">
            {skipped.map((g, i) => (
              <li key={i}>
                <strong>{pageRange(g.pageStart, g.pageEnd) || "some pages"}</strong>
                {g.label ? ` (${g.label})` : ""} — {g.reason}
              </li>
            ))}
          </ul>
          <p className="chap-gaps-note">Worth a read on your own — nothing here is covered by a card.</p>
        </>
      )}
      {unreadable.length > 0 && (
        <p className="chap-gaps-note">
          {unreadable.map((u) => pageRange(u.pageStart, u.pageEnd)).join(", ")} couldn&apos;t be read at all —
          they look scanned, with no text behind the picture.
        </p>
      )}
    </div>
  );
}

/**
 * BIG·5b·2 — the live chapter job, as a view of SERVER state.
 *
 * RESUMABILITY IS THE POINT. The job lives server-side; this hook holds no truth of its own. On
 * mount it asks "what is running?" with no id at all (BIG·5b·2's id-less status read), so a closed
 * tab, a refresh, or a second device reattaches to the work in flight instead of showing nothing —
 * or, worse, tempting a second start.
 */
function useChapterJob() {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  // adopt whatever is already running, before anything else happens
  useEffect(() => {
    let alive = true;
    api.jobs.active().then((j) => { if (alive) setJob(j); });
    return () => { alive = false; };
  }, []);

  // poll while it is live — and stop the moment it is not. A terminal record never changes again,
  // so continuing to ask would be pure noise.
  useEffect(() => {
    if (!job || !JOB_LIVE.has(job.status)) return undefined;
    let alive = true;
    const t = setInterval(async () => {
      const next = await api.jobs.status(job.id);
      if (alive && next) setJob(next);
    }, CHAPTER_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [job && job.id, job && job.status]);

  async function start(libraryId) {
    setBusy(true);
    try {
      const r = await api.jobs.startChapter(libraryId);
      // 200 = BIG·5a's serialization answering: a job is already in flight and THIS is it. Adopting
      // it is the correct outcome, not an error — the kid sees the same tracker, never a second one.
      if (r.ok && r.data && r.data.job) { setJob(r.data.job); return { ok: true, adopted: r.data.created === false }; }
      return { ok: false, error: (r.data && r.data.error) || "That chapter couldn't be started. Try again." };
    } finally { setBusy(false); }
  }

  // BIG·6a-G — `deck` is the choice from the gate. A refusal comes back as TEXT for the gate to
  // render: the server owns "does this deck exist / is this name usable", so the screen repeats its
  // answer rather than inventing a second set of rules that could disagree with it.
  async function go(deck) {
    if (!job) return "";
    setBusy(true);
    try {
      const r = await api.jobs.runChapter(job.id, deck);
      if (r.ok && r.data && r.data.job) { setJob(r.data.job); return ""; }
      const said = (r.data && r.data.error) || "That couldn't start. Try again.";
      setJob(await api.jobs.status(job.id)); // it is still at the gate — re-read, never assume
      return said;
    } finally { setBusy(false); }
  }

  async function stop() {
    if (!job) return;
    const r = await api.jobs.abort(job.id);
    setJob((r.ok && r.data && r.data.job) || (await api.jobs.status(job.id)));
  }

  return { job, busy, start, go, stop, clear: () => setJob(null) };
}

// ── BIG·5b·1 — THE CHAPTER LIBRARY ────────────────────────────────────────────────────────────
// The first slice of this chain a kid can actually see. It manages SOURCES: upload a chapter, see
// what is stored, delete what is finished with. There is deliberately NO "make cards" button here —
// that lands in BIG·5b·2 alongside the tracker, because a start button with no progress surface is
// exactly the "is it hung?" problem this whole chain exists to remove.
//
// THE COPY DOES ONE JOB ABOVE ALL: make it obvious which path a PDF is on. A capture is one request
// over one page's worth of material and yields a handful of cards; a chapter is stored and read
// section by section and yields many. A kid who cannot tell those apart will one day wonder why one
// PDF made twenty cards and another made two hundred, and conclude the app is broken.
const LIBRARY_BLURB =
  "Whole chapters live here — a textbook chapter, a packet, a lecture PDF. Capture is for one page or one set of notes at a time; a chapter gets read section by section, so it makes many more cards.";

/**
 * The sentence a kid actually reads when an upload is refused. The SERVER owns the wording for every
 * case it can answer — oversize names the limit, a full library names what to delete — so this
 * renders `data.error` verbatim rather than inventing a second, different answer to one question.
 * The only copy written here is for the cases the server never got to answer at all: a body the
 * proxy killed before Node saw it (a 413 with no JSON), and no network.
 */
function uploadRefusal(r) {
  if (r && r.data && typeof r.data.error === "string" && r.data.error) return r.data.error;
  if (r && r.status === 413) {
    return "That file was too big to send — it was stopped before it finished uploading. Split the chapter, or export fewer pages.";
  }
  return "That upload didn't go through. Check your connection and try again.";
}

function LibraryView({ onBack }) {
  const [lib, setLib] = useState(null);          // { sources, usage } | null while loading
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");        // an upload refusal
  const [saved, setSaved] = useState("");        // the "it landed" line
  const [confirmDel, setConfirmDel] = useState(null);
  const [blocked, setBlocked] = useState(null);  // the 409 source_busy sentence, rendered in place
  // BIG·5b·2 — the live chapter job. It is adopted from the SERVER on mount, so a refresh reattaches
  // to work in flight rather than showing nothing (or tempting a second start).
  const chapter = useChapterJob();
  // BIG·6a-G — her decks, for the consent gate's "put these cards in" choice. Loaded with the
  // library so the gate never has to wait on a fetch at the moment she is deciding.
  const [decks, setDecks] = useState(null);
  useEffect(() => { api.study.overview().then((ov) => setDecks(ov.decks || [])).catch(() => setDecks([])); }, []);

  function refresh() { return api.library.list().then(setLib); }
  useEffect(() => { refresh(); }, []);
  // when a run ends, the library's own numbers may have moved (a re-upload, a delete elsewhere)
  const jobStatus = chapter.job && chapter.job.status;
  useEffect(() => { if (jobStatus && !JOB_LIVE.has(jobStatus)) refresh(); }, [jobStatus]);

  async function makeCards(id) {
    setError(""); setSaved(""); setBlocked(null);
    const r = await chapter.start(id);
    if (!r.ok) setError(r.error);
  }

  async function onPick(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ""; // let the same file be re-picked after a refusal
    if (!file) return;
    setBusy(true); setError(""); setSaved(""); setBlocked(null);
    try {
      const { data } = await readPdf(file);
      const r = await api.library.upload({ mediaType: "application/pdf", data }, file.name);
      if (!r.ok) { setError(uploadRefusal(r)); return; }
      const source = r.data && r.data.source;
      setSaved(r.data && r.data.created === false
        ? `${source ? source.filename : file.name} is already in your library.`
        : `${source ? source.filename : file.name} is in your library.`);
      await refresh();
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally {
      setBusy(false);
    }
  }

  async function del(id) {
    setError(""); setSaved(""); setBlocked(null);
    const r = await api.library.remove(id);
    setConfirmDel(null);
    if (!r.ok) {
      // 409 source_busy is NOT a generic failure: the server wrote an actionable sentence naming the
      // file and what to do about it, and that sentence is the whole point of the refusal.
      setBlocked(r.data && r.data.error ? r.data.error : "That chapter couldn't be deleted. Try again in a moment.");
      return;
    }
    refresh();
  }

  const usage = lib && lib.usage;
  const pct = usage && usage.quotaBytes > 0
    ? Math.min(100, Math.round((usage.usedBytes / usage.quotaBytes) * 100))
    : 0;

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Home</button>
      </header>
      <h1 className="study-head">Chapter Library</h1>
      <p className="lib-blurb">{LIBRARY_BLURB}</p>

      <div className="lib-add">
        <label className="btn lib-pick">
          {busy ? "Uploading…" : "Add a chapter"}
          <input type="file" accept="application/pdf" aria-label="Add a chapter PDF"
            disabled={busy} onChange={onPick} style={{ display: "none" }} />
        </label>
        <span className="muted">PDF · up to 40 MB</span>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="lib-saved" role="status">{saved}</p>}

      {usage && (
        <section className="lib-usage">
          <p className="lib-usage-line">
            <strong>{fmtBytes(usage.usedBytes)}</strong> of {fmtBytes(usage.quotaBytes)} used
            {" · "}{usage.count} chapter{usage.count === 1 ? "" : "s"}
          </p>
          <div className="meter-track" role="img"
            aria-label={`${fmtBytes(usage.usedBytes)} of ${fmtBytes(usage.quotaBytes)} used`}>
            <span className="meter-fill" style={{ width: `${pct}%` }} />
          </div>
        </section>
      )}

      {blocked && <p className="lib-busy" role="alert">{blocked}</p>}

      {chapter.job && (
        <ChapterJob
          job={chapter.job}
          busy={chapter.busy}
          source={lib && lib.sources.find((s) => s.id === chapter.job.libraryId)}
          decks={decks}
          onGo={chapter.go}
          onAbort={chapter.stop}
          onClose={chapter.clear}
        />
      )}

      {lib === null
        ? <p className="muted">Loading…</p>
        : lib.sources.length === 0
          ? <p className="muted empty">No chapters yet. Add one above — it stays here until you turn it into cards.</p>
          : <ul className="note-list lib-list">
              {lib.sources.map((s) => (
                <li key={s.id} className="note-row lib-row">
                  <span className="lib-name">📄 {s.filename}</span>
                  <span className="note-meta">
                    {fmtBytes(s.bytes)} · added {String(s.uploadedAt).slice(0, 10)}
                  </span>
                  {confirmDel === s.id
                    ? <span className="del-warn" role="alert">
                        Delete {s.filename}? Cards you already made from it are kept.{" "}
                        <button className="link" onClick={() => del(s.id)}>Delete</button>{" "}
                        <button className="link" onClick={() => setConfirmDel(null)}>Keep</button>
                      </span>
                    : <>
                        {/* BIG·5b·2 — one job at a time (BIG·1 serializes), so while one is live the
                            others do not offer a start that would only ever adopt it. */}
                        {!chapter.job && (
                          <button className="link lib-make" disabled={chapter.busy}
                            onClick={() => makeCards(s.id)}>Make cards</button>
                        )}
                        <button className="link" onClick={() => { setBlocked(null); setConfirmDel(s.id); }}>Delete</button>
                      </>}
                </li>
              ))}
            </ul>}
    </div>
  );
}

function NotesView({ onBack }) {
  const [notes, setNotes] = useState(null);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(null);      // the note being read
  const [confirmDel, setConfirmDel] = useState(null);

  function refresh() { return api.notes.list().then(setNotes); }
  useEffect(() => {
    refresh();
    api.hub.courses().then((c) => setCourses(Array.isArray(c) ? c : [])).catch(() => {});
  }, []);

  const courseName = Object.fromEntries(courses.map((c) => [c.id, c.name]));

  async function del(id) {
    await api.notes.remove(id);
    setConfirmDel(null);
    if (open?.id === id) setOpen(null);
    refresh();
  }

  if (open) {
    return (
      <div className="study">
        <header className="study-top">
          <button className="link" onClick={() => setOpen(null)}>← Notes</button>
        </header>
        <h1 className="study-head">{open.title}</h1>
        <p className="notes-tagline">{NOTES_TAGLINE}</p>
        {courseName[open.courseId] && <p className="muted">{courseName[open.courseId]}</p>}
        <article className="note-read">
          {open.sections.map((s, i) => (
            <section key={i} className="note-section">
              <h2>{s.heading}</h2>
              <div className="note-content" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(s.content) }} />
              {s.keyTerms?.length > 0 && (
                <p className="note-terms">{s.keyTerms.map((t) => <span key={t} className="note-term">{t}</span>)}</p>
              )}
            </section>
          ))}
        </article>
      </div>
    );
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Home</button>
      </header>
      <h1 className="study-head">Study Notes</h1>
      <p className="notes-tagline">{NOTES_TAGLINE}</p>
      {notes === null
        ? <p className="muted">Loading…</p>
        : notes.length === 0
          ? <p className="muted empty">No notes yet. On a capture, choose “Study notes” or “Both”.</p>
          : <ul className="note-list">
              {notes.map((n) => (
                <li key={n.id} className="note-row">
                  <button className="link note-open" onClick={() => setOpen(n)}>{n.title}</button>
                  <span className="note-meta">
                    {courseName[n.courseId] ? `${courseName[n.courseId]} · ` : ""}
                    {n.sections.length} section{n.sections.length === 1 ? "" : "s"}
                  </span>
                  {confirmDel === n.id
                    ? <span className="del-warn" role="alert">
                        Delete these notes? This can&apos;t be undone.{" "}
                        <button className="link" onClick={() => del(n.id)}>Delete</button>{" "}
                        <button className="link" onClick={() => setConfirmDel(null)}>Keep</button>
                      </span>
                    : <button className="link" onClick={() => setConfirmDel(n.id)}>Delete</button>}
                </li>
              ))}
            </ul>}
    </div>
  );
}

// Quiz & Exam Prep: the topics she's missing (ease-derived) -> a targeted practice session; plus
// (P·2) the MCQ practice bank, filterable by topic and difficulty. Retention raises ease, so a
// mastered topic drops off the weak-spots list on its own next load.
function QuizView({ onBack }) {
  const [topics, setTopics] = useState(null);
  const [practice, setPractice] = useState(null); // a { tag, weakCards } being drilled
  const [bank, setBank] = useState(null);         // the MCQ practice bank (grown from her cards)
  const [pTopic, setPTopic] = useState("mixed");
  const [pLevel, setPLevel] = useState("all");
  const [session, setSession] = useState(null);   // the shuffled items of an active MCQ round

  function refresh() { return api.study.weakTopics().then(setTopics); }
  useEffect(() => { refresh(); api.study.mcq().then(setBank); }, []);

  if (practice) return <ReviewSession topic={practice} onExit={() => { setPractice(null); refresh(); }} />;
  if (session) return <McqSession items={session} onExit={() => { setSession(null); api.study.mcq().then(setBank); }} />;

  // Her MCQ topics + levels come from her OWN bank (no fixed CLEP taxonomy like Alivia's) — the
  // filter options are exactly what her material produced.
  const bankTopics = bank === null ? [] : [...new Set(bank.map((m) => m.topic).filter(Boolean))].sort();
  const bankLevels = bank === null ? [] : [...new Set(bank.map((m) => m.level).filter((v) => v != null))].sort((a, b) => a - b);
  const filtered = bank === null ? [] : bank.filter((m) =>
    (pTopic === "mixed" || m.topic === pTopic) && (pLevel === "all" || m.level === Number(pLevel)));
  function startPractice() {
    const shuffled = [...filtered];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setSession(shuffled);
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <div className="study-head">
        <h1>Quiz &amp; Exam Prep</h1>
      </div>
      {topics === null
        ? <p className="muted">Loading…</p>
        : topics.length === 0
          ? <p className="muted empty">No weak spots right now — keep reviewing and they'll show up here when they appear.</p>
          : <>
              <p className="muted">Topics you've been missing. Practice pulls those cards until they stick.</p>
              <ul className="deck-list">
                {topics.map((t) => (
                  <li key={t.tag} className="deck-row">
                    <div className="deck-meta">
                      <span className="deck-name">{t.tag}</span>
                      <span className="deck-counts">
                        <span className="deck-due">{t.weakCards} weak card{t.weakCards === 1 ? "" : "s"}</span>
                      </span>
                    </div>
                    <div className="deck-actions">
                      <button className="btn btn-sm" onClick={() => setPractice(t)}>Practice</button>
                    </div>
                  </li>
                ))}
              </ul>
            </>}

      {bank !== null && (
        <section className="practice">
          <div className="mastery-head"><span className="star" aria-hidden="true">★</span><h2>Practice questions</h2></div>
          {bank.length === 0
            ? <p className="muted empty">Practice questions grow from your decks — none yet.</p>
            : <>
                <p className="muted">Multiple choice from your own decks. After you answer, tap through the wrong choices to see why they're wrong.</p>
                <div className="practice-controls">
                  <label className="field"><span>Topic</span>
                    <select value={pTopic} onChange={(e) => setPTopic(e.target.value)}>
                      <option value="mixed">Mixed (all topics)</option>
                      {bankTopics.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></label>
                  {bankLevels.length > 0 &&
                    <label className="field"><span>Difficulty</span>
                      <select value={pLevel} onChange={(e) => setPLevel(e.target.value)}>
                        <option value="all">All levels</option>
                        {bankLevels.map((l) => <option key={l} value={l}>Level {l}</option>)}
                      </select></label>}
                  <button className="btn btn-sm" disabled={filtered.length === 0} onClick={startPractice}>
                    Start · {filtered.length} question{filtered.length === 1 ? "" : "s"}
                  </button>
                </div>
              </>}
        </section>
      )}
    </div>
  );
}

// --- H·1: the course & assignment hub — every class and deadline in one place. ---------------
// One active term shows at a time (default: the term of the most recently created course; a
// switcher appears once a second term exists — rollover is just "add next term's courses").
const COURSE_COLORS = ["#1f4aa8", "#c8202f", "#2e7d32", "#7b1fa2", "#e65100", "#00695c"];

function CourseDot({ color }) {
  return <span className="course-dot" style={{ background: color || "var(--muted)" }} aria-hidden="true" />;
}

function AssignmentRow({ asn, course, showCourse, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(asn.title);
  const [due, setDue] = useState(asn.due);
  const overdue = !asn.done && asn.due < localDateStr();

  async function save() {
    if (!title.trim() || !due) return;
    await api.hub.updateAssignment(asn.id, { title: title.trim(), due });
    setEditing(false); onRefresh();
  }
  async function toggleDone() { await api.hub.updateAssignment(asn.id, { done: !asn.done }); onRefresh(); }
  async function remove() { await api.hub.deleteAssignment(asn.id); onRefresh(); }

  if (editing) {
    return (
      <li className="asn-row asn-edit">
        <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due" />
        <button className="btn btn-sm" onClick={save} disabled={!title.trim() || !due}>Save</button>
        <button className="link" onClick={() => { setEditing(false); setTitle(asn.title); setDue(asn.due); }}>Cancel</button>
      </li>
    );
  }
  return (
    <li className={asn.done ? "asn-row is-done" : "asn-row"}>
      <label className="asn-check">
        <input type="checkbox" checked={!!asn.done} onChange={toggleDone} aria-label={`Done: ${asn.title}`} />
      </label>
      <span className="asn-title">{asn.title}</span>
      {showCourse && course && <span className="asn-course"><CourseDot color={course.color} />{course.name}</span>}
      <span className={overdue ? "asn-due overdue" : "asn-due"}>{overdue ? `overdue · ${asn.due}` : asn.due}</span>
      <span className="asn-actions">
        <button className="link" onClick={() => setEditing(true)}>Edit</button>
        <button className="link" onClick={remove}>Delete</button>
      </span>
    </li>
  );
}

// E·4 — a hand-managed exam under its course (title, date, type). Mirrors AssignmentRow; no "done"
// (an exam is an event, not a task). Uses the Y·2 exm_ CRUD routes.
function ExamRow({ exam, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(exam.title);
  const [date, setDate] = useState(exam.date);
  const [type, setType] = useState(exam.type ?? "");

  async function save() {
    if (!title.trim() || !date) return;
    await api.exams.update(exam.id, { title: title.trim(), date, type: type.trim() || null });
    setEditing(false); onRefresh();
  }
  async function remove() { await api.exams.remove(exam.id); onRefresh(); }

  if (editing) {
    return (
      <li className="asn-row asn-edit">
        <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Exam title" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Exam date" />
        <input value={type} placeholder="type" className="syl-type" onChange={(e) => setType(e.target.value)} aria-label="Exam type" />
        <button className="btn btn-sm" onClick={save} disabled={!title.trim() || !date}>Save</button>
        <button className="link" onClick={() => { setEditing(false); setTitle(exam.title); setDate(exam.date); setType(exam.type ?? ""); }}>Cancel</button>
      </li>
    );
  }
  return (
    <li className="asn-row exam-row">
      <span className="asn-title">📝 {exam.title}{exam.type ? <span className="exam-type"> · {exam.type}</span> : null}</span>
      <span className="asn-due">{exam.date}</span>
      <span className="asn-actions">
        <button className="link" onClick={() => setEditing(true)}>Edit</button>
        <button className="link" onClick={remove}>Delete</button>
      </span>
    </li>
  );
}

function CourseCard({ course, assignments, exams = [], onRefresh, archived = false, onPractice }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(course.name);
  const [term, setTerm] = useState(course.term);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [xTitle, setXTitle] = useState(""); // E·4 new-exam form
  const [xDate, setXDate] = useState("");
  const [xType, setXType] = useState("");
  const [confirmDel, setConfirmDel] = useState(0); // item count from the 409 delete-guard
  const [confirmArch, setConfirmArch] = useState(false); // ARC·1 — the one confirm step before archiving

  const open_ = assignments.filter((a) => !a.done).length;

  async function addAsn() {
    if (!title.trim() || !due) return;
    await api.hub.createAssignment(course.id, title.trim(), due);
    setTitle(""); setDue(""); onRefresh();
  }
  async function addExam() {
    if (!xTitle.trim() || !xDate) return;
    await api.exams.create(course.id, xTitle.trim(), xDate, xType.trim() || undefined);
    setXTitle(""); setXDate(""); setXType(""); onRefresh();
  }
  async function saveCourse() {
    if (!name.trim() || !term.trim()) return;
    await api.hub.updateCourse(course.id, { name: name.trim(), term: term.trim() });
    setEditing(false); onRefresh();
  }
  // the delete-guard: blocked (409 + counts) unless she confirms the cascade. The 409 reports BOTH
  // assignments and exams (Y·2) — a course can now hold exams too, so count both.
  async function remove() {
    const r = await api.hub.deleteCourse(course.id, false);
    if (r.status === 409) { setConfirmDel((r.data?.assignments ?? 0) + (r.data?.exams ?? 0)); return; }
    onRefresh();
  }
  async function removeCascade() { await api.hub.deleteCourse(course.id, true); onRefresh(); }
  // ARC·1 — archive hides, never deletes; unarchive brings it straight back (so no undo prompt).
  async function archive() { await api.hub.archiveCourse(course.id); setConfirmArch(false); onRefresh(); }
  async function unarchive() { await api.hub.unarchiveCourse(course.id); onRefresh(); }

  return (
    <li className="course-card">
      <div className="course-head">
        {editing
          ? <div className="newdeck">
              <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Course name" />
              <input value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Term" />
              <button className="btn btn-sm" onClick={saveCourse} disabled={!name.trim() || !term.trim()}>Save</button>
              <button className="link" onClick={() => { setEditing(false); setName(course.name); setTerm(course.term); }}>Cancel</button>
            </div>
          : <>
              <button type="button" className="course-name" onClick={() => setOpen((o) => !o)}>
                <CourseDot color={course.color} />
                <span>{course.name}</span>
                {archived && <span className="archived-badge">Archived</span>}
                <span className="deck-counts">{open_ > 0 ? `${open_} open` : "all done"}</span>
              </button>
              <span className="deck-actions">
                {/* H·2: the manual GPA entry — one letter per course, updated any time */}
                <select className="grade-pick" value={course.grade ?? ""} aria-label={`Grade: ${course.name}`}
                  onChange={(e) => api.hub.updateCourse(course.id, { grade: e.target.value || null }).then(onRefresh)}>
                  <option value="">no grade</option>
                  {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                {!archived && <button className="link" onClick={() => setEditing(true)}>Edit</button>}
                {archived
                  ? <>
                      {onPractice && <button className="link" onClick={onPractice}>Study now</button>}
                      <button className="link" onClick={unarchive}>Unarchive</button>
                    </>
                  : <button className="link" onClick={() => setConfirmArch(true)}>Archive</button>}
                <button className="link" onClick={remove}>Delete</button>
              </span>
            </>}
      </div>
      {confirmDel > 0 && (
        <p className="del-warn" role="alert">
          This course still has {confirmDel} item{confirmDel === 1 ? "" : "s"} (assignments &amp; exams).{" "}
          <button className="link" onClick={removeCascade}>Delete course and everything in it</button>{" "}
          <button className="link" onClick={() => setConfirmDel(0)}>Keep it</button>
        </p>
      )}
      {confirmArch && (
        <p className="del-warn" role="alert">
          Archive {course.name}? It leaves your course list, your daily plan and your review queue. Nothing is deleted, and Unarchive brings it back.{" "}
          <button className="link" onClick={archive}>Archive it</button>{" "}
          <button className="link" onClick={() => setConfirmArch(false)}>Keep it</button>
        </p>
      )}
      {open && (
        <div className="course-body">
          {assignments.length === 0
            ? <p className="muted">No assignments yet.</p>
            : <ul className="asn-list">
                {[...assignments].sort(byDue).map((a) => (
                  <AssignmentRow key={a.id} asn={a} onRefresh={onRefresh} />
                ))}
              </ul>}
          <div className="asn-add">
            <input value={title} placeholder="New assignment" onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addAsn(); }} aria-label="New assignment title" />
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="New assignment due" />
            <button className="btn btn-sm" onClick={addAsn} disabled={!title.trim() || !due}>Add</button>
          </div>

          <h3 className="course-subhead">Exams</h3>
          {exams.length === 0
            ? <p className="muted">No exams yet.</p>
            : <ul className="asn-list">
                {[...exams].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)).map((x) => (
                  <ExamRow key={x.id} exam={x} onRefresh={onRefresh} />
                ))}
              </ul>}
          <div className="asn-add">
            <input value={xTitle} placeholder="New exam" onChange={(e) => setXTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addExam(); }} aria-label="New exam title" />
            <input type="date" value={xDate} onChange={(e) => setXDate(e.target.value)} aria-label="New exam date" />
            <input value={xType} placeholder="type (optional)" className="syl-type" onChange={(e) => setXType(e.target.value)} aria-label="New exam type" />
            <button className="btn btn-sm" onClick={addExam} disabled={!xTitle.trim() || !xDate}>Add exam</button>
          </div>
        </div>
      )}
    </li>
  );
}

// --- A·3: the Hours Log (ported from Alivia's A·1, restyled to her Patriots tokens) -----------
// Her 4-year arc points at veterinary programs, which ask for documented animal + clinical
// hours — the quiet line under the title says why every entry matters. Timer first, manual add
// as the honest fallback; categories/places are free text seeded from her own history.
const STARTER_CATEGORIES = ["Shadowing", "Clinical", "Research", "Volunteer", "Work"];

/** date+time local inputs -> full ISO instant (the server stores/derives the rest). */
const localToIso = (date, time) => new Date(`${date}T${time}`).toISOString();
const entryDate = (iso) => { const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const entryTime = (iso) => { const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getHours())}:${p(d.getMinutes())}`; };

function HoursDatalists({ categories, places }) {
  return (
    <>
      <datalist id="hours-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="hours-places">{places.map((p) => <option key={p} value={p} />)}</datalist>
    </>
  );
}

function TimerCard({ active, onChanged }) {
  const [cat, setCat] = useState("");
  const [place, setPlace] = useState("");
  const [act, setAct] = useState("");
  const [err, setErr] = useState("");
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000); // re-render; the readout comes from the clock seam
    return () => clearInterval(t);
  }, [active]);

  async function start() {
    setErr("");
    try {
      const r = await api.hours.start(cat.trim(), place.trim(), act.trim());
      if (!r.ok) { setErr(r.data?.error ?? "couldn't start the timer — try again"); return; }
      setCat(""); setPlace(""); setAct("");
      onChanged();
    } catch { setErr("couldn't reach the server — nothing changed"); }
  }
  async function stop() {
    try {
      const r = await api.hours.stop();
      if (r.ok) onChanged();
    } catch { /* the running card stays; she can retry */ }
  }

  if (active) {
    return (
      <section className="timer-card is-running">
        <div className="timer-live">
          <span className="timer-elapsed">{fmtElapsed(nowMs() - Date.parse(active.startedAt))}</span>
          <span className="timer-what">{active.activity || active.category} · {active.place}</span>
        </div>
        <button className="btn" onClick={stop}>Stop</button>
      </section>
    );
  }
  return (
    <section className="timer-card">
      <div className="timer-form">
        <input list="hours-cats" value={cat} placeholder="Category (Shadowing…)" aria-label="Category"
          onChange={(e) => setCat(e.target.value)} />
        <input list="hours-places" value={place} placeholder="Place" aria-label="Place"
          onChange={(e) => setPlace(e.target.value)} />
        <input value={act} placeholder="Activity (optional)" aria-label="Activity"
          onChange={(e) => setAct(e.target.value)} />
        <button className="btn" onClick={start} disabled={!cat.trim() || !place.trim()}>Start</button>
      </div>
      {err && <p className="deck-err">{err}</p>}
    </section>
  );
}

function ManualEntryForm({ onDone, onCancel }) {
  const [cat, setCat] = useState("");
  const [place, setPlace] = useState("");
  const [act, setAct] = useState("");
  const [date, setDate] = useState(localDateStr());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const ready = cat.trim() && place.trim() && date && from && to;

  async function save() {
    setErr("");
    try {
      const r = await api.hours.add({
        category: cat.trim(), place: place.trim(), activity: act.trim(),
        startedAt: localToIso(date, from), endedAt: localToIso(date, to),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (!r.ok) { setErr(r.data?.error ?? "couldn't save that entry"); return; }
      onDone();
    } catch { setErr("couldn't reach the server — nothing changed"); }
  }
  return (
    <div className="newdeck hours-form">
      <input list="hours-cats" value={cat} placeholder="Category" aria-label="Category" autoFocus onChange={(e) => setCat(e.target.value)} />
      <input list="hours-places" value={place} placeholder="Place" aria-label="Place" onChange={(e) => setPlace(e.target.value)} />
      <input value={act} placeholder="Activity (optional)" aria-label="Activity" onChange={(e) => setAct(e.target.value)} />
      <input type="date" value={date} aria-label="Date" onChange={(e) => setDate(e.target.value)} />
      <input type="time" value={from} aria-label="Start time" onChange={(e) => setFrom(e.target.value)} />
      <input type="time" value={to} aria-label="End time" onChange={(e) => setTo(e.target.value)} />
      <input value={note} placeholder="Note (optional)" aria-label="Note" onChange={(e) => setNote(e.target.value)} />
      <button className="btn btn-sm" onClick={save} disabled={!ready}>Save entry</button>
      <button className="link" onClick={onCancel}>Cancel</button>
      {err && <p className="deck-err">{err}</p>}
    </div>
  );
}

function HoursEntryRow({ e, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cat, setCat] = useState(e.category);
  const [place, setPlace] = useState(e.place);
  const [act, setAct] = useState(e.activity ?? "");
  const [date, setDate] = useState(entryDate(e.startedAt));
  const [from, setFrom] = useState(entryTime(e.startedAt));
  const [to, setTo] = useState(entryTime(e.endedAt));
  const [note, setNote] = useState(e.note ?? "");
  const [err, setErr] = useState("");

  async function save() {
    setErr("");
    const r = await api.hours.update(e.id, {
      category: cat.trim(), place: place.trim(), activity: act.trim(),
      startedAt: localToIso(date, from), endedAt: localToIso(date, to),
      note: note.trim() ? note.trim() : null,
    });
    if (!r.ok) { setErr(r.data?.error ?? "couldn't save the change"); return; }
    setEditing(false);
    onRefresh();
  }
  async function del() {
    const r = await api.hours.remove(e.id);
    if (r.ok) onRefresh();
  }

  if (editing) {
    return (
      <li className="asn-row hours-row">
        <div className="newdeck hours-form">
          <input list="hours-cats" value={cat} aria-label="Category" onChange={(ev) => setCat(ev.target.value)} />
          <input list="hours-places" value={place} aria-label="Place" onChange={(ev) => setPlace(ev.target.value)} />
          <input value={act} placeholder="Activity" aria-label="Activity" onChange={(ev) => setAct(ev.target.value)} />
          <input type="date" value={date} aria-label="Date" onChange={(ev) => setDate(ev.target.value)} />
          <input type="time" value={from} aria-label="Start time" onChange={(ev) => setFrom(ev.target.value)} />
          <input type="time" value={to} aria-label="End time" onChange={(ev) => setTo(ev.target.value)} />
          <input value={note} placeholder="Note" aria-label="Note" onChange={(ev) => setNote(ev.target.value)} />
          <button className="btn btn-sm" onClick={save} disabled={!cat.trim() || !place.trim()}>Save</button>
          <button className="link" onClick={() => setEditing(false)}>Cancel</button>
          {err && <p className="deck-err">{err}</p>}
        </div>
      </li>
    );
  }
  return (
    <li className="asn-row hours-row">
      <span className="hours-when">{entryDate(e.startedAt)}</span>
      <span className="hours-dur">{fmtMins(e.durationMin)}</span>
      <span className="asn-title">{e.category} · {e.place}{e.activity ? ` — ${e.activity}` : ""}</span>
      {e.origin === "manual" && <span className="hours-origin" title="Entered by hand (not timed live)">manual</span>}
      {e.note && <span className="hours-note">{e.note}</span>}
      <span className="asn-actions">
        <button className="link" onClick={() => setEditing(true)}>Edit</button>
        <button className="link" onClick={() => setConfirming(true)}>Delete</button>
      </span>
      {confirming && (
        <p className="del-warn">
          Delete this {fmtMins(e.durationMin)} {e.category} entry? This can&apos;t be undone.{" "}
          <button className="link" onClick={del}>Delete it</button>{" "}
          <button className="link" onClick={() => setConfirming(false)}>Keep it</button>
        </p>
      )}
    </li>
  );
}

function HoursView({ onBack }) {
  const [entries, setEntries] = useState(null);
  const [active, setActive] = useState(null);
  const [adding, setAdding] = useState(false);

  function refresh() {
    return Promise.all([api.hours.list().then(setEntries), api.hours.active().then(setActive)]);
  }
  useEffect(() => { refresh(); }, []);

  if (entries === null) return <div className="study"><p className="muted">Loading…</p></div>;

  const categories = [...new Set([...entries.map((e) => e.category), ...STARTER_CATEGORIES])];
  const places = [...new Set(entries.map((e) => e.place))];
  const totals = {};
  for (const e of entries) totals[e.category] = (totals[e.category] || 0) + e.durationMin;

  function exportCsv() {
    const blob = new Blob([hoursCsv(entries)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `estelle-hours-${localDateStr()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="study hours">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
        {entries.length > 0 && <button className="link" onClick={exportCsv}>Export CSV</button>}
      </header>
      <div className="study-head">
        <h1>Hours Log</h1>
        {!adding && <button className="btn btn-sm" onClick={() => setAdding(true)}>+ Add entry</button>}
      </div>
      <p className="hours-why">Vet programs ask for documented animal and clinical hours — every entry here is future application material.</p>
      <HoursDatalists categories={categories} places={places} />
      <TimerCard active={active} onChanged={refresh} />
      {adding && <ManualEntryForm onDone={() => { setAdding(false); refresh(); }} onCancel={() => setAdding(false)} />}
      {Object.keys(totals).length > 0 && (
        <section className="hours-totals">
          <h2>Totals</h2>
          <ul className="totals-list">
            {Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([c, m]) => (
              <li key={c}><span>{c}</span><b>{fmtMins(m)}</b></li>
            ))}
          </ul>
        </section>
      )}
      {entries.length === 0
        ? <p className="muted empty">No hours yet — start the timer when you walk in, or add an entry.</p>
        : <ul className="asn-list hours-list">
            {entries.map((e) => <HoursEntryRow key={e.id} e={e} onRefresh={refresh} />)}
          </ul>}
    </div>
  );
}

// Y·4 — the syllabus intake screen, ported from Alivia's Y·3 (her Maverick red → Estelle's Patriots
// blue via the shared syl-* classes + her own tokens). Drop a syllabus PDF, read exactly what was
// extracted, correct anything, add what was missed, save the term into the hub. Reuses the capture
// seam (readPdf + its honest size/type copy). Preview-then-save is absolute; weights are OPTIONAL,
// EDITABLE, and NEVER invented — a missing weight is an empty input, never a zero. No credits field
// (credit-weighting is Alivia's alone). She's a minor: no new AI surface beyond the shared parse.
function SyllabusIntake({ onDone, onCancel }) {
  const [phase, setPhase] = useState("compose"); // "compose" | "preview"
  const [attach, setAttach] = useState(null);    // { name, mediaType, data } — the PDF
  const [guide, setGuide] = useState("");        // optional focus guidance (the reused capture pattern)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // pct/weight are STRINGS (the raw input value): "" means empty, never 0.
  const [course, setCourse] = useState({ name: "", term: "" });
  const [asns, setAsns] = useState([]);       // [{ _k, title, due, weight }]
  const [exams, setExams] = useState([]);     // [{ _k, title, date, type, weight }]
  const [weights, setWeights] = useState([]); // [{ _k, label, pct }]
  const keyRef = useRef(0);
  const nextKey = () => (keyRef.current += 1);

  async function onPickPdf(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ""; // let her re-pick the same file after removing it
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { data } = await readPdf(file); // the SAME capture upload path (jsdom stubs window.__readPdf)
      setAttach({ name: file.name, mediaType: "application/pdf", data });
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally { setBusy(false); }
  }
  function removeAttach() { setAttach(null); setError(""); }

  function loadPreview(p) {
    setCourse({ name: p.course?.name || "", term: p.course?.term || "" });
    setAsns((p.assignments || []).map((a) => ({ _k: nextKey(), title: a.title || "", due: a.dueDate || "", weight: a.weight != null ? String(a.weight) : "" })));
    setExams((p.exams || []).map((x) => ({ _k: nextKey(), title: x.title || "", date: x.date || "", type: x.type || "", weight: x.weight != null ? String(x.weight) : "" })));
    // Grading weights render with their value; a weight the parse did NOT provide is never zeroed.
    // If it found none at all, seed ONE empty row — an honest invitation to fill in later.
    const gw = (p.gradingWeights || []).map((w) => ({ _k: nextKey(), label: w.label || "", pct: w.pct != null ? String(w.pct) : "" }));
    setWeights(gw.length ? gw : [{ _k: nextKey(), label: "", pct: "" }]);
  }

  async function readSyllabus() {
    if (!attach || busy) return;
    setBusy(true); setError("");
    try {
      const r = await api.syllabus.parsePdf({ mediaType: attach.mediaType, data: attach.data }, guide.trim() || undefined);
      const code = r.data?.code;
      if (r.ok && r.data?.deferred) {
        // the known Fable failure mode (refusal / truncation / transient): honest + retryable. Stay
        // on compose and KEEP everything typed (the file + the guidance) so a retry costs nothing.
        setError("Couldn't read this one — try again.");
      } else if (r.ok && r.data?.preview) {
        loadPreview(r.data.preview);
        setPhase("preview");
      } else if (r.status === 415 || code === "unsupported") {
        setError("That file isn't a readable PDF — export or save it as a PDF.");
      } else if (code === "too_big" || r.status === 413) {
        setError("That PDF is too large to upload — try fewer pages or split it.");
      } else if (r.status === 401) {
        setError("Your session expired — sign in again.");
      } else {
        setError("Something went wrong reading that syllabus. Give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  const editAsn = (k, f, v) => setAsns((p) => p.map((a) => (a._k === k ? { ...a, [f]: v } : a)));
  const addAsn = () => setAsns((p) => [...p, { _k: nextKey(), title: "", due: "", weight: "" }]);
  const dropAsn = (k) => setAsns((p) => p.filter((a) => a._k !== k));
  const editExam = (k, f, v) => setExams((p) => p.map((x) => (x._k === k ? { ...x, [f]: v } : x)));
  const addExam = () => setExams((p) => [...p, { _k: nextKey(), title: "", date: "", type: "", weight: "" }]);
  const dropExam = (k) => setExams((p) => p.filter((x) => x._k !== k));
  const editWeight = (k, f, v) => setWeights((p) => p.map((w) => (w._k === k ? { ...w, [f]: v } : w)));
  const addWeight = () => setWeights((p) => [...p, { _k: nextKey(), label: "", pct: "" }]);
  const dropWeight = (k) => setWeights((p) => p.filter((w) => w._k !== k));

  const datelessCount = asns.filter((a) => a.title.trim() && !a.due.trim()).length
    + exams.filter((x) => x.title.trim() && !x.date.trim()).length;
  const weightTotal = weights.reduce((s, w) => s + (w.pct.trim() && Number.isFinite(Number(w.pct)) ? Number(w.pct) : 0), 0);
  const canSave = !busy && Boolean(course.name.trim()) && Boolean(course.term.trim());

  async function save() {
    if (!canSave) return;
    setBusy(true); setError("");
    const payload = {
      course: { name: course.name.trim(), term: course.term.trim() },
      assignments: asns.filter((a) => a.title.trim()).map((a) => ({
        title: a.title.trim(),
        ...(a.due.trim() ? { dueDate: a.due.trim() } : {}),
        ...(a.weight.trim() ? { weight: Number(a.weight) } : {}), // empty -> omitted, never 0
      })),
      exams: exams.filter((x) => x.title.trim()).map((x) => ({
        title: x.title.trim(),
        ...(x.date.trim() ? { date: x.date.trim() } : {}),
        ...(x.type.trim() ? { type: x.type.trim() } : {}),
        ...(x.weight.trim() ? { weight: Number(x.weight) } : {}),
      })),
      // a weight row needs BOTH a label and a non-empty pct — a blank row is dropped, never sent as 0
      gradingWeights: weights.filter((w) => w.label.trim() && w.pct.trim()).map((w) => ({ label: w.label.trim(), pct: Number(w.pct) })),
    };
    try {
      const r = await api.syllabus.save(payload);
      if (r.ok) { onDone(); return; }
      if (r.status === 409) {
        setError("You already have a term by that name — rename the course or term, or delete the old one first.");
      } else if (r.status === 400) {
        setError("Add a course name and term before saving.");
      } else if (r.status === 401) {
        setError("Your session expired — sign in again.");
      } else {
        setError("Couldn't save this term — give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  if (phase === "preview") {
    return (
      <div className="study syllabus-intake">
        <header className="study-top">
          <button className="link" onClick={() => { setPhase("compose"); setError(""); }}>← Back</button>
        </header>
        <h1 className="study-head">Check &amp; save</h1>
        {error && <p className="error" role="alert">{error}</p>}

        <section className="syl-sec">
          <h2>Course</h2>
          <div className="syl-row">
            <input value={course.name} placeholder="Course name" aria-label="Course name"
              onChange={(e) => setCourse({ ...course, name: e.target.value })} />
            <input value={course.term} placeholder="Term" aria-label="Term"
              onChange={(e) => setCourse({ ...course, term: e.target.value })} />
          </div>
        </section>

        <section className="syl-sec">
          <h2>Assignments</h2>
          {asns.map((a) => (
            <div key={a._k} className="syl-row">
              <input value={a.title} placeholder="Title" aria-label="Assignment title" onChange={(e) => editAsn(a._k, "title", e.target.value)} />
              <input type="date" value={a.due} aria-label="Due date" onChange={(e) => editAsn(a._k, "due", e.target.value)} />
              <input type="number" className="syl-weight" value={a.weight} placeholder="wt %" aria-label="Assignment weight percent" onChange={(e) => editAsn(a._k, "weight", e.target.value)} />
              <button className="link" onClick={() => dropAsn(a._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addAsn}>+ Add assignment</button>
        </section>

        <section className="syl-sec">
          <h2>Exams</h2>
          {exams.map((x) => (
            <div key={x._k} className="syl-row">
              <input value={x.title} placeholder="Title" aria-label="Exam title" onChange={(e) => editExam(x._k, "title", e.target.value)} />
              <input type="date" value={x.date} aria-label="Exam date" onChange={(e) => editExam(x._k, "date", e.target.value)} />
              <input value={x.type} placeholder="type" className="syl-type" aria-label="Exam type" onChange={(e) => editExam(x._k, "type", e.target.value)} />
              <input type="number" className="syl-weight" value={x.weight} placeholder="wt %" aria-label="Exam weight percent" onChange={(e) => editExam(x._k, "weight", e.target.value)} />
              <button className="link" onClick={() => dropExam(x._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addExam}>+ Add exam</button>
        </section>

        <section className="syl-sec">
          <h2>Grading weights</h2>
          {weights.map((w) => (
            <div key={w._k} className="syl-row">
              <input value={w.label} placeholder="Category (e.g. Exams)" aria-label="Weight label" onChange={(e) => editWeight(w._k, "label", e.target.value)} />
              <input type="number" className="syl-weight" value={w.pct} placeholder="%" aria-label="Weight percent" onChange={(e) => editWeight(w._k, "pct", e.target.value)} />
              <button className="link" onClick={() => dropWeight(w._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addWeight}>+ Add weight</button>
          <p className="muted syl-total">Total {weightTotal}%{weightTotal !== 100 ? " — partial is fine" : ""}</p>
        </section>

        {datelessCount > 0 && (
          <p className="muted syl-dateless" role="note">
            {datelessCount} item{datelessCount === 1 ? "" : "s"} {datelessCount === 1 ? "has" : "have"} no date yet — add {datelessCount === 1 ? "one" : "dates"} to include {datelessCount === 1 ? "it" : "them"}, or {datelessCount === 1 ? "it" : "they"} won't be saved.
          </p>
        )}

        <div className="cardform-actions">
          <button className="btn" onClick={save} disabled={!canSave}>{busy ? "Saving…" : "Save term"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="study syllabus-intake">
      <header className="study-top">
        <button className="link" onClick={onCancel}>← Courses</button>
      </header>
      <h1 className="study-head">Import a syllabus</h1>
      <div className="cardform">
        <p className="muted">Drop your syllabus PDF — I'll pull out the assignments, exam dates, and grading weights for you to check before anything is saved.</p>
        <div className="gen-source">
          {attach
            ? <span className="gen-chip">📄 {attach.name} · {fmtBytes(b64Bytes(attach.data))}
                <button className="link" onClick={removeAttach}>Remove</button></span>
            : <label className="gen-photo-add"><span className="link">Choose a PDF</span>
                <input type="file" accept="application/pdf" onChange={onPickPdf} style={{ display: "none" }} /></label>}
        </div>
        <label className="field"><span>Focus (optional)</span>
          <textarea rows={2} value={guide} placeholder="Optional — tell me what to focus on (e.g. “just the exam schedule”)"
            onChange={(e) => setGuide(e.target.value)} /></label>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="cardform-actions">
          <button className="btn" onClick={readSyllabus} disabled={!attach || busy}>{busy ? "Reading…" : "Read syllabus"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// SCH·4 — the weekly class-schedule grid (Mon–Fri), ported from Alivia's SCH·3. Pure display.
const GRID_DAYS = [["MO", "Mon"], ["TU", "Tue"], ["WE", "Wed"], ["TH", "Thu"], ["FR", "Fri"]];
const DAY_LABEL = { MO: "Mon", TU: "Tue", WE: "Wed", TH: "Thu", FR: "Fri", SA: "Sat", SU: "Sun" };
function fmt12(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  if (!m) return hhmm || "";
  let h = Number(m[1]); const ap = h < 12 ? "am" : "pm";
  h = h % 12 || 12;
  return m[2] === "00" ? `${h}${ap}` : `${h}:${m[2]}${ap}`;
}
function WeeklyGrid({ meetings }) {
  const byDay = (d) => meetings.filter((m) => m.day === d).sort((a, b) => a.start.localeCompare(b.start));
  const weekend = meetings.filter((m) => m.day === "SA" || m.day === "SU");
  return (
    <div className="week-grid" role="table" aria-label="Weekly class schedule">
      {GRID_DAYS.map(([code, label]) => (
        <div key={code} className="week-col" role="column">
          <div className="week-day">{label}</div>
          {byDay(code).length === 0
            ? <p className="muted week-empty">—</p>
            : byDay(code).map((m) => (
                <div key={m.id} className={`week-slot week-${m.kind}`}>
                  <div className="week-course">{m.course || "Class"}</div>
                  <div className="week-time">{fmt12(m.start)}–{fmt12(m.end)}</div>
                  {m.room && <div className="week-room">{m.room}</div>}
                </div>
              ))}
        </div>
      ))}
      {weekend.length > 0 && (
        <p className="muted week-weekend">Also: {weekend.map((m) => `${DAY_LABEL[m.day]} ${fmt12(m.start)} ${m.course || "Class"}`).join(" · ")}</p>
      )}
    </div>
  );
}

// SCH·4 — the class-schedule intake (paste accessible text → free/deterministic, or drop a PDF →
// the model reads the grid) → an editable meetings preview → Save writes recurring `meetings`.
const MEETING_KINDS = ["lecture", "lab", "office-hours", "other"];
function ScheduleIntake({ onDone, onCancel }) {
  const [phase, setPhase] = useState("compose");
  const [pasted, setPasted] = useState("");
  const [attach, setAttach] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [termStart, setTermStart] = useState("");
  const [termEnd, setTermEnd] = useState("");
  const keyRef = useRef(0);
  const nextKey = () => (keyRef.current += 1);

  async function onPickPdf(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { data } = await readPdf(file);
      setAttach({ name: file.name, mediaType: "application/pdf", data });
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally { setBusy(false); }
  }
  function removeAttach() { setAttach(null); setError(""); }

  function loadPreview(p) {
    setMeetings((p.meetings || []).map((m) => ({
      _k: nextKey(), day: m.day || "MO", start: m.start || "", end: m.end || "",
      course: m.course || "", room: m.room || "", instructor: m.instructor || "", kind: m.kind || "lecture",
    })));
    if (p.termStart) setTermStart(p.termStart);
    if (p.termEnd) setTermEnd(p.termEnd);
  }

  async function readSchedule() {
    if (busy) return;
    const text = pasted.trim();
    if (!text && !attach) return;
    setBusy(true); setError("");
    try {
      const r = text ? await api.schedule.parseText(text)
                     : await api.schedule.parsePdf({ mediaType: attach.mediaType, data: attach.data });
      const code = r.data?.code;
      if (r.ok && r.data?.deferred) {
        setError("Couldn't read this one — try again.");
      } else if (r.ok && r.data?.preview && r.data.preview.meetings?.length) {
        loadPreview(r.data.preview);
        setPhase("preview");
      } else if (r.ok && r.data?.preview) {
        setError("I couldn't find any class times in that — check the text or try the PDF.");
      } else if (r.status === 415 || code === "unsupported") {
        setError("That file isn't a readable PDF — export or save it as a PDF.");
      } else if (code === "too_big" || r.status === 413) {
        setError("That PDF is too large to upload — try fewer pages or split it.");
      } else if (r.status === 401) {
        setError("Your session expired — sign in again.");
      } else {
        setError("Something went wrong reading that schedule. Give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  const editMtg = (k, f, v) => setMeetings((p) => p.map((m) => (m._k === k ? { ...m, [f]: v } : m)));
  const addMtg = () => setMeetings((p) => [...p, { _k: nextKey(), day: "MO", start: "", end: "", course: "", room: "", instructor: "", kind: "lecture" }]);
  const dropMtg = (k) => setMeetings((p) => p.filter((m) => m._k !== k));

  const isHHMM = (t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
  const validRows = meetings.filter((m) => m.day && isHHMM(m.start) && isHHMM(m.end));
  const gridMeetings = validRows.map((m) => ({ id: `k${m._k}`, day: m.day, start: m.start, end: m.end, kind: m.kind, course: m.course, room: m.room }));
  const canSave = !busy && validRows.length > 0 && Boolean(termStart) && Boolean(termEnd) && termEnd >= termStart;

  async function save() {
    if (!canSave) return;
    setBusy(true); setError("");
    const payload = {
      meetings: validRows.map((m) => ({
        day: m.day, start: m.start, end: m.end, kind: m.kind,
        ...(m.course.trim() ? { course: m.course.trim() } : {}),
        ...(m.room.trim() ? { room: m.room.trim() } : {}),
        ...(m.instructor.trim() ? { instructor: m.instructor.trim() } : {}),
      })),
      termStart, termEnd,
    };
    try {
      const r = await api.schedule.save(payload);
      if (r.ok) { onDone(); return; }
      if (r.status === 400) setError("Add term start and end dates, and at least one class time.");
      else if (r.status === 401) setError("Your session expired — sign in again.");
      else setError("Couldn't save this schedule — give it another try.");
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  if (phase === "preview") {
    return (
      <div className="study schedule-intake">
        <header className="study-top">
          <button className="link" onClick={() => { setPhase("compose"); setError(""); }}>← Back</button>
        </header>
        <h1 className="study-head">Check &amp; save your schedule</h1>
        {error && <p className="error" role="alert">{error}</p>}

        <section className="syl-sec">
          <h2>Term dates</h2>
          <div className="syl-row">
            <label className="field"><span>Term starts</span>
              <input type="date" value={termStart} aria-label="Term start" onChange={(e) => setTermStart(e.target.value)} /></label>
            <label className="field"><span>Term ends</span>
              <input type="date" value={termEnd} aria-label="Term end" onChange={(e) => setTermEnd(e.target.value)} /></label>
          </div>
          <p className="muted">Classes repeat weekly between these dates.</p>
        </section>

        <section className="syl-sec">
          <h2>Meetings</h2>
          {meetings.map((m) => (
            <div key={m._k} className="syl-row sched-row">
              <select value={m.day} aria-label="Day" onChange={(e) => editMtg(m._k, "day", e.target.value)}>
                {Object.entries(DAY_LABEL).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <input type="time" value={m.start} aria-label="Start time" onChange={(e) => editMtg(m._k, "start", e.target.value)} />
              <input type="time" value={m.end} aria-label="End time" onChange={(e) => editMtg(m._k, "end", e.target.value)} />
              <input value={m.course} placeholder="Class" aria-label="Course" onChange={(e) => editMtg(m._k, "course", e.target.value)} />
              <input value={m.room} placeholder="Room" className="sched-room" aria-label="Room" onChange={(e) => editMtg(m._k, "room", e.target.value)} />
              <select value={m.kind} aria-label="Kind" onChange={(e) => editMtg(m._k, "kind", e.target.value)}>
                {MEETING_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <button className="link" onClick={() => dropMtg(m._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addMtg}>+ Add a class</button>
        </section>

        {gridMeetings.length > 0 && (
          <section className="syl-sec">
            <h2>Your week</h2>
            <WeeklyGrid meetings={gridMeetings} />
          </section>
        )}

        <div className="cardform-actions">
          <button className="btn" onClick={save} disabled={!canSave}>{busy ? "Saving…" : "Save schedule"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="study schedule-intake">
      <header className="study-top">
        <button className="link" onClick={onCancel}>← Courses</button>
      </header>
      <h1 className="study-head">Add your class schedule</h1>
      <div className="cardform">
        <p className="muted">Paste your timetable's text (the "accessible text" version reads best), or drop the PDF — I'll pull out your weekly classes for you to check before anything is saved.</p>
        <label className="field"><span>Paste timetable text</span>
          <textarea rows={5} value={pasted} placeholder={"e.g.\nMonday: 8–9:15 a.m.: AP Biology, Rm 114\nTuesday: 1–2:30 p.m.: Chemistry Lab, Lab 2"}
            onChange={(e) => setPasted(e.target.value)} /></label>
        <div className="gen-source">
          {attach
            ? <span className="gen-chip">📄 {attach.name} · {fmtBytes(b64Bytes(attach.data))}
                <button className="link" onClick={removeAttach}>Remove</button></span>
            : <label className="gen-photo-add"><span className="link">…or choose a PDF</span>
                <input type="file" accept="application/pdf" onChange={onPickPdf} style={{ display: "none" }} /></label>}
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="cardform-actions">
          <button className="btn" onClick={readSchedule} disabled={(!pasted.trim() && !attach) || busy}>{busy ? "Reading…" : "Read schedule"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// I·3 — the calendar subscription feed: a secret link a phone's calendar app polls. The server
// shows the full link ONCE (at mint); Regenerate revokes the old link (audited server-side).
function CalendarFeedRow() {
  const [feed, setFeed] = useState(null);     // { configured } from the server (never the token)
  const [minted, setMinted] = useState(null); // the one-time full URL after a regenerate
  const [busy, setBusy] = useState(false);
  useEffect(() => { req("GET", "/api/calendar/feed").then((r) => setFeed(r.ok ? r.data : { configured: false })); }, []);
  async function regen() {
    setBusy(true);
    try {
      const r = await req("POST", "/api/calendar/feed/regenerate");
      if (r.ok) { setMinted(window.location.origin + r.data.url); setFeed({ configured: true }); }
    } finally { setBusy(false); }
  }
  return (
    <div className="calendar-feed">
      {minted
        ? <p className="feed-url">Subscribe link — copy it now, it's shown once:<br /><code>{minted}</code></p>
        : <p className="muted">
            {feed?.configured
              ? "Calendar feed is active. Regenerating revokes the current link."
              : "Create a private link your calendar app can subscribe to."}
          </p>}
      <button className="link" onClick={regen} disabled={busy}>
        {feed?.configured ? "Regenerate feed link" : "Create feed link"}
      </button>
    </div>
  );
}

// ── CAL·6a — the on-screen month calendar (ported from Alivia's CAL·3/4; Estelle's tokens) ─────────
// Reads already-synced data and flattens it through core's expandOccurrences. Grid date math is UTC +
// tz-free; "today" is the ONLY thing that reads local (localDateStr) — the view's job, not the engine's.
// Distinct from the "Calendar export (.ics)" row under Courses (that's the download/subscribe feed).
const CAL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CAL_DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAL_CHIP_CAP = 3; // chips shown per cell before the "+N more" indicator
const CAL_WEEKDAYS = [["MO", "Monday"], ["TU", "Tuesday"], ["WE", "Wednesday"], ["TH", "Thursday"], ["FR", "Friday"], ["SA", "Saturday"], ["SU", "Sunday"]];

const calYmd = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;

// A 6-week (42-cell), Sunday-first grid for {year, month(0-11)}. Leading/trailing days belong to the
// adjacent months (inMonth:false → styled faded). Built off UTC so the run machine's tz never shifts a cell.
function calMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const gridStart = new Date(Date.UTC(year, month, 1 - first.getUTCDay())); // back up to the Sunday on/before the 1st
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getTime() + i * 86_400_000);
    cells.push({ date: calYmd(d), inMonth: d.getUTCMonth() === month });
  }
  return cells;
}
function calShiftMonth({ year, month }, delta) {
  const m = month + delta;
  return { year: year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
}
function calFmt12(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}
function calOccTime(o) {
  if (o.allDay || !o.start) return "All day";
  return o.end ? `${calFmt12(o.start)} – ${calFmt12(o.end)}` : calFmt12(o.start);
}
function calLongDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${CAL_MONTHS[m - 1]} ${d}, ${y}`;
}

// The inline Add/Edit form for a personal event, revealed inside the day-detail panel (mirrors the
// Courses `course-form` reveal, not a modal). Save is gated by core's validateEvent — the SAME rules
// the server enforces; the server stays authoritative (a 400 surfaces inline). onSaved() refetches events.
function EventForm({ day, event, latestTermEnd, onSaved, onCancel }) {
  const editing = !!event;
  const [title, setTitle] = useState(event?.title ?? "");
  const [kind, setKind] = useState(event?.kind ?? "activity");
  const [repeats, setRepeats] = useState(event && event.days ? "weekly" : "once");
  const [date, setDate] = useState(event?.date ?? day);
  const [days, setDays] = useState(event?.days ?? []);
  const [from, setFrom] = useState(event?.from ?? day);
  const [to, setTo] = useState(event?.to ?? (editing ? "" : (latestTermEnd ?? "")));
  const [allDay, setAllDay] = useState(editing ? !event.start : true);
  const [start, setStart] = useState(event?.start ?? "");
  const [end, setEnd] = useState(event?.end ?? "");
  const [color, setColor] = useState(event?.color ?? "");
  const [note, setNote] = useState(event?.note ?? "");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // The create-shaped payload — ONLY the fields for the chosen mode. Gates Save (validateEvent) AND, on add, is POSTed.
  function buildFields() {
    const p = { title: title.trim(), kind };
    if (repeats === "once") p.date = date;
    else { p.days = days; p.from = from; p.to = to; }
    if (!allDay) { p.start = start; p.end = end; }
    if (color) p.color = color;
    if (note.trim()) p.note = note.trim();
    return p;
  }
  const valid = !validateEvent(buildFields()).error;

  async function save() {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    const f = buildFields();
    let r;
    if (editing) {
      // null-drop the fields the chosen mode no longer uses (patchRecord semantics) so a once↔weekly
      // or timed↔all-day switch clears the stale keys server-side.
      r = await api.events.update(event.id, {
        title: f.title, kind: f.kind,
        date: f.date ?? null, days: f.days ?? null, from: f.from ?? null, to: f.to ?? null,
        start: f.start ?? null, end: f.end ?? null, color: f.color ?? null, note: f.note ?? null,
      });
    } else {
      r = await api.events.create(f);
    }
    setBusy(false);
    if (r && r.ok) onSaved();
    else setErr((r && r.data && r.data.error) || "Could not save — try again.");
  }

  const toggleDay = (code) => setDays((ds) => (ds.includes(code) ? ds.filter((d) => d !== code) : [...ds, code]));

  return (
    <div className="course-form cal-form">
      <input value={title} placeholder="Event title" autoFocus aria-label="Event title" onChange={(e) => setTitle(e.target.value)} />
      <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
        {EVENT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <select aria-label="Repeats" value={repeats} onChange={(e) => setRepeats(e.target.value)}>
        <option value="once">Just once</option>
        <option value="weekly">Repeats weekly</option>
      </select>
      {repeats === "once"
        ? <input type="date" aria-label="Date" value={date} onChange={(e) => setDate(e.target.value)} />
        : <>
            <span className="cal-days" role="group" aria-label="Days">
              {CAL_WEEKDAYS.map(([code, name]) => (
                <button key={code} type="button" aria-label={name}
                  className={days.includes(code) ? "day-toggle is-on" : "day-toggle"}
                  onClick={() => toggleDay(code)}>{code}</button>
              ))}
            </span>
            <input type="date" aria-label="Repeat from" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input type="date" aria-label="Repeat until" value={to} onChange={(e) => setTo(e.target.value)} />
          </>}
      <label className="cal-allday">
        <input type="checkbox" aria-label="All day" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> All day
      </label>
      {!allDay && (
        <span className="cal-times">
          <input type="time" aria-label="Start time" value={start} onChange={(e) => setStart(e.target.value)} />
          <input type="time" aria-label="End time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </span>
      )}
      <span className="swatches" role="group" aria-label="Color">
        {COURSE_COLORS.map((hex) => (
          <button key={hex} type="button" className={color === hex ? "swatch is-picked" : "swatch"}
            style={{ background: hex }} aria-label={`Color ${hex}`}
            onClick={() => setColor(color === hex ? "" : hex)} />
        ))}
      </span>
      <input value={note} placeholder="Note (optional)" aria-label="Note" onChange={(e) => setNote(e.target.value)} />
      <button className="btn btn-sm" onClick={save} disabled={!valid || busy}>{editing ? "Save" : "Add event"}</button>
      <button className="link" onClick={onCancel}>Cancel</button>
      {err && <p className="cal-form-err del-warn" role="alert">{err}</p>}
    </div>
  );
}

function CalendarView({ onBack }) {
  const [data, setData] = useState(null); // { courses, assignments, exams, meetings, events } — fetched once
  const [selected, setSelected] = useState(null); // the tapped day (YYYY-MM-DD), or null
  const [form, setForm] = useState(null); // null | { event?: rawRecord } — the inline add/edit form
  const [confirmDel, setConfirmDel] = useState(null); // eventId pending delete-confirm
  const today = localDateStr();
  const [ym, setYm] = useState(() => {
    const [y, m] = today.split("-").map(Number);
    return { year: y, month: m - 1 };
  });

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.hub.courses(), api.hub.assignments(), api.exams.list(),
      api.schedule.meetings(), api.events.list(),
    ]).then(([courses, assignments, exams, meetings, events]) => {
      if (alive) setData({ courses, assignments, exams, meetings, events });
    });
    return () => { alive = false; };
  }, []);

  if (!data) {
    return (
      <div className="study cal">
        <header className="study-top"><button className="link" onClick={onBack}>← Dashboard</button></header>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const cells = calMonthGrid(ym.year, ym.month);
  // Occurrences across the WHOLE visible grid span. Courses are NOT passed to expandOccurrences — they
  // feed the color map only (the engine stays color-policy-free).
  const occ = expandOccurrences({
    meetings: data.meetings, events: data.events, assignments: data.assignments, exams: data.exams,
    from: cells[0].date, to: cells[41].date,
  });
  const byDate = new Map();
  for (const o of occ) {
    if (!byDate.has(o.date)) byDate.set(o.date, []);
    byDate.get(o.date).push(o);
  }
  // COLOR RESOLUTION: course-linked items inherit the course color; personal events use the color she
  // picked; anything else gets a neutral class (no inline color, never a guessed default).
  const courseColor = new Map(data.courses.filter((c) => c.color).map((c) => [c.id, c.color]));
  const colorFor = (o) => (o.courseId && courseColor.has(o.courseId)) ? courseColor.get(o.courseId) : (o.color || null);

  const selectedOcc = selected ? (byDate.get(selected) ?? []) : null;
  const goToday = () => { const [y, m] = today.split("-").map(Number); setYm({ year: y, month: m - 1 }); };
  // A write refetches ONLY events; courses/assignments/exams/meetings state is left untouched (the grid recomputes).
  const refreshEvents = () => api.events.list().then((events) => setData((d) => ({ ...d, events })));
  const closeForm = () => { setForm(null); setConfirmDel(null); };
  const pickDay = (date) => { setSelected(date); closeForm(); };
  const latestTermEnd = data.meetings.map((m) => m.termEnd).filter(Boolean).sort().at(-1) ?? "";

  return (
    <div className="study cal">
      <header className="study-top"><button className="link" onClick={onBack}>← Dashboard</button></header>
      <div className="study-head">
        <h1 className="cal-heading">{CAL_MONTHS[ym.month]} {ym.year}</h1>
        <span className="cal-nav">
          <button className="btn btn-sm btn-ghost" onClick={() => setYm(calShiftMonth(ym, -1))} aria-label="Previous month">‹ Prev</button>
          <button className="btn btn-sm btn-ghost" onClick={goToday}>Today</button>
          <button className="btn btn-sm btn-ghost" onClick={() => setYm(calShiftMonth(ym, 1))} aria-label="Next month">Next ›</button>
        </span>
      </div>
      <div className="cal-dow" aria-hidden="true">
        {CAL_DOW.map((d) => <span key={d} className="cal-dow-cell">{d}</span>)}
      </div>
      <div className="cal-grid" role="grid">
        {cells.map((cell) => {
          const items = byDate.get(cell.date) ?? [];
          const shown = items.slice(0, CAL_CHIP_CAP);
          const overflow = items.length - shown.length;
          const cls = ["cal-cell"];
          if (!cell.inMonth) cls.push("is-faded");
          if (cell.date === today) cls.push("is-today");
          if (cell.date === selected) cls.push("is-selected");
          return (
            <button type="button" key={cell.date} className={cls.join(" ")} data-date={cell.date}
              onClick={() => pickDay(cell.date)} aria-label={cell.date}>
              <span className="cal-daynum">{Number(cell.date.slice(8, 10))}</span>
              <span className="cal-chips">
                {shown.map((o, i) => {
                  const color = colorFor(o);
                  return (
                    <span key={o.source + o.id + i} className={color ? "cal-chip" : "cal-chip is-neutral"}
                      style={color ? { borderLeftColor: color } : undefined} data-source={o.source} data-id={o.id}>
                      <span className="cal-chip-dot" style={color ? { background: color } : undefined} aria-hidden="true" />
                      <span className="cal-chip-title">{o.title}</span>
                    </span>
                  );
                })}
                {overflow > 0 && <span className="cal-more">+{overflow} more</span>}
              </span>
            </button>
          );
        })}
      </div>
      {selected && (
        <section className="cal-detail">
          <div className="cal-detail-head">
            <h2>{calLongDate(selected)}</h2>
            {!form && <button className="btn btn-sm" onClick={() => setForm({})}>+ Add</button>}
          </div>
          {form && (
            <EventForm day={selected} event={form.event} latestTermEnd={latestTermEnd}
              onSaved={() => { closeForm(); refreshEvents(); }} onCancel={closeForm} />
          )}
          {selectedOcc.length === 0 && !form
            ? <p className="muted">Nothing scheduled.</p>
            : <ul className="cal-detail-list">
                {selectedOcc.map((o, i) => {
                  const color = colorFor(o);
                  const isEvent = o.source === "event"; // ONLY personal events are editable here — homework/tests/classes are read-only on the calendar
                  return (
                    <li key={o.source + o.id + i} className="cal-detail-row" data-source={o.source} data-id={o.id}>
                      <span className="cal-chip-dot" style={color ? { background: color } : undefined} aria-hidden="true" />
                      <span className="cal-detail-title">{o.title}</span>
                      <span className="cal-detail-time">{calOccTime(o)}</span>
                      {isEvent && confirmDel !== o.id && (
                        <span className="asn-actions">
                          <button className="link" onClick={() => setForm({ event: data.events.find((e) => e.id === o.id) })}>Edit</button>
                          <button className="link" onClick={() => setConfirmDel(o.id)}>Delete</button>
                        </span>
                      )}
                      {isEvent && confirmDel === o.id && (
                        <span className="del-warn cal-del" role="alert">
                          Delete this event?{" "}
                          <button className="link" onClick={async () => { await api.events.remove(o.id); setConfirmDel(null); refreshEvents(); }}>Delete</button>{" "}
                          <button className="link" onClick={() => setConfirmDel(null)}>Keep</button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>}
        </section>
      )}
    </div>
  );
}

// ARC·1 — "Study now" on an ARCHIVED course: a flip-through over the course's cards that WRITES
// NOTHING — no /api/study/review, no quiz-answer, no bookmark. Archiving hides a course from the
// daily surfaces, not from her; this is how she still reaches its cards without a due date, an
// ease, an interval, or a streak ever moving. Reveal, then Next — that is the whole loop.
function PracticeSession({ course, onExit }) {
  const [cards, setCards] = useState(null);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    let alive = true;
    api.study.practice(course.id).then((c) => { if (alive) { setCards(c); setIdx(0); setRevealed(false); } });
    return () => { alive = false; };
  }, [course.id]);
  if (cards === null) return <div className="review"><p className="muted">Loading…</p></div>;
  const done = idx >= cards.length;
  if (cards.length === 0 || done) return (
    <div className="review review-done">
      <div className="review-deck">Study · {course.name}</div>
      <h1>{cards.length === 0 ? "No cards here yet" : "That's the set"}</h1>
      <p className="muted">Practice only — your review schedule is untouched.</p>
      <button className="btn" onClick={onExit}>Back to courses</button>
    </div>
  );
  const card = cards[idx];
  return (
    <div className="review">
      <header className="review-top">
        <button className="link" onClick={onExit}>← Back</button>
        <span className="review-progress">{idx + 1} / {cards.length}</span>
      </header>
      <div className="review-deck">Study · {course.name}</div>
      <p className="muted">Practice only — your review schedule is untouched.</p>
      <section className="flashcard" onClick={() => !revealed && setRevealed(true)}>
        <p className="card-front" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceFront(card, card.front)) }} />
        {revealed && <><hr className="card-rule" /><p className="card-back" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(faceBack(card)) }} /></>}
      </section>
      {!revealed
        ? <button className="btn btn-reveal" onClick={() => setRevealed(true)}>Show</button>
        : <button className="btn" onClick={() => { setRevealed(false); setIdx((i) => i + 1); }}>Next</button>}
    </div>
  );
}

function CoursesView({ onBack }) {
  const [courses, setCourses] = useState(null);
  const [asns, setAsns] = useState(null);
  const [exams, setExams] = useState([]); // E·4 — exams by course
  const [term, setTerm] = useState(null); // null = auto (latest created course's term)
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false); // Y·4: the syllabus intake screen
  const [schedIntake, setSchedIntake] = useState(false); // SCH·4: the class-schedule intake screen
  const [meetings, setMeetings] = useState([]); // SCH·4: saved weekly meetings (for the grid)
  const [cName, setCName] = useState("");
  const [cTerm, setCTerm] = useState("");
  const [cColor, setCColor] = useState("");
  const [practice, setPractice] = useState(null); // ARC·1 — the archived course being studied (zero FSRS)

  function refresh() {
    api.exams.list().then(setExams); // E·4 — exams ride alongside (empty stays empty; no gate on them)
    api.schedule.meetings().then(setMeetings); // SCH·4 — the weekly grid (empty stays empty)
    return Promise.all([api.hub.courses().then(setCourses), api.hub.assignments().then(setAsns)]);
  }
  useEffect(() => { refresh(); }, []);

  // Y·4 — a saved syllabus lands a whole term; on done, refresh so the new course/assignments show.
  if (importing) return <SyllabusIntake onDone={() => { setImporting(false); refresh(); }} onCancel={() => setImporting(false)} />;
  // SCH·4 — a saved schedule lands the week's recurring meetings; on done, refresh so the grid shows.
  if (schedIntake) return <ScheduleIntake onDone={() => { setSchedIntake(false); refresh(); }} onCancel={() => setSchedIntake(false)} />;
  if (practice) return <PracticeSession course={practice} onExit={() => setPractice(null)} />; // ARC·1 — zero FSRS

  if (courses === null || asns === null) {
    return <div className="study"><p className="muted">Loading…</p></div>;
  }

  // ARC·1 — the term flow is ACTIVE courses only; archived ones (any term) sit in their own
  // collapsed section below, and never drive the term switcher or the "latest term" default.
  const active = activeCourses(courses);
  const archived = archivedCourses(courses);
  const terms = [...new Set(active.map((c) => c.term))];
  // BIG·1b: ONE shared total order in core decides this (createdAt, then id — nothing can tie).
  // A same-millisecond import is the normal case for a Canvas sync, and the old inline comparator
  // never returned 0, so its answer moved with the order the courses arrived in.
  const latest = latestTerm(active);
  const activeTerm = term ?? latest;
  const termCourses = active.filter((c) => c.term === activeTerm);
  // X·4: the lookup spans ALL terms, not the term-filtered list — renaming a course's term must
  // not strand its open assignments out of Upcoming/Overdue while the dashboard tile (which counts
  // every term) still counts them. Tile and view agree: open assignments of any existing course.
  const courseById = Object.fromEntries(courses.map((c) => [c.id, c]));
  const today = localDateStr();
  const horizon = addDays(14); // Upcoming lean: a two-week lookahead (a 7-day window hides next week's test)
  const openAsns = forActiveCourses(asns, courses).filter((a) => courseById[a.courseId] && !a.done); // ARC·1 — an archived course's work leaves Upcoming/Overdue
  const overdue = openAsns.filter((a) => a.due < today).sort(byDue);
  const upcoming = openAsns.filter((a) => a.due >= today && a.due <= horizon).sort(byDue);

  async function createCourse() {
    if (!cName.trim() || !cTerm.trim()) return;
    await api.hub.createCourse(cName.trim(), cTerm.trim(), cColor || undefined);
    setCName(""); setCColor(""); setCreating(false); refresh();
  }

  return (
    <div className="study hub">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
        {terms.length > 1 && (
          <select className="term-switch" value={activeTerm} onChange={(e) => setTerm(e.target.value)} aria-label="Term">
            {terms.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </header>
      <div className="study-head">
        <h1>Courses &amp; Assignments</h1>
        {!creating
          ? <span className="hub-actions">
              <button className="btn btn-sm" onClick={() => { setCTerm(activeTerm ?? "Fall 2026"); setCreating(true); }}>+ New course</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setImporting(true)}>Import syllabus</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setSchedIntake(true)}>Class schedule</button>
            </span>
          : <div className="newdeck course-form">
              <input value={cName} placeholder="Course name" autoFocus onChange={(e) => setCName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createCourse(); }} aria-label="Course name" />
              <input value={cTerm} placeholder="Term" onChange={(e) => setCTerm(e.target.value)} aria-label="Term" />
              <span className="swatches" role="group" aria-label="Color">
                {COURSE_COLORS.map((hex) => (
                  <button key={hex} type="button"
                    className={cColor === hex ? "swatch is-picked" : "swatch"}
                    style={{ background: hex }} aria-label={`Color ${hex}`}
                    onClick={() => setCColor(cColor === hex ? "" : hex)} />
                ))}
              </span>
              <button className="btn btn-sm" onClick={createCourse} disabled={!cName.trim() || !cTerm.trim()}>Create</button>
              <button className="link" onClick={() => { setCreating(false); setCName(""); setCColor(""); }}>Cancel</button>
            </div>}
      </div>

      {termCourses.length === 0
        ? <p className="muted empty">No courses yet — add your fall schedule.</p>
        : <>
            <p className="gpa-line">
              Term GPA <b>{fmtGpa(gpaOf(termCourses))}</b> · Cumulative <b>{fmtGpa(gpaOf(courses))}</b>
              {gpaOf(courses) === null && <span className="muted"> — no grades entered yet</span>}
            </p>

            <section className="upcoming">
              <h2>Upcoming · next 14 days</h2>
              {overdue.length === 0 && upcoming.length === 0
                ? <p className="muted">Nothing due in the next 14 days.</p>
                : <ul className="asn-list">
                    {[...overdue, ...upcoming].map((a) => (
                      <AssignmentRow key={a.id} asn={a} course={courseById[a.courseId]} showCourse onRefresh={refresh} />
                    ))}
                  </ul>}
            </section>

            <ul className="course-list">
              {termCourses.map((c) => (
                <CourseCard key={c.id} course={c}
                  assignments={asns.filter((a) => a.courseId === c.id)}
                  exams={exams.filter((x) => x.courseId === c.id)} onRefresh={refresh} />
              ))}
            </ul>
          </>}
      {/* ARC·1 — archived courses: every term, collapsed, gone entirely when there are none. Every
          byte is still here (assignments, exams, decks); the course just left the daily surfaces. */}
      {archived.length > 0 && (
        <details className="archived-courses">
          <summary>Archived ({archived.length})</summary>
          <ul className="course-list">
            {archived.map((c) => (
              <CourseCard key={c.id} course={c} archived
                assignments={asns.filter((a) => a.courseId === c.id)}
                exams={exams.filter((x) => x.courseId === c.id)} onRefresh={refresh} onPractice={() => setPractice(c)} />
            ))}
          </ul>
        </details>
      )}
      {/* SCH·4 — the saved weekly class schedule (recurring meetings; rides the calendar feed) */}
      {meetings.length > 0 && (
        <section className="week-section">
          <div className="week-head"><h2>Weekly schedule</h2>
            <button className="link" onClick={() => setSchedIntake(true)}>Update</button></div>
          <WeeklyGrid meetings={meetings} />
        </section>
      )}
      {/* I·2 — the whole hub as a calendar file (server-emitted .ics; the browser saves it) */}
      <p className="calendar-export"><a href="/api/calendar.ics" download>Calendar export (.ics)</a></p>
      <CalendarFeedRow />
    </div>
  );
}

// W·5 — the AI Tutor surface (behavior parity with Alivia's W·4; her navy/red register + plain,
// age-appropriate copy). Two modes over one deck picker that scopes the server-side grounding:
// Chat (a thread; history held here + re-posted each turn) and Explain it back (write an
// explanation of a deck, get a what-you-got / what-you-missed / muddled verdict). Nothing persists.
const TUTOR_MSG_MAX = 4000; // mirrors core TUTOR_MESSAGE_MAX — the client guards before posting

function TutorView({ onBack }) {
  const [decks, setDecks] = useState(null);
  const [deckId, setDeckId] = useState("");
  const [mode, setMode] = useState("chat");
  useEffect(() => { api.study.overview().then((o) => setDecks(o.decks)); }, []);

  return (
    <div className="study tutor-view">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
        <div className="tutor-modes" role="tablist">
          <button role="tab" aria-selected={mode === "chat"} className={`chip ${mode === "chat" ? "is-on" : ""}`} onClick={() => setMode("chat")}>Chat</button>
          <button role="tab" aria-selected={mode === "explain"} className={`chip ${mode === "explain" ? "is-on" : ""}`} onClick={() => setMode("explain")}>Explain it back</button>
        </div>
      </header>
      <div className="study-head"><h1>AI Tutor</h1></div>

      <label className="tutor-deck">
        <span>Grounded in</span>
        <select value={deckId} onChange={(e) => setDeckId(e.target.value)} aria-label="Deck to ground the tutor in">
          <option value="">All my cards</option>
          {(decks ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </label>
      <p className="muted tutor-hint">Answers come from your own cards — nothing random off the internet.</p>

      {mode === "chat"
        ? <TutorChat deckId={deckId} />
        : <TutorExplain deckId={deckId} decks={decks ?? []} />}
    </div>
  );
}

function TutorChat({ deckId }) {
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tooLong = draft.length > TUTOR_MSG_MAX;

  async function send() {
    const message = draft.trim();
    if (!message || busy || tooLong) return;
    setError("");
    // W·6: post only the recent tail — mirrors the route ceiling (TUTOR_HISTORY_CAP * 2 = 40); the
    // engine keeps the most recent 20 turns, so the tail is lossless and a long sitting never wedges.
    const history = thread.slice(-40).map((t) => ({ role: t.role, content: t.content }));
    setThread((t) => [...t, { role: "user", content: message }]);
    setDraft("");
    setBusy(true);
    try {
      const r = await api.tutor.chat(message, history, deckId || undefined);
      if (r.ok && r.data?.reply) {
        setThread((t) => [...t, { role: "assistant", content: r.data.reply, truncated: r.data.truncated }]);
      } else {
        setThread((t) => t.slice(0, -1)); // T2·1: her turn never landed — take the stranded bubble back out
        setDraft(message); // …and the typed text survives the failure (no retyping to retry)
        setError(r.status === 503 ? "The tutor couldn't answer just now — try again." : (r.data?.error || "Something went wrong — try again."));
      }
    } catch {
      setThread((t) => t.slice(0, -1)); // T2·1: same rescue on a network failure
      setDraft(message);
      setError("Couldn't reach the tutor — check your connection and try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="tutor-chat">
      {thread.length > 0 && (
        <button type="button" className="link tutor-new" onClick={() => { setThread([]); setError(""); setDraft(""); }}>New chat</button>
      )}
      <div className="tutor-thread">
        {thread.length === 0 && <p className="muted">Ask a question about your cards — what something means, why it works, or how two things are different.</p>}
        {thread.map((t, i) => (
          <div key={i} className={`bubble bubble-${t.role}`}>
            <div dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(t.content) }} />
            {t.truncated && <p className="bubble-note">…(cut off — ask me to keep going)</p>}
          </div>
        ))}
        {busy && <div className="bubble bubble-assistant is-thinking">…</div>}
      </div>
      {error && <p className="error">{error}</p>}
      <div className="tutor-compose">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          placeholder="Ask the tutor…" rows={2} aria-label="Message the tutor" />
        <button className="btn" onClick={send} disabled={busy || !draft.trim() || tooLong}>Send</button>
      </div>
      {tooLong && <p className="error">That's a bit long — keep it under {TUTOR_MSG_MAX} characters.</p>}
    </div>
  );
}

function TutorExplain({ deckId, decks }) {
  const [text, setText] = useState("");
  const [verdict, setVerdict] = useState(null);
  const [promptBack, setPromptBack] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tooLong = text.length > TUTOR_MSG_MAX;
  const deckName = deckId ? (decks.find((d) => d.id === deckId)?.name ?? "this deck") : null;

  async function check() {
    if (busy || tooLong) return;
    setError(""); setVerdict(null); setPromptBack("");
    if (!deckId) { setError("Pick a specific deck to check your explanation against."); return; }
    setBusy(true);
    try {
      const r = await api.tutor.explain(deckId, text);
      if (r.ok && r.data?.verdict) setVerdict(r.data.verdict);
      else if (r.ok && r.data?.promptBack) setPromptBack(r.data.promptBack);
      else setError(r.status === 503 ? "Couldn't check that just now — try again." : (r.data?.error || "Something went wrong — try again."));
    } catch {
      setError("Couldn't reach the tutor — check your connection and try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="tutor-explain">
      <p className="muted">Explain {deckName ? <b>{deckName}</b> : "a deck"} in your own words — I'll check it against your cards and show what you got, what you missed, and anything mixed up.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Explain it in your own words…" rows={6} aria-label="Your explanation" />
      {tooLong && <p className="error">That's a bit long — keep it under {TUTOR_MSG_MAX} characters.</p>}
      <button className="btn" onClick={check} disabled={busy || tooLong}>{busy ? "Checking…" : "Check my explanation"}</button>
      {error && <p className="error">{error}</p>}
      {promptBack && <p className="muted">{promptBack}</p>}
      {verdict && (
        <div className="verdict">
          {verdict.summary && <p className="verdict-summary">{verdict.summary}</p>}
          <VerdictBucket className="v-right" title="You got" items={verdict.right} empty="—" />
          <VerdictBucket className="v-missed" title="You missed" items={verdict.missed} empty="Nothing — you covered it all." />
          <VerdictBucket className="v-fuzzed" title="Mixed up" items={verdict.fuzzed} empty="Nothing mixed up." />
        </div>
      )}
    </div>
  );
}

function VerdictBucket({ className, title, items, empty }) {
  return (
    <div className={`verdict-bucket ${className}`}>
      <h3>{title}</h3>
      {items.length === 0
        ? <p className="muted">{empty}</p>
        : <ul>{items.map((it, i) => <li key={i}>{it}</li>)}</ul>}
    </div>
  );
}

// G·2 — the semester-zero guide (Estelle's register: plain, encouraging, one idea at a time; the
// vet-programs framing on hours; the honest note that Review wakes up once her decks have cards).
// Static + read-only — no data, no endpoints, no model calls; ships inside the bundle, works offline.
function GuideView({ onBack }) {
  return (
    <div className="study guide">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <div className="study-head"><h1>Guide</h1></div>
      <p className="muted">How each part works. Skim it now, come back whenever something surprises you.</p>

      <section className="guide-section" id="g-learn">
        <h2>Study &amp; Review — learn first, recall second</h2>
        <p>A brand-new card doesn't start as a flashcard — it starts as a <b>multiple-choice
        question</b>, because recognizing the right answer is how you learn it in the first place.
        Answer its question right in <b>two separate quizzes</b> and the card <b>graduates</b>: at the
        end of that quiz you'll answer it cold, as a flashcard, for the first time.</p>
        <p>A quiz is one sitting — a right answer never brings the same card back around; it shows up in
        your <i>next</i> quiz instead. If a card's first flashcard try comes up empty, no big deal: the
        button says <b>Blank</b>, not Again, and the card just goes back into the quiz pool to get
        locked in first. It's a redo, never a strike.</p>
        <p className="guide-note">Review wakes up once your decks have cards — capture some notes and
        it all starts working.</p>
      </section>

      <section className="guide-section" id="g-fsrs">
        <h2>The four grade buttons</h2>
        <p>Once a card is on your review schedule, each grade tells the scheduler (FSRS — a
        spaced-repetition algorithm) how well you knew it: <b>Again</b> means show it soon; <b>Hard</b>,
        <b> Good</b>, and <b>Easy</b> push it further out. The little label under each button is the real
        predicted gap. Grade honestly — there's no penalty for Again; it's information, not a score.</p>
      </section>

      <section className="guide-section" id="g-cloze">
        <h2>Fill-in-the-blank cards</h2>
        <p>Some cards are <b>cloze</b> — a sentence with a word hidden, shown as <code>[…]</code>. They
        work well for lists and dense facts. One sentence with two hidden words becomes two cards, each
        hiding its own word; reveal fills the sentence back in. Turn them on with <b>Cloze mode</b> when
        you make cards.</p>
      </section>

      <section className="guide-section" id="g-type">
        <h2>Type the answer</h2>
        <p>Turn on <b>Type answers</b> in a review and, for short answers, you'll type it before you flip.
        It checks your typing and <i>suggests</i> a grade, but the grade is always yours to change. It's
        off by default and skips long or fill-in-the-blank cards — use it when you want recall to be a
        little harder.</p>
      </section>

      <section className="guide-section" id="g-ramp">
        <h2>Before a test</h2>
        <p>When a test you've entered is close, your review session quietly <b>re-orders</b> so the
        weakest cards for that class lead — same session, same size, just aimed at what's coming. The
        banner tells you when that's happening, and the dashboard counts down to each test.</p>
      </section>

      <section className="guide-section" id="g-examsim">
        <h2>Simulate the exam</h2>
        <p><b>Simulate exam</b> deals a practice paper from your question bank under test conditions — one
        question at a time, <b>no feedback until the end</b>, and a clock. The end report scores you and
        bookmarks every miss so you can drill it after.</p>
      </section>

      <section className="guide-section" id="g-forecast">
        <h2>Exam-day forecast</h2>
        <p>When a test is close, Study &amp; Review shows a small table projecting how much you'll likely
        still remember <i>on test day</i>, per topic. It's a heads-up on where to push — not a grade.</p>
      </section>

      <section className="guide-section" id="g-today">
        <h2>The Today panel</h2>
        <p>The dashboard's Today panel keeps you out of all-nighters: what's overdue or due today, plus
        the one nearest thing worth starting early. The Study tile shows a <b>finishable</b> number for
        today — never the whole mountain.</p>
      </section>

      <section className="guide-section" id="g-notes">
        <h2>Study Notes</h2>
        <p>When you capture — paste, photo, or PDF — you choose what to make: <b>Cards</b>, <b>Study
        notes</b>, or <b>Both</b>. Study notes take your class material and lay it out in sections you
        can skim before a test: headings, short bullets, and the key terms that section defines. They
        only contain <b>what was in your source</b> — nothing gets added, so if your teacher
        didn&apos;t cover it, it isn&apos;t in there.</p>
        <p><i>Notes orient you; cards make it stick.</i> The notes get you oriented; the cards are
        still where the learning happens. Reading notes doesn&apos;t schedule anything or count as
        review.</p>
        <p>Choose <b>Both</b> and each card is tagged with the note section it came from, so you can
        trace any card back to where it started. Everything is editable before you save, and the
        <b> Study Notes</b> tile keeps them, newest first, until you delete one.</p>
      </section>

      <section className="guide-section" id="g-hours">
        <h2>Hours Log</h2>
        <p>Animal, clinical, volunteer — <b>vet programs ask for documented, dated hours</b>, and hours
        written down as they happen count for more than ones pieced together later. Start the timer when
        you begin (it keeps running even if you close the app, and can be stopped from another device),
        or add an entry by hand. <b>Export CSV</b> hands you the whole log when an application wants it.</p>
      </section>

      <section className="guide-section" id="g-tutor">
        <h2>AI Tutor</h2>
        <p>Ask questions about your own cards — the tutor reads your deck, so its answers use the same
        wording your tests will. It sticks to schoolwork and won't do assignments for you.
        <b> Explain it back</b> is the second mode: pick a deck, explain it in your own words, and see
        what you got, what you missed, and anything mixed up. Conversations aren't saved anywhere, and
        <b> New chat</b> clears the thread whenever you want a fresh start.</p>
      </section>

      <section className="guide-section" id="g-calendar">
        <h2>Calendar</h2>
        <p>Open <b>Calendar</b> from the dashboard for a month grid. Your homework, tests, and classes
        show up automatically, each in its <b>class color</b>. Tap any day to see everything on it, and
        tap <b>+ Add</b> to add your own after-school plans — practice, work, an appointment. Choose
        <b>Just once</b> or <b>Repeats weekly</b> (pick the weekdays and a date range), pick a color,
        and it lands on the grid. Only your own events can be edited or deleted here; homework and tests
        are managed under Courses.</p>
        <p>Under Courses, <b>Calendar export</b> downloads your whole schedule — homework, tests, classes,
        and your own events — as a file any calendar app can open. The <b>feed link</b> is the live
        version — your phone's calendar subscribes and stays current. The link shows once when you create
        it; <b>regenerating makes a new link and the old one stops working</b>, so re-subscribe after.</p>
      </section>

      <section className="guide-section" id="g-schedule">
        <h2>Class schedule</h2>
        <p>Under Courses, <b>Class schedule</b> takes your term timetable — paste it or drop the PDF —
        and lays out your classes as a <b>weekly grid</b>. Those class times also ride your calendar feed,
        so your phone shows them next to due dates. Saving a schedule replaces the one for that term.</p>
      </section>

      <section className="guide-section" id="g-report">
        <h2>If a card looks wrong</h2>
        <p>Tap <i>"Something wrong with this card?"</i> during any session, say what's off (or don't),
        and send — Dad sees it and fixes the card. You know your classes best.</p>
      </section>
    </div>
  );
}



// S2·2 — Settings: change password (requires the current one; the server enforces bounds + audits).
// NOTE: existing signed-in devices stay signed in after a change (stateless sessions) — the
// invalidate-on-change follow-up is flagged in PHASE-5, deliberately not built yet.
function SettingsView({ onBack }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState(null); // { kind: "ok" | "err", text }
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!current || !next) { setMsg({ kind: "err", text: "Fill in both fields." }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api.changePassword(current, next);
      if (r.ok) { setMsg({ kind: "ok", text: "Password changed. Use the new one next time you sign in." }); setCurrent(""); setNext(""); }
      else setMsg({ kind: "err", text: r.data?.error ?? "That didn't work — check your details and try again." });
    } catch {
      setMsg({ kind: "err", text: "Couldn't reach the server — try again." });
    } finally { setBusy(false); }
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <div className="study-head"><h1>Settings</h1></div>
      <form className="card settings-card" onSubmit={submit}>
        <h2 className="settings-h">Change password</h2>
        <label className="field"><span>Current password</span>
          <input type="password" value={current} autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} /></label>
        <label className="field"><span>New password (at least 8 characters)</span>
          <input type="password" value={next} autoComplete="new-password" onChange={(e) => setNext(e.target.value)} /></label>
        <button className="btn" type="submit" disabled={busy}>{busy ? "Changing…" : "Change password"}</button>
        {msg && <p className={msg.kind === "ok" ? "settings-ok" : "error"} role={msg.kind === "ok" ? "status" : "alert"}>{msg.text}</p>}
      </form>
      <DownloadMyData />
    </div>
  );
}

// DX·1 — "Download my data": her whole change-log + review history as one JSON file, plus a
// human-readable card list as markdown. Her data, her copy, one tap.
function DownloadMyData() {
  return (
    <div className="card settings-card">
      <h2 className="settings-h">Download my data</h2>
      <p className="muted">Your cards, decks, review history, hours, and courses — your copy to keep.</p>
      <div className="settings-downloads">
        <a className="btn" href="/api/export.json">Download everything (JSON)</a>
        <a className="link" href="/api/export-cards.md">Just my cards (Markdown)</a>
      </div>
    </div>
  );
}

function Shell({ user, onSignOut }) {
  const [view, setView] = useState("dashboard"); // "dashboard" | "study" | "quiz" | "courses"
  return (
    <div className="app">
      <header className="topbar">
        <button className="wordmark-btn" onClick={() => setView("dashboard")} aria-label="Home"><Wordmark /></button>
        <div className="topbar-right">
          <span className="who">{user.email}</span>
          <button className="link" onClick={() => setView("settings")}>Settings</button>
          <button className="link" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <main className="content">
        {view === "study"
          ? <StudyView onBack={() => setView("dashboard")} />
          : view === "quiz"
            ? <QuizView onBack={() => setView("dashboard")} />
            : view === "courses"
              ? <CoursesView onBack={() => setView("dashboard")} />
              : view === "calendar"
              ? <CalendarView onBack={() => setView("dashboard")} />
              : view === "hours"
                ? <HoursView onBack={() => setView("dashboard")} />
                : view === "tutor"
                  ? <TutorView onBack={() => setView("dashboard")} />
                  : view === "settings"
                    ? <SettingsView onBack={() => setView("dashboard")} />
                  : view === "guide"
                    ? <GuideView onBack={() => setView("dashboard")} />
                  : view === "notes"
                    ? <NotesView onBack={() => setView("dashboard")} />
                  : view === "library"
                    ? <LibraryView onBack={() => setView("dashboard")} />
                    : <Dashboard onOpenStudy={() => setView("study")} onOpenQuiz={() => setView("quiz")}
                        onOpenCourses={() => setView("courses")} onOpenCalendar={() => setView("calendar")}
                        onOpenHours={() => setView("hours")}
                        onOpenTutor={() => setView("tutor")} onOpenGuide={() => setView("guide")}
                        onOpenNotes={() => setView("notes")} onOpenLibrary={() => setView("library")} />}
      </main>
      {/* V·1: the build baked into THIS bundle (esbuild define from sw.js VERSION) — a stale install shows the old string. */}
      <footer className="app-footer">{__APP_VERSION__}</footer>
    </div>
  );
}

function App() {
  const [status, setStatus] = useState("checking"); // "checking" | "out" | "in"
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;
    api.me().then((u) => {
      if (!alive) return;
      if (u) { setUser(u); setStatus("in"); loadFsrsWeights(); } else setStatus("out");
    });
    return () => { alive = false; };
  }, []);

  async function signOut() {
    await api.signOut();
    setUser(null);
    setStatus("out");
  }

  if (status === "checking") return <main className="splash"><Wordmark /></main>;
  if (status === "in") return <Shell user={user} onSignOut={signOut} />;
  return <AuthScreen onSignedIn={(u) => { setUser(u); setStatus("in"); loadFsrsWeights(); }} />;
}

const root = document.getElementById("root");
if (root) createRoot(root).render(<App />);

// Register the service worker (offline shell + installability). Guarded so it no-ops in
// environments without it (e.g. the jsdom smoke test).
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
